import { useEffect, useMemo, useRef, useState } from "react";
import { AlarmClock, Archive, ArchiveRestore, Bell, Check, Copy, CreditCard, Eye, EyeOff, FileDown, FlaskConical, Hand, HandCoins, HelpCircle, ImagePlus, Link2, ListChecks, Loader2, Mail, MapPin, Package, Paperclip, Percent, Plus, Printer, QrCode, RefreshCw, RotateCcw, Save, ScrollText, Search, StickyNote, Trash2, TrendingDown, Truck, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { fmtDateTimeMtl } from "../../lib/dateMtl";
import { StallionLabel } from "./StallionLabel";
import SignatureLivraison from "./SignatureLivraison";
import GravureLignes, { extraitTexteGravure } from "../GravureLignes";
import { useLang } from "../../i18n/LanguageContext";
import { formatPhone } from "../../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)

// Onglet « Soumissions » de l'admin : TOUTES les demandes « Votre soumission en
// 30 secondes » avec suivi par étapes à cocher (Répondu → Archivé), expédition avec
// courriel au client (transporteur + nº de suivi, ou livraison en main propre),
// impression (logo imprimante) et vue détaillée (œil).
const STEPS = [
  ["repondu", "Répondu"],
  ["accepte", "Accepté"],
  ["refuse", "Refusé"],
  ["montage_recu", "Texte reçu"],
  ["paye", "Payé"],
  ["commande", "Commandé"],
  ["montage_accepte", "Montage accepté"],
  ["expedie", "Expédié"],
  ["livre", "Livré"],
  ["archive", "Archivé"],
  // « Retirée par le client » (demande du propriétaire, 2026-10-06) : retrait depuis
  // le portail client avec raison obligatoire — pilule GRISE, fin des relances
  ["retire", "Retirée par le client"],
];

// Pilule de l'étape « Montage accepté » — flux ÉPREUVE (demande du propriétaire,
// 2026-10-06) : le libellé suit la réponse du client (bleu = envoyée, rouge =
// modifications demandées, or = acceptée)
const PROOF_LABELS = { envoyee: "Épreuve envoyée", modifications: "Modifications demandées", acceptee: "Épreuve acceptée" };
const stepLabel = (r, key, lab) =>
  (key === "montage_accepte" && r.proof?.status ? PROOF_LABELS[r.proof.status] || lab : lab);


// Marge du dossier payé (demande du propriétaire, 2026-10-16) : vente nette
// − coûtants produits connus − coûtant livraison. ADMIN SEULEMENT (coûtants) —
// chargée à la demande, jamais chez le vendeur ni le client
const MargeDossier = ({ r }) => {
  const [m, setM] = useState(undefined);
  useEffect(() => {
    let stop = false;
    api.get(`/admin/quote-requests/${r.id}/marge`)
      .then((res) => { if (!stop) setM(res.data); })
      .catch(() => { if (!stop) setM(null); });
    return () => { stop = true; };
  }, [r.id]);
  if (!m) return null;
  return (
    <p className="text-[11px] mt-1" data-testid={`quote-marge-${r.id}`}>
      <span className="text-amber-300/90 font-semibold">Marge : {m.marge.toFixed(2)} $</span>
      <span className="text-ivory/50"> — vente {m.vente.toFixed(2)} $ − coûtants {m.cout_produits.toFixed(2)} $ − livraison {m.cout_livraison.toFixed(2)} $</span>
      {m.articles_sans_coutant?.length > 0 && (
        <span className="block text-amber-200/70" data-testid={`quote-marge-manquants-${r.id}`}>
          sans coûtant : {m.articles_sans_coutant.join(" · ")}
        </span>
      )}
    </p>
  );
};
const CARRIER_LABELS = { postes_canada: "Postes Canada", ups: "UPS", fedex: "FedEx", purolator: "Purolator", dhl: "DHL", gls: "GLS", ics: "ICS Courier", autre: "Autre" };

// Prix COÛTANT de chaque ligne, à côté du prix client (demande du propriétaire,
// 2026-10-20) : admin + vendeur AUTORISÉ (case « Voir les prix coûtants » cochée
// sur une liste) — JAMAIS côté client. Cache partagé par soumission : une seule
// requête par dossier peu importe le nombre de lignes. 403 = non autorisé → rien.
const _cacheCoutants = new Map();
const _chargeCoutants = (qid) => {
  if (!_cacheCoutants.has(qid)) {
    _cacheCoutants.set(qid, api.get(`/admin/quote-requests/${qid}/coutants`)
      .then((res) => res.data.coutants || []).catch(() => null));
  }
  return _cacheCoutants.get(qid);
};
const CoutantLigne = ({ qid, index, qty }) => {
  const [couts, setCouts] = useState(undefined);
  useEffect(() => {
    let stop = false;
    _chargeCoutants(qid).then((c) => { if (!stop) setCouts(c); });
    return () => { stop = true; };
  }, [qid]);
  if (!couts) return null;
  const c = couts[index];
  if (c == null) {
    return (
      <p className="text-[10px] mt-1.5 text-ivory/35" data-testid={`quote-coutant-${qid}-${index}`}>
        Prix coûtant inconnu pour cette ligne
      </p>
    );
  }
  return (
    <p className="text-[10px] mt-1.5 text-amber-300/85" data-testid={`quote-coutant-${qid}-${index}`}>
      Prix coûtant : <strong>{c.toFixed(2)} $ /un.</strong>
      {qty > 1 && <span className="text-amber-300/60"> · {(c * qty).toFixed(2)} $ pour {qty}</span>}
    </p>
  );
};

// Prix de détail SUGGÉRÉ par ligne (demande du propriétaire, 2026-10-20) :
// coûtant × multiplicateur (« 3 » = ×3, « 200 % » = +200 %), ARRONDI AU ,95 $
// SUPÉRIEUR (ex. : 2,50 $ × 3 = 7,50 $ → 7,95 $) — bouton « Appliquer » remplit
// le prix produit. ADMIN SEULEMENT (l'appelant ne l'affiche qu'en mode admin) ;
// s'appuie sur le même cache de coûtants que CoutantLigne.
const parseMultiplicateur = (s) => {
  const brut = String(s || "3").trim().replace(",", ".");
  if (brut.endsWith("%")) {
    const p = parseFloat(brut.slice(0, -1));
    return isFinite(p) && p > 0 ? 1 + p / 100 : 3;
  }
  const m = parseFloat(brut.replace(/^[x×]\s*/i, ""));
  return isFinite(m) && m > 0 ? m : 3;
};
// Arrondi au ,95 $ SUPÉRIEUR, en cents pour éviter les pièges de virgule flottante
const arrondi95 = (p) => {
  const cents = Math.round(p * 100);
  const base = Math.floor(cents / 100) * 100 + 95;
  return (cents <= base ? base : base + 100) / 100;
};
const SuggPrixLigne = ({ qid, index, multiplier, onApply }) => {
  const [couts, setCouts] = useState(undefined);
  useEffect(() => {
    let stop = false;
    _chargeCoutants(qid).then((c) => { if (!stop) setCouts(c); });
    return () => { stop = true; };
  }, [qid]);
  if (!couts) return null;
  const c = couts[index];
  if (c == null) return null;
  const sugg = arrondi95(c * parseMultiplicateur(multiplier));
  const mult = parseMultiplicateur(multiplier);
  return (
    <p className="text-[10px] mt-0.5 text-gold/85" data-testid={`quote-sugg-${qid}-${index}`}>
      Prix de détail suggéré : <strong>{sugg.toFixed(2)} $ /un.</strong>
      <span className="text-ivory/45"> (coûtant {c.toFixed(2)} $ × {Math.round(mult * 100) / 100}, arrondi au ,95 $)</span>
      <button type="button" onClick={() => onApply(sugg)} data-testid={`quote-sugg-appliquer-${qid}-${index}`}
        className="ml-2 underline underline-offset-2 text-gold hover:text-champagne transition-colors">
        Appliquer
      </button>
    </p>
  );
};

// Lien de suivi public par transporteur (« Autre » : pas d'URL connue — texte brut)
const CARRIER_TRACK_URL = {
  postes_canada: "https://www.canadapost-postescanada.ca/track-reperage/fr#/search?searchFor={n}",
  ups: "https://www.ups.com/track?tracknum={n}&loc=fr_CA",
  fedex: "https://www.fedex.com/fedextrack/?trknbr={n}",
  purolator: "https://www.purolator.com/fr/shipping/tracker?pin={n}",
  dhl: "https://www.dhl.com/ca-fr/home/suivi.html?tracking-id={n}",
  gls: "https://ship.gls-canada.com/tracking/{n}",
  ics: "https://www.icscourier.ca/usercontrols/TrackingViewDetail.aspx?TrackNumber={n}&Lan=FR&sign=False",
  stallion: "https://track.stallionexpress.ca/{n}",
};
const trackingUrl = (carrier, n) => (CARRIER_TRACK_URL[carrier] && n ? CARRIER_TRACK_URL[carrier].replace("{n}", n) : "");
const carrierLabel = (ship) => (ship?.carrier === "autre" ? (ship?.carrier_other || "Autre") : CARRIER_LABELS[ship?.carrier] || "transporteur");

// Motifs de refus proposés (admin ET client) — la note libre est obligatoire
const REFUSAL_MOTIFS = [
  ["prix", "Le prix"],
  ["delai", "Le délai de livraison"],
  ["annule", "Projet annulé / changement d'idée"],
  ["autre", "Autre"],
];

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) =>
  ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const fmtDate = (iso) => fmtDateTimeMtl(iso);  // heure de Montréal (règle permanente)

// AVIS DE RETARD (demande du propriétaire, 2026-10-22) : macaron rouge sur la
// fiche quand la date de livraison ESTIMÉE du transporteur dépasse le « Livrer
// avant le ». Estimation = date exacte du suivi (estimated_delivery) si le
// transporteur l'a fournie, sinon création de l'étiquette + délai annoncé
// (estimated_delivery_days) — même source que la note pré-remplie du courriel.
const MOIS_COURTS = ["janv.", "févr.", "mars", "avr.", "mai", "juin",
                     "juill.", "août", "sept.", "oct.", "nov.", "déc."];
const etaTransporteur = (r) => {
  const st = r.stallion || {};
  if (!st.tracking_number) return null;
  if (st.estimated_delivery) {
    const d = new Date(String(st.estimated_delivery).slice(0, 10) + "T12:00:00");
    return isNaN(d) ? null : d;
  }
  if (st.estimated_delivery_days && st.created_at) {
    const d = new Date(st.created_at);
    if (isNaN(d)) return null;
    d.setDate(d.getDate() + Number(st.estimated_delivery_days));
    return d;
  }
  return null;
};
const retardEstime = (r) => {
  if (!r.needed_by || r.status?.livre) return null;  // livrée : plus d'utilité
  const eta = etaTransporteur(r);
  if (!eta) return null;
  const limite = new Date(String(r.needed_by).slice(0, 10) + "T23:59:59");
  if (isNaN(limite)) return null;
  return eta > limite ? eta : null;
};
const fmtDateCourte = (d) => `${d.getDate()} ${MOIS_COURTS[d.getMonth()]} ${d.getFullYear()}`;

// Adresse COMPLÈTE du client (demande du propriétaire, 2026-10-07) : rue (+ suite),
// ville, province, code postal — affichée sur la carte et dans la fiche
const adresseComplete = (r) => {
  const rue = [r.address1, r.address2].filter(Boolean).join(" ");
  const villeCp = [[r.city, r.province].filter(Boolean).join(", "), r.postal_code]
    .filter(Boolean).join("  ");
  return [rue, villeCp].filter(Boolean).join(", ");
};

// Texte de gravure en ÉVIDENCE sur la version papier (demande du propriétaire) :
// chaque « Ligne N : … » sur SA propre ligne, plus grande et en gras, au lieu
// d'être enfilée avec « / ». Fonctionne FR (« Texte — Ligne 1 : … ») et EN.
const detailsHtml = (d) => {
  const e = esc(d || "");
  const m = e.match(/^(.*?)(?:Texte|Text) — ([\s\S]*)$/);
  if (!m) return e;
  let [, avant, apres] = m;
  // Le bloc texte s'arrête à la PREMIÈRE métadonnée suivante (notes, couleur,
  // mention « à déterminer ») — elle suit en dessous, non collée à la dernière ligne
  let reste = "";
  let finIdx = apres.length;
  for (const fin of [" — Notes : ", " — Note : ", " — Couleur : ", " — Color: ", " — Gravure/impression"]) {
    const j = apres.indexOf(fin);
    if (j !== -1 && j < finIdx) finIdx = j;
  }
  if (finIdx < apres.length) { reste = apres.slice(finIdx); apres = apres.slice(0, finIdx); }
  const lignes = apres.split(" / ").map((x) => x.trim()).filter(Boolean)
    .map((x) => `<span style="font-size:15px;color:#111"><strong>${x}</strong></span>`).join("<br>");
  avant = avant.replace(/[ —]+$/, "");
  return (avant ? `${avant}<br>` : "") + `Texte :<br>${lignes}`
    + (reste.trim() ? `<br>${reste.trim().replace(/^—\s*/, "")}` : "");
};

// Fenêtre d'impression propre de la soumission (logo imprimante de chaque demande)
// Corps HTML d'une soumission pour l'impression (partagé : simple ET en lot)
function quoteBodyHtml(r) {
  const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;
  const METHOD_FR = { laser: "Gravure au laser", serigraphie: "Impression sérigraphie" };
  const rows = (r.items || []).map((it, i) =>
    `<tr><td>${i + 1}</td><td>${esc(it.catalogue) || "Hors catalogue"}</td><td>${esc(it.product_name || (it.page ? `p. ${it.page}` : "")) || "—"}` +
    // Type d'inscription (manuel admin ou portail client) — l'atelier en a besoin
    `${it.method && METHOD_FR[it.method] ? `<br><strong>${METHOD_FR[it.method]}</strong>` : ""}` +
    `${it.details ? "<br>" + detailsHtml(it.details) : ""}` +
    // Miniature du logo joint à l'article (demande du propriétaire — atelier)
    // Miniatures de TOUTES les pièces jointes de la ligne (demande du propriétaire)
    `${[it.logo_path, ...(it.attachments || [])].filter(Boolean).map((p) => `<br><img class="logo-img" src="${esc(fileUrl(p))}" alt="Pièce jointe">`).join("")}</td>` +
    `<td>${esc(it.product_no) || "—"}</td><td style="text-align:center">${esc(it.qty)}</td></tr>`).join("");
  const steps = STEPS.filter(([k]) => r.status?.[k])
    .map(([k, lab]) => `${stepLabel(r, k, lab)} (${fmtDate(r.status_at?.[k])})`).join(" · ") || "Aucune";
  const portalLogo = r.client_response?.logo
    ? `<h2>Logo du client (portail)</h2><p><img class="logo-img" src="${esc(process.env.REACT_APP_BACKEND_URL + r.client_response.logo)}" alt="Logo du client">` +
      `${r.client_response.logo_name ? `<br><span class="steps">${esc(r.client_response.logo_name)}</span>` : ""}` +
      // Pièces jointes supplémentaires du client (plusieurs possibles)
      (r.client_response.attachments || []).map((a) => {
        const u = typeof a === "string" ? a : a.url;
        return u ? ` <img class="logo-img" src="${esc(process.env.REACT_APP_BACKEND_URL + u)}" alt="Pièce jointe du client">` : "";
      }).join("") + `</p>`
    : "";
  // Réponse du portail (demande du propriétaire) : type d'inscription (gravure au
  // laser / impression sérigraphie) + textes choisis — l'atelier en a besoin
  const resp = r.client_response || {};
  // Récap des modèles COCHÉS en tête (demande du propriétaire, 2026-10-28) :
  // noms + quantités, pour que l'imprimé se lise en un coup d'œil
  const cochesIdx = resp.selected || [];
  const recapCoches = cochesIdx.length
    ? `<p class="meta"><strong>Modèles cochés :</strong> ${cochesIdx.map((i) => { const it = (r.items || [])[i] || {}; const qte = parseFloat(it.qty) || 0; return esc(`${it.product_name || `Choix nº ${i + 1}`}${qte > 1 ? ` × ${it.qty}` : ""}`); }).join(" • ")}</p>`
    : "";
  const respLines = cochesIdx.map((i) => {
    const txt = ((resp.texts || {})[String(i)] || "").trim();
    const m = METHOD_FR[(resp.methods || {})[String(i)]] || "";
    if (!txt && !m) return null;
    return `<p class="meta"><strong>Choix ${i + 1}</strong>${m ? ` — <strong>${esc(m)}</strong>` : ""}${txt ? ` : « ${esc(txt)} »` : ""}</p>`;
  }).filter(Boolean).join("");
  const respBloc = recapCoches + respLines;
  return `
    <h1>Mr Trophée — Soumission rapide</h1>
    <p class="steps">Reçue le ${fmtDate(r.created_at)}</p>
    <h2>Client</h2>
    <p class="meta">
      Nom complet : <strong>${esc(r.name)}</strong><br>
      ${r.organization ? `Organisation : ${esc(r.organization)}<br>` : ""}
      ${r.city ? `Ville : ${esc(r.city)}<br>` : ""}
      Téléphone : <strong>${esc(r.phone)}</strong><br>
      Courriel : ${esc(r.email)}<br>
      ${r.needed_by ? `<strong>Livrer avant le : ${esc(r.needed_by)}</strong><br>` : ""}
      ${r.delivery_date ? `<strong>Livraison prévue le : ${esc(r.delivery_date)}</strong><br>` : ""}
      ${r.invoice_no ? `Nº de facture : <strong>${esc(r.invoice_no)}</strong><br>` : ""}
    </p>
    <h2>Choix de produits</h2>
    <table><tr><th>Choix</th><th>Catalogue</th><th>Produit</th><th>Nº produit</th><th>Quantité</th></tr>${rows}</table>
    ${respBloc ? `<h2>Réponse du client (portail)</h2>${respBloc}` : ""}
    ${portalLogo}
    ${r.shipping ? `<h2>Expédition</h2><p class="meta">${r.shipping.method === "hand" ? "Livraison en main propre" : r.shipping.method === "pickup" ? "Prête à être ramassée — <strong>Mr Trophée, 1314 ch. du Lac-Saint-Louis, Léry (Québec) J6N 1B1</strong> — +1 514-565-6665" : `Transporteur — suivi : <strong>${esc(r.shipping.tracking)}</strong>`} (${fmtDate(r.shipping.at)})</p>` : ""}
    <h2>Étapes</h2><p class="steps">${esc(steps)}</p>`;
}

// Fenêtre d'impression : une ou PLUSIEURS soumissions, chacune sur sa page
// (« imprimer en lot » — demande du propriétaire)
function openPrint(title, bodies) {
  const w = window.open("", "_blank", "width=820,height=900");
  if (!w) { toast.error("Fenêtre bloquée — autorisez les popups pour imprimer."); return; }
  w.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8">
    <title>${esc(title)}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:32px;font-size:14px}
      h1{font-size:22px;margin:0 0 4px} h2{font-size:14px;text-transform:uppercase;letter-spacing:1px;color:#8a6d00;margin:24px 0 8px}
      table{border-collapse:collapse;width:100%} td,th{border:1px solid #999;padding:6px 10px;text-align:left}
      th{background:#f5edd8} .meta{line-height:1.9} .steps{color:#444;font-size:13px}
      .logo-img{max-height:90px;max-width:220px;border:1px solid #bbb;padding:4px;margin-top:6px;background:#fff}
      .page{page-break-after:always}
    </style></head><body>
    ${bodies.map((b) => `<div class="page">${b}</div>`).join("")}
    </body></html>`);
  w.document.close();
  // Attendre le chargement des miniatures de logos AVANT d'ouvrir le dialogue
  // d'impression (sinon elles manquent à l'impression) — 4 s max en filet de sécurité
  const imgs = Array.from(w.document.images || []);
  const ready = Promise.all(imgs.map((img) => img.complete
    ? Promise.resolve()
    : new Promise((res) => { img.onload = res; img.onerror = res; })));
  Promise.race([ready, new Promise((res) => setTimeout(res, 4000))]).then(() => {
    w.focus();
    w.print();
  });
}

function printQuote(r) {
  openPrint(`Soumission — ${r.name}`, [quoteBodyHtml(r)]);
}

export default function QuoteRequestsTab({ vendeur = false, onCycle }) {
  const [items, setItems] = useState(null);
  const [vendeurs, setVendeurs] = useState([]);            // comptes vendeurs (cases « visible par »)
  const [showArchived, setShowArchived] = useState(false);
  // Filtre « Lignes sans coûtant » (demande du propriétaire, 2026-10-20) :
  // repérer vite les dossiers avec au moins une ligne sans prix coûtant connu.
  // Admin toujours ; vendeur seulement si sa case « Voir les prix coûtants »
  // est cochée (vérifié côté serveur de toute façon).
  const [sansCoutant, setSansCoutant] = useState(false);
  const sansCoutantRef = useRef(false); // lu par le rafraîchissement auto (30 s)
  const [coutsAutorise, setCoutsAutorise] = useState(false);
  const autoBusy = useRef(false);  // garde anti-chevauchement du rafraîchissement auto (30 s)
  const connus = useRef(null);     // ids déjà vus (null = premier chargement)
  const [nouveaux, setNouveaux] = useState({});  // {id: true} — badge « Nouveau » pulsant
  const [majLe, setMajLe] = useState(null);      // heure de la dernière actualisation (Montréal)
  const [selected, setSelected] = useState({});   // cases cochées {id: true} — actions en lot
  const [batchBusy, setBatchBusy] = useState(""); // "archive" | "delete" pendant l'action en lot
  const [newBusy, setNewBusy] = useState(false);  // bouton « Nouvelle fiche » (création à la main, 2026-10-22)
  const [dupBusy, setDupBusy] = useState("");     // id de la fiche en cours de duplication (2026-10-22)
  const [openId, setOpenId] = useState(null);       // œil : détails dépliés
  const [shipFor, setShipFor] = useState(null);     // popup « Expédié »
  const [signFor, setSignFor] = useState(null);     // popup « Livré » — signature (main propre / ramassage)
  const [signBusy, setSignBusy] = useState(false);
  const [shipMethod, setShipMethod] = useState("carrier");
  const [shipCarrier, setShipCarrier] = useState("postes_canada");
  const [shipCarrierOther, setShipCarrierOther] = useState("");  // nom libre si « Autre »
  const [shipCost, setShipCost] = useState("");                  // prix COÛTANT (admin seulement)
  const [shipTracking, setShipTracking] = useState("");
  const [shipNoEmail, setShipNoEmail] = useState(false);  // case « ne pas envoyer de courriel au client »
  // Case « sans courriel » PERSISTANTE du dossier (no_email, enregistrée côté
  // serveur — demande du propriétaire 2026-10-16) : lue depuis le dossier,
  // elle survit au rechargement et pause aussi les courriels automatiques
  const [zoomImg, setZoomImg] = useState(null);           // pièce jointe agrandie (visionneuse — clic sur la miniature de gravure)
  const [refuseNoEmail, setRefuseNoEmail] = useState(false); // case « sans courriel » du popup de refus
  const [shipBusy, setShipBusy] = useState(false);
  const [pricingDraft, setPricingDraft] = useState({});  // {id soumission: [{product, engraving, insert, matrix, transport}]}
  const [itemsDraft, setItemsDraft] = useState({});      // {id soumission: [lignes de produits modifiées]}
  const [detailsDraft, setDetailsDraft] = useState({});  // {id soumission: {name, organization, city, phone, email, needed_by}}
  const [busyId, setBusyId] = useState("");
  const [payBusy, setPayBusy] = useState("");            // création du lien Stripe en cours
  const [refuseFor, setRefuseFor] = useState(null);      // popup « Refusé » (motif + note obligatoires)
  const [proofFor, setProofFor] = useState(null);   // popup « Épreuve » (étape « Montage accepté »)
  const [paiementFor, setPaiementFor] = useState(null); // popup « Payé » (choix du mode de paiement)
  const [paiementMethod, setPaiementMethod] = useState(""); // "interac" | "comptant" | "qr" | "stripe"
  const [paiementComplet, setPaiementComplet] = useState(true); // true = complet, false = partiel
  const [paiementMontant, setPaiementMontant] = useState(""); // montant partiel (si paiementComplet = false)
  const [paiementUnite, setPaiementUnite] = useState("$"); // unité de l'acompte Stripe : "$" | "%" (admin/vendeur seuls)
  const [paiementBusy, setPaiementBusy] = useState(false); // création du lien Stripe + aperçu en cours
  // Dépôt MANUEL (demande du propriétaire, 2026-10-31) : enregistrer un acompte
  // reçu hors ligne SANS envoyer de courriel ni de facture au client
  const [depotFor, setDepotFor] = useState(null);
  const [depotMontant, setDepotMontant] = useState("");
  const [depotUnite, setDepotUnite] = useState("$");
  const [depotMethode, setDepotMethode] = useState("comptant");
  const [depotBusy, setDepotBusy] = useState(false);
  const [proofFile, setProofFile] = useState(null); // fichier de l'épreuve — choisi OU image collée
  const [proofDriveLink, setProofDriveLink] = useState(""); // OU lien Google Drive partagé (demande du propriétaire, 2026-07-02)
  const [proofBusy, setProofBusy] = useState(false);
  const [refuseMotif, setRefuseMotif] = useState("");
  const [refuseNote, setRefuseNote] = useState("");
  const [refuseBusy, setRefuseBusy] = useState(false);
  const [discBusy, setDiscBusy] = useState("");          // envoi de la contre-offre en cours
  const [discFor, setDiscFor] = useState(null);          // popup « Proposer un rabais »
  const [discPct, setDiscPct] = useState("5");           // pourcentage de la contre-offre (défaut 5 %)
  const [discPctDef, setDiscPctDef] = useState("5");     // % par défaut réglé dans Paramètres (demande du propriétaire)
  const [markup, setMarkup] = useState("3");             // multiplicateur du prix de détail suggéré (Paramètres, admin)
  const [vpFor, setVpFor] = useState(null);              // popup « Marquer comme vente perdue »
  const [decisionFor, setDecisionFor] = useState(null);  // petit choix Accepter/Refuser sous la pilule « Accepté » (2026-10-22)
  const [vpReason, setVpReason] = useState("");          // raison de la perte (obligatoire)
  const [vpBusy, setVpBusy] = useState(false);
  const [iaBusy, setIaBusy] = useState(false);          // rédaction du message par Claude
  const [askFor, setAskFor] = useState(null);            // popup « Demander plus d'info »
  const [factureRapide, setFactureRapide] = useState(null); // popup consultation rapide — dernières factures payées du client
  const [notePopup, setNotePopup] = useState("");           // note interne du client (éditée dans le popup)
  const [notePopupBusy, setNotePopupBusy] = useState(false);
  // Seuil de la pastille « Client or » — réglable dans Paramètres (demande du
  // propriétaire, 2026-10-24) ; 10 000 $ par défaut
  const [seuilOr, setSeuilOr] = useState(10000);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      const v = Number(data?.client_or_seuil || 0);
      if (v > 0) setSeuilOr(v);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Ouvre/bascule le popup en chargeant la note interne de la fiche affichée
  const ouvrirFacture = (f) => { setFactureRapide(f); setNotePopup(f.note || ""); };
  const [askMsg, setAskMsg] = useState("");
  const [askBusy, setAskBusy] = useState(false);
  const [sortMode, setSortMode] = useState("recent");    // classement : récentes, nom ou courriel (A→Z / Z→A)
  // Recherche plein texte du classement (demande du propriétaire) : retrouve un
  // dossier par Nº DE FACTURE, nom, organisation, courriel, téléphone, ville,
  // référence ou produit.
  const [search, setSearch] = useState("");
  // Recherche rapide (demande du propriétaire, 2026-10-22) : filtre les fiches
  // PENDANT LA FRAPPE — nom, courriel, téléphone (écriture libre : tirets et
  // espaces ignorés), ville, nº de facture, produit… Insensible aux accents
  // (« Valerie » trouve « Valérie »).
  const normTxt = (v) => (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const matchSearch = (r) => {
    const brut = search.trim();
    if (!brut) return true;
    const s = normTxt(brut);
    const hay = normTxt([r.name, r.organization, r.email, r.phone, r.city, r.id, r.invoice_no,
                 ...(r.items || []).map((it) => `${it.product_name || ""} ${it.product_no || ""}`)]
      .filter(Boolean).join(" "));
    if (hay.includes(s)) return true;
    // Téléphone : la recherche tolère l'écriture (514-555-0000, espaces, parenthèses)
    const chiffres = brut.replace(/\D/g, "");
    if (chiffres.length >= 3) {
      const tel = (r.phone || "").replace(/\D/g, "");
      if (tel.includes(chiffres)) return true;
    }
    return false;
  };

  // Raccourci clavier Ctrl+F (demande du propriétaire, 2026-10-22) : ouvre la
  // recherche de l'onglet SANS lâcher le clavier — remplace la recherche du
  // fureteur dans cet écran (le texte tapé y est présélectionné)
  const searchRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "f" || e.key === "F")) {
        e.preventDefault();
        const el = searchRef.current;
        if (el) {
          el.scrollIntoView({ block: "center", behavior: "smooth" });
          el.focus();
          el.select();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const num = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".")); return Number.isFinite(n) && n > 0 ? n : 0; };
  // Lignes de produits MODIFIABLES (demande du propriétaire) : brouillon par
  // soumission — catalogue, nº de produit, description, quantité, ajout/retrait.
  const { t } = useLang();
  // Choix de catalogue offerts par ligne (même liste que le formulaire client)
  const catalogueOptions = useMemo(
    () => (t("catalogues.groups") || []).flatMap((g) => (g.links || []).map((l) => l.label)),
    [t]);
  const itemsOf = (r) => itemsDraft[r.id] || (r.items || []).map((it) => ({ ...it }));
  // Textes de gravure/impression + pièces jointes d'un dossier (demande du
  // propriétaire) : réutilisé par la carte (espace gravure central) et la fiche
  const METHOD_LABELS = { laser: "Gravure au laser", serigraphie: "Impression sérigraphie" };
  const gravureOf = (r) => {
    const resp = r.client_response || {};
    const textes = itemsOf(r).map((it, i) => ({
      nom: it.product_name || `Choix ${i + 1}`,
      texte: ((resp.texts || {})[String(i)] || it.details || "").trim(),
      // Méthode : choix du client au portail, sinon saisie manuelle de l'admin
      methode: METHOD_LABELS[(resp.methods || {})[String(i)] || it.method || ""] || "",
    })).filter((t) => t.texte || t.methode);
    const fichiers = itemsOf(r)
      .flatMap((it, i) => {
        // Pièces jointes multiples (demande du propriétaire) : logo principal + les autres
        const paths = [it.logo_path, ...(it.attachments || [])].filter(Boolean);
        return paths.map((p, j) => ({
          url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`,
          nom: `${it.product_name || `Choix ${i + 1}`} — pièce jointe${paths.length > 1 ? ` ${j + 1}` : ""}`,
        }));
      })
      .filter(Boolean);
    if (resp.logo) {
      fichiers.push({ url: `${process.env.REACT_APP_BACKEND_URL}${resp.logo}`,
                      nom: `Logo du client${resp.logo_name ? ` — ${resp.logo_name}` : ""} (portail)` });
    }
    // Pièces jointes supplémentaires du client au portail (plusieurs possibles)
    for (const [j, a] of (resp.attachments || []).entries()) {
      const url = typeof a === "string" ? a : a.url;
      const nm = typeof a === "string" ? "" : a.name;
      if (url) {
        fichiers.push({ url: `${process.env.REACT_APP_BACKEND_URL}${url}`,
                        nom: `Pièce jointe du client ${j + 2}${nm ? ` — ${nm}` : ""} (portail)` });
      }
    }
    return { textes, fichiers };
  };
  // Brouillon de prix d'une soumission : saisie en cours, sinon prix enregistrés,
  // sinon vides — toujours réaligné sur le NOMBRE de lignes du brouillon produits
  const draftOf = (r) => {
    const n = itemsOf(r).length;
    let arr = pricingDraft[r.id]
      || ((r.pricing && r.pricing.length === (r.items || []).length) ? r.pricing : (r.items || []).map(() => ({})));
    if (arr.length < n) arr = [...arr, ...Array.from({ length: n - arr.length }, () => ({}))];
    return arr.slice(0, n);
  };
  const lineTotal = (it, p) => {
    const base = Math.round(((num(it.qty) || 1) * (num(p.product) + num(p.engraving))
      + num(p.insert) + num(p.matrix) + num(p.transport)) * 100) / 100;
    // Rabais de ligne (% ou $ fixe — le % l'emporte, comme côté serveur)
    if (num(p.rabais_pct)) return Math.max(0, Math.round(base * (1 - Math.min(num(p.rabais_pct), 100) / 100) * 100) / 100);
    return Math.max(0, Math.round((base - num(p.rabais_amt)) * 100) / 100);
  };
  // Mode du rabais de ligne : « pct » (%) ou « amt » ($) — déduit du champ rempli,
  // ou du choix de bascule en cours (rabais_mode, champ de brouillon seulement)
  const rabaisModeOf = (p) => p.rabais_mode || (num(p.rabais_amt) > 0 ? "amt" : "pct");
  const setRabais = (r, i, v) => setPricingDraft((d) => {
    const arr = draftOf(r).map((x) => ({ ...x }));
    const mode = rabaisModeOf(arr[i]);
    arr[i] = { ...arr[i], rabais_mode: mode,
               rabais_pct: mode === "pct" ? v : "", rabais_amt: mode === "amt" ? v : "" };
    return { ...d, [r.id]: arr };
  });
  const toggleRabaisMode = (r, i) => setPricingDraft((d) => {
    const arr = draftOf(r).map((x) => ({ ...x }));
    const p = arr[i] || {};
    const mode = rabaisModeOf(p) === "pct" ? "amt" : "pct";
    const val = (mode === "pct" ? p.rabais_pct : p.rabais_amt) || "";
    arr[i] = { ...p, rabais_mode: mode,
               rabais_pct: mode === "pct" ? val : "", rabais_amt: mode === "amt" ? val : "" };
    return { ...d, [r.id]: arr };
  });
  const rabaisLabel = (p) => (num(p.rabais_pct) ? `−${p.rabais_pct} %`
    : num(p.rabais_amt) ? `−${num(p.rabais_amt).toFixed(2)} $` : "");
  // Rabais GLOBAL (toute la soumission — gros volumes) : % ou $ fixe, en plus des
  // rabais par ligne. Brouillon {mode, value} par soumission.
  const [globalDraft, setGlobalDraft] = useState({});
  const globalOf = (r) => globalDraft[r.id] || {
    mode: num(r.global_rabais_amt) > 0 ? "amt" : "pct",
    value: num(r.global_rabais_pct) ? String(r.global_rabais_pct)
      : num(r.global_rabais_amt) ? String(r.global_rabais_amt) : "",
  };
  const setGlobal = (r, patch) => setGlobalDraft((d) => ({ ...d, [r.id]: { ...globalOf(r), ...patch } }));
  const globalPayload = (r) => {
    const g = globalOf(r);
    return { global_rabais_pct: g.mode === "pct" ? num(g.value) : 0,
             global_rabais_amt: g.mode === "amt" ? num(g.value) : 0 };
  };
  const globalLabel = (r) => {
    const g = globalOf(r);
    if (!num(g.value)) return "";
    return g.mode === "pct" ? `−${g.value} %` : `−${num(g.value).toFixed(2)} $`;
  };
  // Grand total affiché : lignes (rabais de ligne déduits) PUIS rabais global
  const grandNet = (r) => {
    const lignes = itemsOf(r).reduce((s, it, i) => s + lineTotal(it, draftOf(r)[i] || {}), 0);
    const g = globalOf(r);
    const v = num(g.value);
    if (!v) return Math.round(lignes * 100) / 100;
    if (g.mode === "pct") return Math.max(0, Math.round(lignes * (1 - Math.min(v, 100) / 100) * 100) / 100);
    return Math.max(0, Math.round((lignes - v) * 100) / 100);
  };
  // Totaux avant/après taxes affichés dans la fiche (demande du propriétaire) —
  // mêmes taux que le lien de paiement Stripe et le PDF : TPS 5 % + TVQ 9,975 %
  const r2 = (v) => Math.round(v * 100) / 100;
  // « Exempt de taxes » (demande du propriétaire) : TPS/TVQ à 0 $ pour la fiche
  const tpsOf = (r) => (r.tax_exempt ? 0 : r2(grandNet(r) * 0.05));
  const tvqOf = (r) => (r.tax_exempt ? 0 : r2(grandNet(r) * 0.09975));
  const totalTtc = (r) => r2(grandNet(r) + tpsOf(r) + tvqOf(r));
  const setPrice = (r, i, key, v) => setPricingDraft((d) => {
    const arr = draftOf(r).map((x) => ({ ...x }));
    arr[i] = { ...arr[i], [key]: v };
    return { ...d, [r.id]: arr };
  });
  const setItem = (r, i, key, v) => setItemsDraft((d) => {
    const arr = itemsOf(r).map((x) => ({ ...x }));
    arr[i] = { ...arr[i], [key]: key === "qty" ? v.replace(/[^0-9]/g, "") : v };
    return { ...d, [r.id]: arr };
  });
  // Infos CLIENT modifiables (demande du propriétaire — « tout modifier ») :
  // nom, organisation, ville, téléphone, courriel, « livrer avant le », nº de
  // facture, date de livraison prévue
  const DETAIL_FIELDS = [["name", "Nom complet"], ["organization", "Organisation"],
                         ["address1", "Adresse"], ["address2", "Adresse (suite)"],
                         ["city", "Ville"], ["province", "Prov."], ["postal_code", "Code postal"],
                         ["phone", "Téléphone"],
                         ["email", "Courriel"], ["needed_by", "Livrer avant le"],
                         ["invoice_no", "Nº de facture"],
                         ["delivery_date", "Livraison prévue le (AAAA-MM-JJ)"]];
  // Catégories de vente (demande du propriétaire — statistiques à long terme) :
  // classement manuel de chaque dossier, admin seulement — même liste que le backend
  const SALES_CATEGORIES = [["resine", "Résine"], ["verre", "Verre"], ["cristal", "Cristal"],
                            ["acrylique", "Acrylique"], ["medailles_serie", "Médailles de série"],
                            ["medailles_sur_mesure", "Médailles sur mesure"], ["plaques", "Plaques"],
                            ["gravure", "Gravure seulement"], ["impression", "Impression seulement"],
                            ["promotionnel", "Promotionnel"], ["autres", "Autres"]];
  const categoryLabel = (k) => (SALES_CATEGORIES.find(([key]) => key === k) || [])[1] || "";
  const detailsOf = (r) => detailsDraft[r.id] || {
    name: r.name || "", organization: r.organization || "", city: r.city || "",
    address1: r.address1 || "", address2: r.address2 || "",
    province: r.province || "", postal_code: r.postal_code || "",
    phone: r.phone || "", email: r.email || "", needed_by: r.needed_by || "",
    invoice_no: r.invoice_no || "", delivery_date: r.delivery_date || "",
    // Blocs « Facturé à » / « Livrer à » (demande du propriétaire, 2026-10-07)
    facture_a: r.facture_a || {}, livraison_a: r.livraison_a || {},
  };
  const setDetail = (r, key, v) => setDetailsDraft((d) => ({
    ...d, [r.id]: { ...detailsOf(r), [key]: v },
  }));
  // Sous-champ d'un bloc d'adresse (facture_a / livraison_a)
  const setDetailBloc = (r, bloc, key, v) => setDetailsDraft((d) => ({
    ...d, [r.id]: { ...detailsOf(r), [bloc]: { ...(detailsOf(r)[bloc] || {}), [key]: v } },
  }));
  // Case « Copier l'adresse du client » (demande du propriétaire, 2026-10-19) :
  // cocher remplit le bloc Facturé à / Livrer à avec les coordonnées du client
  // ci-dessus (ajustables ensuite champ par champ) ; décocher vide le bloc,
  // ce qui redonne le comportement « vide = identique au client »
  const BLOC_ADDR_KEYS = ["name", "address1", "address2", "city", "province", "postal_code"];
  const adresseCopiee = (r, bloc) => {
    const det = detailsOf(r);
    const b = det[bloc] || {};
    return !!(b.name || b.address1 || b.city) &&
      BLOC_ADDR_KEYS.every((k) => (b[k] || "") === (det[k] || ""));
  };
  const copierAdresseClient = (r, bloc, on) => setDetailsDraft((d) => {
    const det = detailsOf(r);
    const blocVal = on
      ? Object.fromEntries(BLOC_ADDR_KEYS.map((k) => [k, det[k] || ""]))
      : {};
    return { ...d, [r.id]: { ...det, [bloc]: blocVal } };
  });
  const addItem = (r) => {
    setItemsDraft((d) => ({
      ...d, [r.id]: [...itemsOf(r), { catalogue: "", product_name: "", product_no: "", qty: 1 }],
    }));
    // Le brouillon de PRIX suit le même indice (demande du propriétaire, 2026-10-23 :
    // le prix tapé sur la nouvelle ligne « n'accrochait » pas chez certains)
    setPricingDraft((d) => ({ ...d, [r.id]: [...draftOf(r), {}] }));
  };
  // Téléversement MANUEL de pièces jointes sur une ligne (demande du propriétaire —
  // PLUSIEURS par ligne : la 1re devient le logo principal, les suivantes s'ajoutent ;
  // ex. logos reçus par courriel) : 25 Mo max chacune — « Enregistrer » pour garder
  const [logoBusy, setLogoBusy] = useState("");  // "idSoumission:indice" pendant l'envoi
  const attachmentsOf = (it) => [it.logo_path, ...(it.attachments || [])].filter(Boolean);
  // TOUTES les images jointes à la soumission (demande du propriétaire,
  // 2026-10-27 : « montrer toutes les images jointes, pas juste la
  // première ») — petite rangée de vignettes SUR la carte, sans ouvrir la
  // fiche ; 1 clic = visionneuse avec le lien copiable
  const imagesJointes = (r) => {
    const ps = [];
    for (const it of (r.items || [])) ps.push(it.logo_path, ...(it.attachments || []));
    ps.push(...(r.attachments || []));
    return [...new Set(ps.filter((p) => p && /\.(png|jpe?g|webp|gif|svg|heic|heif)$/i.test(p)))];
  };
  const setItemAttachments = (r, i, paths) => setItemsDraft((d) => {
    const arr = itemsOf(r).map((x) => ({ ...x }));
    const itm = { ...arr[i] };
    itm.logo_path = paths[0] || "";
    itm.attachments = paths.slice(1);
    arr[i] = itm;
    return { ...d, [r.id]: arr };
  });
  const uploadItemFile = async (r, i, file) => {
    // Corps commun du téléversement d'image de ligne (bouton « Joindre » ET
    // collage Ctrl+V sur la vignette produit — demande du propriétaire 2026-10-30)
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Fichier trop lourd — 25 Mo maximum."); return; }
    if (attachmentsOf(itemsOf(r)[i] || {}).length >= 10) { toast.error("Maximum 10 pièces jointes par ligne."); return; }
    setLogoBusy(`${r.id}:${i}`);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/logo", form);
      setItemAttachments(r, i, [...attachmentsOf(itemsOf(r)[i] || {}), data.path]);
      toast.success("Pièce jointe ajoutée — cliquez « Enregistrer les modifications » pour la garder.");
    } catch {
      toast.error("Téléversement impossible — réessayez.");
    } finally {
      setLogoBusy("");
    }
  };
  const uploadItemLogo = (r, i, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    uploadItemFile(r, i, file);
  };

  // Dernière ligne de prix touchée (clic/focus) — cible du collage Ctrl+V
  // d'image de produit n'importe où dans la zone des lignes (correctif 2026-10-30)
  const derniereLigneRef = useRef("");
  // PHOTO CATALOGUE À LA LIGNE (demande du propriétaire, 2026-10-30) : un Nº de
  // produit CONNU (boutique) propose d'elle-même son image dans la vignette de la
  // ligne — seulement si la ligne n'a pas encore d'image (jamais d'écrasement) ;
  // anti-rebond 600 ms pendant la saisie
  const lookupImgTimers = useRef({});
  const proposerImageCatalogue = (r, i, no) => {
    const cle = `${r.id}:${i}`;
    clearTimeout(lookupImgTimers.current[cle]);
    const brut = (no || "").trim();
    if (brut.length < 3) return;
    lookupImgTimers.current[cle] = setTimeout(async () => {
      try {
        const it = itemsOf(r)[i] || {};
        if (attachmentsOf(it).length) return;  // déjà une image : on n'écrase pas
        const { data } = await api.get("/admin/products/lookup-image", { params: { no: brut } });
        if (data?.found && !attachmentsOf(itemsOf(r)[i] || {}).length) {
          setItemAttachments(r, i, [data.path]);
          toast.success(`Image du catalogue ajoutée (${data.sku || brut}) — « Enregistrer les modifications » pour la garder.`);
        }
      } catch { /* proposition silencieuse */ }
    }, 600);
  };
  const removeItem = (r, i) => {
    if (itemsOf(r).length <= 1) { toast.error("Une soumission doit garder au moins une ligne de produit."); return; }
    if (!window.confirm(`Retirer la ligne « Choix ${i + 1} » de cette soumission ?`)) return;
    setItemsDraft((d) => ({ ...d, [r.id]: itemsOf(r).filter((_, k) => k !== i) }));
    // le brouillon de prix suit le même indice : la ligne retirée y est retirée aussi
    setPricingDraft((d) => ({ ...d, [r.id]: draftOf(r).filter((_, k) => k !== i) }));
  };

  // Enregistre les brouillons en cours : détails client, produits (si modifiés,
  // avec les prix réalignés) puis prix — utilisé par « Enregistrer », « Envoyer
  // au client », la contre-offre et le lien de paiement.
  const flushDrafts = async (r) => {
    if (itemsDraft[r.id] || detailsDraft[r.id]) {
      const payload = { ...globalPayload(r) };
      if (itemsDraft[r.id]) { payload.items = itemsOf(r); payload.pricing = draftOf(r); }
      if (detailsDraft[r.id]) payload.details = detailsOf(r);
      const { data } = await api.patch(`/admin/quote-requests/${r.id}/items`, payload);
      setItems((xs) => xs.map((x) => (x.id === r.id
        ? { ...x, items: data.items, pricing: data.pricing, ...data.details,
            global_rabais_pct: data.global_rabais_pct, global_rabais_amt: data.global_rabais_amt,
            // Ajout APRÈS la vente (demande du propriétaire, 2026-10-22) : la
            // balance ajustée revient du serveur — l'ancien lien est invalidé
            ...(data.solde_ajuste != null
              ? { payment: { ...(x.payment || {}), balance_due: data.solde_ajuste, url: "",
                             solde_ajuste_at: new Date().toISOString() } }
              : {}) } : x)));
      setItemsDraft((d) => { const c = { ...d }; delete c[r.id]; return c; });
      setDetailsDraft((d) => { const c = { ...d }; delete c[r.id]; return c; });
      setPricingDraft((d) => { const c = { ...d }; delete c[r.id]; return c; });
      setGlobalDraft((d) => { const c = { ...d }; delete c[r.id]; return c; });
      if (data.solde_ajuste != null) {
        toast.success(`Fiche modifiée — balance à encaisser ajustée automatiquement : ${Number(data.solde_ajuste).toFixed(2)} $. L'ancien lien de paiement est invalidé : recréez-le pour le solde.`);
        // COURRIEL DE SOLDE PROPOSÉ D'EMBLÉE (demande du propriétaire, 2026-10-23) :
        // après l'ajout, offrir tout de suite le courriel « nouveau solde à payer »
        // — plein solde OU dépôt convenu (ex. 50 % du solde) ; aperçu modifiable,
        // rien n'est envoyé sans « Envoyer »
        if (Number(data.solde_ajuste) > 0) {
          setSoldeOffre({ qid: r.id, name: r.name, email: r.email,
                          solde: Number(data.solde_ajuste), mode: "full", pct: 50, busy: false });
        }
      } else if (data.payment_stale) {
        toast.warning("Le lien de paiement déjà créé garde l'ANCIEN montant — recréez-le avec « Lien de paiement ».");
      }
      return;
    }
    if (pricingDraft[r.id] || globalDraft[r.id]) {
      const { data } = await api.patch(`/admin/quote-requests/${r.id}/pricing`,
        { pricing: draftOf(r), ...globalPayload(r) });
      setItems((xs) => xs.map((x) => (x.id === r.id
        ? { ...x, pricing: data.pricing,
            global_rabais_pct: data.global_rabais_pct, global_rabais_amt: data.global_rabais_amt } : x)));
      setPricingDraft((d) => { const c = { ...d }; delete c[r.id]; return c; });
      setGlobalDraft((d) => { const c = { ...d }; delete c[r.id]; return c; });
    }
  };

  const savePricing = async (r) => {
    setBusyId(r.id + ":p");
    try {
      const hadItems = !!(itemsDraft[r.id] || detailsDraft[r.id]);
      await flushDrafts(r);
      toast.success(hadItems
        ? "Modifications enregistrées pour cette soumission."
        : "Prix enregistrés pour cette soumission.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  // Terme de PAIEMENT de la fiche (demande du propriétaire, 2026-10-21) :
  // « relâchée lorsque le paiement sera reçu ou 30 jours net ou 15 jours net —
  // à mon choix ou celui convenu dans le dossier client ». Enregistré aussitôt
  // choisi ; vide = le terme convenu au DOSSIER CLIENT s'applique (sinon
  // « réception »). Imprimé sur le PDF et dans le courriel client.
  const TERMES_PAIEMENT_LABELS = { reception: "relâchée une fois le paiement reçu",
                                   "15j": "15 jours net", "30j": "30 jours net",
                                   manuel: "modalités manuelles" };
  // Modèles de modalités MANUELLES réutilisables (demande du propriétaire,
  // 2026-10-22) : les textes favoris (ex. « Dépôt 50 %, balance à la livraison »)
  // sont conservés dans les réglages et proposés dans le champ libre de la fiche
  const [termPresets, setTermPresets] = useState([]);
  const garderModeleTerme = async (texte) => {
    const t = (texte || "").trim();
    if (!t) { toast.error("Inscrivez d'abord les modalités à garder en modèle"); return; }
    if (termPresets.some((p) => p.toLowerCase() === t.toLowerCase())) {
      toast.info("Ce modèle est déjà conservé.");
      return;
    }
    const liste = [...termPresets, t].slice(0, 10);
    try {
      await api.patch("/admin/settings", { payment_term_presets: liste });
      setTermPresets(liste);
      toast.success("Modèle conservé — il sera proposé dans le champ des modalités manuelles.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const changerTerme = async (r, terms, custom) => {
    setBusyId(r.id + ":t");
    try {
      await api.patch(`/admin/quote-requests/${r.id}/status`,
        { key: "payment_terms", terms, custom: custom ?? (r.payment_terms_custom || "") });
      setItems((xs) => xs.map((x) => (x.id === r.id
        ? { ...x, payment_terms: terms, payment_terms_custom: terms === "manuel" ? (custom ?? x.payment_terms_custom) : "" }
        : x)));
      toast.success(terms === "manuel"
        ? "Modalités manuelles enregistrées — imprimées telles quelles sur le PDF et le courriel."
        : terms
          ? `Terme de paiement enregistré : ${TERMES_PAIEMENT_LABELS[terms]} — imprimé sur le PDF et le courriel.`
          : "Choix de la fiche retiré — le terme convenu au dossier client s'appliquera.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  // CONFIRMATION AVANT TOUT COURRIEL (demande du propriétaire 2026-09-25) : aucun
  // courriel ne part sans un « OK » explicite — « Renvoyer la soumission », les
  // étapes qui écrivent au client (Accepté, Texte reçu, Livré), Demander plus
  // d'info, Refusé, Expédié, contre-offre. Évite les envois par mégarde.
  const EMAILING_STEPS = { accepte: "Accepté", montage_recu: "Texte reçu", livre: "Livré", paye: "Payé" };
  // Boîte MAISON (demande du propriétaire) : remplace le window.confirm natif, qui ne
  // peut pas contenir de case à cocher — la case « Ne pas envoyer de courriel au
  // client » s'offre dans la boîte quand allowSkip (étapes, refus, expédition)
  const [emailAsk, setEmailAsk] = useState(null);  // {email, quoi, allowSkip, resolve}
  const [emailAskSkip, setEmailAskSkip] = useState(false);
  const confirmEmail = (email, quoi, allowSkip = false) => new Promise((resolve) => {
    setEmailAskSkip(false);
    setEmailAsk({ email, quoi, allowSkip, resolve });
  });
  const closeEmailAsk = (proceed) => {
    emailAsk?.resolve({ proceed, skip: emailAskSkip && emailAsk?.allowSkip });
    setEmailAsk(null);
  };

  // APERÇU MODIFIABLE AVANT TOUT COURRIEL CLIENT (demande du propriétaire,
  // 2026-10-22 — « tout ce qui envoie un courriel au client doit faire
  // apparaître un popup avec le courriel à envoyer, modifiable s'il y a
  // lieu ») : « Envoyer/Renvoyer la soumission », « SVP confirmer votre
  // adresse » et « Demander plus d'info » montrent le courriel TEL QU'IL
  // PARTIRA — objet modifiable, note d'accompagnement facultative en tête,
  // question éditable avec aperçu rafraîchi. Le corps est régénéré côté
  // serveur (jamais de HTML libre).
  const COURRIEL_TITRES = { soumission: "La soumission — aperçu avant envoi",
                            adresse: "Confirmation des adresses — aperçu avant envoi",
                            question: "Demande de précision — aperçu avant envoi",
                            facture: "Renvoi de la facture — aperçu avant envoi",
                            comptable: "PDF groupé au comptable — aperçu avant envoi" };
  const COURRIEL_ENVOI = { soumission: "Envoyer la soumission au client",
                           adresse: "Envoyer la demande d'adresses",
                           question: "Envoyer la question au client",
                           facture: "Renvoyer la facture au client",
                           comptable: "Envoyer au comptable (PDF joint)" };
  const [courrielApercu, setCourrielApercu] = useState(null);  // {type, qid, name, email, sujet, html, note, message, etape, expedie, fiche, modele, sansCourriel, busy}
  // PIÈCES JOINTES du courriel « soumission » (demande du propriétaire,
  // 2026-10-27 : « où est l'option des P.J. ? ») — fichiers choisis dans le
  // popup d'aperçu, téléversés puis JOINTS au courriel client à l'envoi
  const [pjFiles, setPjFiles] = useState([]);
  // Chemins de fichiers DÉJÀ stockés choisis comme P.J. (images reçues du
  // client en 1 clic, ou la pièce favorite) — « Joindre leurs photos » +
  // « Gabarit catalogue » (demande du propriétaire, 2026-10-27)
  const [pjChemins, setPjChemins] = useState([]);  // [{path, nom}]
  // Chemins venus de la FILE « prochain courriel » de la fiche client
  // (2026-10-28) — affichés avec la mention « fiche client » ; décochés, ils
  // RESTENT en file (seules les pièces parties sont retirées)
  const [pjFilePaths, setPjFilePaths] = useState([]);
  // PIÈCE FAVORITE réglée dans Paramètres — proposée en 1 clic dans chaque popup
  const [pieceFavori, setPieceFavori] = useState(null);  // {path, nom} | null
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        if (data?.piece_favorite_path) {
          setPieceFavori({ path: data.piece_favorite_path, nom: data.piece_favorite_nom || "Document favori" });
        }
      } catch { /* pas de favori réglé — le bouton ne s'affiche simplement pas */ }
    })();
  }, []);
  const viderPieces = () => { setPjFiles([]); setPjChemins([]); setPjFilePaths([]); };
  const televerserPieces = async () => {
    // Fichiers du poste téléversés à l'envoi (HEIC converti en JPG lisible au
    // passage) + chemins déjà stockés choisis — 5 au total
    const chemins = pjChemins.map((c) => c.path);
    for (const f of pjFiles.slice(0, Math.max(0, 5 - chemins.length))) {
      const form = new FormData();
      form.append("file", f);
      const { data: up } = await api.post("/uploads/logo", form);
      chemins.push(up.path);
    }
    return chemins.slice(0, 5);
  };
  const basculerChemin = (path, nom) => {
    setPjChemins((cs) => (cs.some((c) => c.path === path)
      ? cs.filter((c) => c.path !== path)
      : [...cs, { path, nom }].slice(0, 5)));
  };
  // Section P.J. COMMUNE aux popups de courriel (demande du propriétaire,
  // 2026-10-27 : « P.J. partout ») : fichiers du poste, images reçues du
  // client en 1 clic (vignettes), et la pièce favorite des Paramètres
  const renderPiecesJointes = (fiche, prefix) => {
    const recues = fiche ? imagesJointes(fiche).slice(0, 8) : [];
    return (
      <div className="mb-3" data-testid={`${prefix}-pieces`}>
        <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">
          Pièces jointes (facultatif — images ou PDF, 5 max)
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 text-[11px] text-gold border border-gold/30 rounded-full px-3 py-1.5 cursor-pointer hover:border-gold/60 transition-colors">
            <Paperclip className="w-3 h-3" /> Ajouter des fichiers
            <input type="file" multiple className="hidden"
              accept=".png,.jpg,.jpeg,.webp,.gif,.svg,.pdf,.heic,.heif"
              data-testid={`${prefix}-pieces-input`}
              onChange={(e) => {
                setPjFiles([...pjFiles, ...Array.from(e.target.files || [])].slice(0, Math.max(0, 5 - pjChemins.length)));
                e.target.value = "";
              }} />
          </label>
          {pieceFavori && !pjChemins.some((c) => c.path === pieceFavori.path) && (
            <button type="button" onClick={() => basculerChemin(pieceFavori.path, pieceFavori.nom)}
              data-testid={`${prefix}-pieces-favori`}
              className="inline-flex items-center gap-1.5 text-[11px] text-champagne border border-champagne/40 rounded-full px-3 py-1.5 hover:border-champagne transition-colors">
              <FileDown className="w-3 h-3" /> Joindre « {pieceFavori.nom} » (favori)
            </button>
          )}
        </div>
        {recues.length > 0 && (
          <div className="mt-2">
            <span className="block text-[10px] text-ivory/50 mb-1">Images reçues du client — cliquez pour les joindre :</span>
            <div className="flex flex-wrap gap-1.5">
              {recues.map((p, j) => {
                const sel = pjChemins.some((c) => c.path === p);
                return (
                  <button key={j} type="button"
                    onClick={() => basculerChemin(p, `Image du client ${j + 1}`)}
                    data-testid={`${prefix}-piece-recue-${j}`}
                    title={sel ? "Sera jointe — cliquez pour retirer" : "Cliquez pour la joindre au courriel"}
                    className={`rounded-lg overflow-hidden border transition-all ${sel ? "border-gold ring-2 ring-gold/60" : "border-ivory/20 opacity-70 hover:opacity-100"}`}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt={`Image reçue ${j + 1}`}
                      className="w-11 h-11 object-cover" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {(pjFiles.length > 0 || pjChemins.length > 0) && (
          <ul className="mt-2 space-y-1">
            {pjChemins.map((c, i) => {
              // CHOIX AU CLIC (demande du propriétaire, 2026-10-28) : une pièce
              // PRÉ-JOINTE depuis la fiche client peut être décochée SANS la
              // perdre — seules les pièces réellement parties quittent la file ;
              // une décochée y reste pour la prochaine fois (mention affichée)
              const deLaFile = pjFilePaths.includes(c.path);
              return (
              <li key={`c${i}`} data-testid={`${prefix}-piece-chemin-${i}`}
                title={deLaFile ? "De la fiche client — décochée, elle restera en file pour la prochaine fois" : undefined}
                className={`flex items-center justify-between gap-2 text-xs text-ivory/75 bg-ink border rounded-lg px-2.5 py-1.5 ${deLaFile ? "border-emerald-400/40" : "border-gold/25"}`}>
                <span className="truncate">
                  {deLaFile && <span className="text-[9px] uppercase tracking-widest text-emerald-300/80 mr-1.5">fiche client</span>}
                  {c.nom}
                </span>
                <button type="button" aria-label="Retirer" onClick={() => basculerChemin(c.path, c.nom)}
                  data-testid={`${prefix}-piece-chemin-retirer-${i}`}
                  className="text-red-300/80 hover:text-red-200 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
              );
            })}
            {pjFiles.map((f, i) => (
              <li key={`f${i}`} data-testid={`${prefix}-piece-fichier-${i}`}
                className="flex items-center justify-between gap-2 text-xs text-ivory/75 bg-ink border border-ivory/10 rounded-lg px-2.5 py-1.5">
                <span className="truncate">{f.name}</span>
                <button type="button" aria-label="Retirer" onClick={() => setPjFiles(pjFiles.filter((_, j) => j !== i))}
                  data-testid={`${prefix}-piece-fichier-retirer-${i}`}
                  className="text-red-300/80 hover:text-red-200 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };
  const ouvrirApercuCourriel = async (r, type, message = "", extra = null) => {
    try {
      const params = new URLSearchParams({ type });
      if (message) params.set("message", message);  // question : le texte ; etape : la clé
      if (extra) Object.entries(extra).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== "") params.set(k, v); });
      // APERÇU AU COMPTABLE (règle permanente 2026-10-27 : modifiable avant
      // l'envoi) : son point d'accès est distinct (le PDF groupé est joint à
      // l'envoi seulement) ; le destinataire vient des Paramètres
      const apercuUrl = (qid, p) => p.get("type") === "comptable"
        ? `/admin/quote-requests/${qid}/factures-groupees/apercu?${p}`
        : `/admin/quote-requests/${qid}/courriel/apercu?${p}`;
      const { data } = await api.get(apercuUrl(r.id, params));
      // MODÈLE RÉUTILISABLE (demande du propriétaire, 2026-10-22) : un modèle
      // enregistré (objet et/ou note) pré-remplit le popup ; sinon, la note est
      // pré-remplie avec la DATE DE LIVRAISON ESTIMÉE du transporteur quand la
      // fiche a une étiquette Stallion (note_defaut). Dans les deux cas, si une
      // note s'applique, on redemande l'aperçu AVEC elle pour le rendu exact
      let html = data.html;
      const modele = data.modele || null;
      const noteInit = (modele && modele.note) || data.note_defaut || "";
      if (noteInit) {
        params.set("note", noteInit);
        html = (await api.get(apercuUrl(r.id, params))).data.html;
      }
      setCourrielApercu({ type, qid: r.id, name: data.nom || r.name, email: data.email || r.email,
                          sujet: (modele && modele.sujet) || data.sujet, html,
                          note: noteInit, message,
                          etape: type === "etape" ? message : null,
                          expedie: type === "expedie" ? extra : null,
                          fiche: r, modele,
                          sansCourriel: !!r.no_email, busy: false });
      setPjFiles([]);
      setPjChemins([]);
      // FILE « prochain courriel » de la fiche client (demande du propriétaire,
      // 2026-10-28) : les P.J. marquées « Joindre au prochain courriel » dans la
      // fiche client sont PRÉ-JOINTES dès l'ouverture du popup
      try {
        const { data: attente } = await api.get("/admin/pj-attente", { params: { email: r.email } });
        const enFile = (attente.files || []).map((f) => ({ path: f.path, nom: f.nom }));
        setPjFilePaths(enFile.map((f) => f.path));
        if (enFile.length) {
          setPjChemins(enFile.slice(0, 5));
          toast(`${enFile.length} pièce(s) jointe(s) de la fiche client pré-jointe(s) — elles partiront avec ce courriel. Décochée, une pièce RESTE en file pour la prochaine fois.`, { duration: 8000 });
        }
      } catch { /* pas de file — le popup s'ouvre normalement */ }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const rafraichirApercuCourriel = async () => {
    if (!courrielApercu) return;
    try {
      const params = new URLSearchParams({ type: courrielApercu.type, note: courrielApercu.note });
      if (courrielApercu.message) params.set("message", courrielApercu.message);
      if (courrielApercu.expedie) Object.entries(courrielApercu.expedie).forEach(([k, v]) => { if (v) params.set(k, v); });
      const url = courrielApercu.type === "comptable"
        ? `/admin/quote-requests/${courrielApercu.qid}/factures-groupees/apercu?${params}`
        : `/admin/quote-requests/${courrielApercu.qid}/courriel/apercu?${params}`;
      const { data } = await api.get(url);
      setCourrielApercu((p) => ({ ...p, html: data.html }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  // Modèles réutilisables des courriels d'étapes (demande du propriétaire,
  // 2026-10-22) : l'objet et la note du popup peuvent être GARDÉS comme modèle —
  // proposés automatiquement aux prochaines fiches
  const etapeDuModele = (a) => (a.type === "expedie" ? "expedie" : a.etape);
  const enregistrerModeleCourriel = async () => {
    const a = courrielApercu;
    if (!a || a.busy) return;
    try {
      await api.post("/admin/settings/courriel-modele",
        { etape: etapeDuModele(a), sujet: a.sujet, note: a.note });
      setCourrielApercu({ ...a, modele: { sujet: a.sujet, note: a.note } });
      toast.success("Modèle enregistré — il sera proposé automatiquement aux prochaines fiches.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const retirerModeleCourriel = async () => {
    const a = courrielApercu;
    if (!a || a.busy) return;
    try {
      await api.delete(`/admin/settings/courriel-modele/${etapeDuModele(a)}`);
      setCourrielApercu(null);
      toast.success("Modèle retiré — retour au courriel standard.");
      await ouvrirApercuCourriel(a.fiche, a.type, a.message || "", a.expedie);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const envoyerCourrielApercu = async () => {
    const a = courrielApercu;
    if (!a || a.busy) return;
    setCourrielApercu({ ...a, busy: true });
    try {
      const chemins = await televerserPieces();
      const payload = { sujet: a.sujet, note: a.note, pieces: chemins };
      if (a.type === "soumission") {
        await api.post(`/admin/quote-requests/${a.qid}/send-quote`, payload);
        setItems((xs) => xs.map((x) => (x.id === a.qid
          ? { ...x, quote_sent_at: new Date().toISOString(), status: { ...(x.status || {}), repondu: true } }
          : x)));
        toast.success(`Soumission envoyée à ${a.email} (copie à commande@trophee.ca) — « Répondu » coché automatiquement.`);
      } else if (a.type === "adresse") {
        await api.post(`/admin/quote-requests/${a.qid}/address-request`, payload);
        setItems((xs) => xs.map((x) => (x.id === a.qid ? { ...x, addr_request_last: new Date().toISOString() } : x)));
        toast.success(`Demande d'adresses envoyée à ${a.email} — tracée dans l'historique.`);
      } else if (a.type === "question") {
        await api.post(`/admin/quote-requests/${a.qid}/ask-info`, { ...payload, message: a.message });
        setItems((xs) => xs.map((x) => (x.id === a.qid ? { ...x, ask_info_last: new Date().toISOString() } : x)));
        toast.success(`Courriel envoyé à ${a.email} — la demande est tracée dans l'historique.`);
        setAskFor(null); setAskMsg("");
      } else if (a.type === "facture") {
        // RENVOI DU REÇU (demande du propriétaire, 2026-10-24) : facture PDF
        // régénérée et renvoyée au client, copie à l'atelier, historique tracé
        await api.post(`/admin/quote-requests/${a.qid}/renvoyer-facture`, payload);
        toast.success(`Facture renvoyée à ${a.email} (PDF joint, copie à l'atelier) — tracée dans l'historique.`);
      } else if (a.type === "comptable") {
        // PDF GROUPÉ AU COMPTABLE (règle permanente 2026-10-27 : TOUJOURS après
        // l'aperçu modifiable) — PDF régénéré et joint à l'envoi
        const { data: rep } = await api.post(`/admin/quote-requests/${a.qid}/factures-groupees/courriel`, payload);
        toast.success(`PDF groupé des ${rep.n} facture${rep.n > 1 ? "s" : ""} envoyé au comptable (${rep.dest}) — copie à info@trophee.ca.`);
      } else if (a.type === "etape") {
        // Pilule d'étape (Accepté / Texte reçu / Livré / Payé) : cochée SEULEMENT
        // après « Envoyer » du popup — le courriel part avec l'objet/note ajustés,
        // ou pas du tout si la case « sans courriel » est cochée
        const { data: repPaye } = await api.patch(`/admin/quote-requests/${a.qid}/status`,
          { key: a.etape, value: true, sujet: a.sujet, note: a.note, pieces: chemins,
            payment_method: a.paymentMethod || "inconnu",  // "interac" ou "comptant" pour « paye »
            ...(a.montantPartiel ? { montant_partiel: a.montantPartiel } : {}),
            ...(a.sansCourriel ? { skip_email: true } : {}) });
        patchLocal(a.qid, a.etape, true);
        // COMPTABILITÉ EN DIRECT (2026-10-31) : la réponse transporte le paiement
        // réel (dépôt/balance/statut) — la carte affiche les bons montants tout de
        // suite, sans attendre le rafraîchissement de 30 s
        if (a.etape === "paye" && repPaye?.payment) {
          setItems((xs) => xs.map((x) => (x.id === a.qid
            ? { ...x, payment: repPaye.payment,
                status: { ...(x.status || {}), paye: !!repPaye.status?.paye } }
            : x)));
        }
        toast.success(a.sansCourriel
          ? `« ${EMAILING_STEPS[a.etape]} » coché — AUCUN courriel envoyé (case « sans courriel » cochée).`
          : `« ${EMAILING_STEPS[a.etape]} » coché — courriel envoyé à ${a.email} (copie à commande@trophee.ca).`);
      } else if (a.type === "expedie") {
        // Expédition (aperçu modifiable, demande du propriétaire 2026-10-22) : les
        // paramètres viennent du popup d'expédition (mode, transporteur, nº de
        // suivi, coût) — « Expédié » coché SEULEMENT après « Envoyer » du popup
        const p = a.expedie || {};
        await api.post(`/admin/quote-requests/${a.qid}/ship`,
          { method: p.method, tracking: p.tracking, carrier: p.carrier,
            carrier_other: p.carrier_other, shipping_cost: p.shipping_cost,
            notify_client: !a.sansCourriel, sujet: a.sujet, note: a.note, pieces: chemins });
        patchLocal(a.qid, "expedie", true,
          { shipping: { method: p.method, carrier: p.carrier,
                        carrier_other: p.carrier_other, tracking: p.tracking,
                        tracking_url: trackingUrl(p.carrier, p.tracking),
                        cost: p.shipping_cost || 0,
                        at: new Date().toISOString() } });
        toast.success(a.sansCourriel
          ? "Marquée « Expédiée » — AUCUN courriel envoyé (case « sans courriel » cochée)."
          : `Marquée « Expédiée » — courriel envoyé à ${a.email} (copie à commande@trophee.ca).`);
        setShipFor(null); setShipBusy(false);
      }
      // Les P.J. de la file « prochain courriel » RÉELLEMENT parties sont
      // retirées de la file (sauf l'envoi au comptable, qui n'est pas au client)
      if (["soumission", "adresse", "question", "facture", "etape", "expedie"].includes(a.type) && chemins.length) {
        api.post("/admin/pj-attente/consommer", { email: a.email, paths: chemins }).catch(() => {});
      }
      viderPieces();
      setCourrielApercu(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setCourrielApercu({ ...a, busy: false });
    }
  };

  // « Envoyer la soumission au client » : prix enregistrés au besoin, puis APERÇU
  // MODIFIABLE du courriel (popup, demande du propriétaire 2026-10-22) — rien ne
  // part sans « Envoyer » dans le popup ; au départ : lien portail + copie à
  // commande@trophee.ca, « Répondu » coché tout seul
  const sendQuote = async (r) => {
    setBusyId(r.id + ":s");
    try {
      await flushDrafts(r);  // produits modifiés + prix saisis, enregistrés d'abord — l'aperçu montre les bons montants
      await ouvrirApercuCourriel(r, "soumission");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  // « SVP confirmer votre adresse » (demande du propriétaire, 2026-10-19) : courriel
  // au client avec le lien de sa page pour confirmer facturation + livraison —
  // tracé dans l'historique ; ouvre la section « Vos adresses » du portail.
  // APERÇU MODIFIABLE avant envoi (demande du propriétaire, 2026-10-22)
  const requestAddresses = async (r) => {
    setBusyId(r.id);
    try {
      await ouvrirApercuCourriel(r, "adresse");
    } finally { setBusyId(""); }
  };

  // « Demander plus d'info » (demande du propriétaire) : question libre de l'atelier
  // → APERÇU MODIFIABLE du courriel (popup, 2026-10-22) puis envoi au client avec
  // le rappel de ses coordonnées — tracé dans l'historique
  const askInfo = async () => {
    if (!askFor || !askMsg.trim()) return;
    setAskBusy(true);
    try {
      await ouvrirApercuCourriel(askFor, "question", askMsg.trim());
    } finally { setAskBusy(false); }
  };

  // « Test » (demande du propriétaire — FORMATION) : une vente marquée Test est
  // exclue des rapports de ventes et de taxes ; tracé dans l'historique
  const toggleTest = async (r) => {
    try {
      const res = await api.post(`/admin/quote-requests/${r.id}/test-mode`, { test_mode: !r.test_mode });
      setItems((xs) => xs.map((x) => (x.id === r.id ? { ...x, test_mode: res.data.test_mode } : x)));
      toast.success(res.data.test_mode
        ? "Marquée Formation — exclue des rapports"
        : "Marque Formation retirée — la vente compte de nouveau dans les rapports");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = () => api.get("/admin/quote-requests" + (sansCoutantRef.current ? "?sans_coutant=1" : ""))
    .then(({ data }) => {
      const liste = data.requests || [];
      // Nouvelles demandes entre deux rafraîchissements (demande du propriétaire,
      // 2026-10-08) : toast + badge « Nouveau » pulsant sur la carte — on les
      // repère « dès qu'elles arrivent » sans rien rater
      const avant = connus.current;
      const frais = avant ? liste.filter((r) => !avant.has(r.id)) : [];
      console.debug(`[rafraîchissement 30 s] ${liste.length} demandes — ${frais.length} nouvelle(s)`, frais.map((r) => r.id));
      if (frais.length) {
        setNouveaux((prev) => ({ ...prev, ...Object.fromEntries(frais.map((r) => [r.id, true])) }));
        frais.forEach((r) => toast.success(
          `Nouvelle demande de soumission — ${r.name}${r.city ? ` (${r.city})` : ""}`,
          { duration: 10000 }));
      }
      connus.current = new Set(liste.map((r) => r.id));
      setItems(liste);
      setMajLe(new Date());
      // Demande du propriétaire (2026-10-21) : le cycle des 30 s rafraîchit TOUTE
      // la page — y compris les pilules du haut et les compteurs des onglets
      // (non répondues, commandes, paniers) via le parent
      try { onCycle?.(); } catch { /* le repli des pilules ne bloque jamais la liste */ }
    })
    .catch(() => setItems([]));
  useEffect(() => {
    load();
    // Vendeurs (demande du propriétaire) : pour les cases « Visible par le vendeur X »
    // (l'appel est réservé à l'admin — ignoré en mode vendeur, qui est en lecture seule)
    if (!vendeur) api.get("/admin/vendeurs").then(({ data }) => setVendeurs(data.vendeurs || [])).catch(() => {});
    // Mode vendeur : la case « Voir les prix coûtants » donne aussi le filtre
    // « Lignes sans coûtant » (au moins une liste partagée active)
    if (vendeur) api.get("/vendeur/prix-coutant")
      .then(({ data }) => setCoutsAutorise((data.items || []).length > 0)).catch(() => {});
    // % par défaut de la contre-offre (réglé dans Paramètres par l'admin) —
    // pré-remplit le popup « Proposer un rabais »
    api.get("/admin/settings").then(({ data }) => {
      if (data?.counter_offer_pct) setDiscPctDef(String(data.counter_offer_pct));
      if (data?.markup_multiplier) setMarkup(String(data.markup_multiplier));
      // Modèles de modalités manuelles (demande du propriétaire, 2026-10-22) —
      // proposés dans le champ libre « manuel » de la fiche
      if (Array.isArray(data?.payment_term_presets)) setTermPresets(data.payment_term_presets);
    }).catch(() => {});
    // Rafraîchissement automatique au 30 s (demande du propriétaire) — silencieux,
    // en pause quand l'onglet du navigateur est en arrière-plan ; un cycle encore
    // en cours n'est JAMAIS doublé (garde autoBusy — revue de code iter50)
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible" || autoBusy.current) return;
      autoBusy.current = true;
      load().finally(() => { autoBusy.current = false; });
    }, 30000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Le badge « Nouveau » s'efface dès qu'on ouvre le dossier (œil / détails)
  useEffect(() => {
    if (openId && nouveaux[openId]) {
      setNouveaux((p) => { const n = { ...p }; delete n[openId]; return n; });
    }
  }, [openId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fiche créée DEPUIS l'onglet Clients (demande du propriétaire, 2026-10-22) :
  // l'onglet Clients dépose l'id dans sessionStorage et bascule ici — la fiche
  // s'ouvre dès qu'elle paraît dans la liste
  useEffect(() => {
    const id = sessionStorage.getItem("ouvrir_fiche");
    if (id && items && items.some((r) => r.id === id)) {
      sessionStorage.removeItem("ouvrir_fiche");
      setSearch("");
      setOpenId(id);
    }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vue LECTURE SEULE d'un dossier pour un vendeur (demande du propriétaire) :
  // il voit tout le détail des dossiers cochés pour lui, mais ne peut RIEN modifier
  const VendeurQuoteDetails = ({ r }) => (
    <div className="mt-4 pt-4 border-t border-ivory/10" data-testid={`quote-details-${r.id}`}>
      <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Client</p>
      <p className="text-sm text-ivory/80 mb-4">
        {r.name}{r.organization ? ` — ${r.organization}` : ""}
        <span className="block text-xs text-ivory/60 mt-0.5">
          {[r.email, r.phone, adresseComplete(r) || r.city].filter(Boolean).join(" · ")}
          {r.needed_by ? ` · livrer avant le ${r.needed_by}` : ""}
          {r.delivery_date ? ` · livraison prévue le ${r.delivery_date}` : ""}
        </span>
      </p>
      <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Produits</p>
      <ul className="space-y-1.5 mb-4">
        {(r.items || []).map((it, i) => (
          <li key={i} className="text-xs text-ivory/80 flex justify-between gap-3 border-b border-ivory/8 pb-1.5">
            <span>{i + 1}. {it.product_name}{it.product_no ? ` (${it.product_no})` : ""} × {it.qty}
              {" "}<span className="text-ivory/45">{it.catalogue}</span></span>
            <span className="text-gold font-semibold whitespace-nowrap">{lineTotal(it, (r.pricing || [])[i] || {}).toFixed(2)} $</span>
          </li>
        ))}
      </ul>
      <p className="text-sm text-ivory/80" data-testid={`quote-total-ttc-${r.id}`}>
        Après taxes : <strong className="text-gold">{totalTtc(r).toFixed(2)} $</strong>
        {r.tax_exempt
          ? <span className="text-emerald-400 text-xs font-semibold"> — EXEMPT DE TAXES</span>
          : <span className="text-ivory/45 text-xs"> (TPS {tpsOf(r).toFixed(2)} $ · TVQ {tvqOf(r).toFixed(2)} $)</span>}
      </p>
      <p className="text-[10px] text-ivory/40 mt-3 uppercase tracking-widest">
        Lecture seule — les modifications et envois se font par Mr Trophée
      </p>
    </div>
  );

  // Case « Visible par le vendeur X » sur une soumission (demande du propriétaire) :
  // le vendeur ne voit QUE les dossiers cochés pour lui dans son portail /vendeur
  const toggleVendeurVisible = async (r, vid) => {
    const cur = r.visible_vendeurs || [];
    const ids = cur.includes(vid) ? cur.filter((x) => x !== vid) : [...cur, vid];
    try {
      await api.patch(`/admin/quote-requests/${r.id}/vendeurs`, { vendeur_ids: ids });
      setItems((xs) => xs.map((x) => (x.id === r.id ? { ...x, visible_vendeurs: ids } : x)));
      toast.success(ids.includes(vid) ? "Dossier maintenant visible par ce vendeur"
                                      : "Dossier retiré de ce vendeur");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Case « Exempt de taxes » (demande du propriétaire) : TPS/TVQ à 0 $ — fiche,
  // lien de paiement Stripe, PDF, page imprimable et rapports suivent tous
  const toggleTaxExempt = async (r) => {
    const next = !r.tax_exempt;
    setBusyId(r.id + ":tx");
    try {
      const { data } = await api.post(`/admin/quote-requests/${r.id}/tax-exempt`, { exempt: next });
      setItems((xs) => xs.map((x) => (x.id === r.id
        ? { ...x, tax_exempt: next, payment: data.payment_link_reset ? {} : x.payment }
        : x)));
      toast.success(next
        ? "Exempt de taxes — TPS/TVQ à 0 $. Recréez le lien de paiement s'il existait (nouveau montant)."
        : "Taxes rétablies (TPS 5 % + TVQ 9,975 %). Recréez le lien de paiement s'il existait.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusyId(null);
  };

  const patchLocal = (id, key, value, extra = {}) => setItems((xs) =>
    xs.map((x) => (x.id === id
      ? { ...x, ...extra, status: { ...(x.status || {}), [key]: value } }
      : x)));

  const toggleStep = async (r, key) => {
    if (key === "livre" && !r.status?.livre && ["hand", "pickup"].includes(r.shipping?.method)) {
      // « Livré » en main propre / ramassage : popup de SIGNATURE (demande du
      // propriétaire, 2026-10-08) — le client signe à l'écran, facture finale ensuite
      setSignFor(r);
      return;
    }
    if (key === "expedie" && !r.status?.expedie) {
      // « Expédié » passe par le popup : mode d'envoi + transporteur + nº de suivi + courriels
      setShipFor(r); setShipMethod("carrier");
      setShipCarrier(r.shipping?.carrier || "postes_canada");
      setShipCarrierOther(r.shipping?.carrier_other || "");
      setShipCost(r.shipping?.cost ? String(r.shipping.cost) : "");
      setShipTracking(r.shipping?.tracking || "");
      setShipNoEmail(!!r.no_email);  // la case « sans courriel » persistante du dossier se reporte dans le popup
      return;
    }
    if (key === "refuse" && !r.status?.refuse) {
      // « Refusé » passe par le popup : motif + note OBLIGATOIRES + courriel poli au client
      setRefuseFor(r); setRefuseMotif(""); setRefuseNote("");
      setRefuseNoEmail(!!r.no_email);  // la case « sans courriel » persistante du dossier se reporte dans le popup
      return;
    }
    if (key === "retire" && !r.status?.retire) {
      // « Retirée par le client » (fusion demandée par le propriétaire, 2026-10-22 —
      // remplace le bouton « Marquer comme vente perdue » qui faisait la même chose) :
      // popup avec raison OBLIGATOIRE — rapport « Ventes perdues », relances arrêtées.
      // Le décochage reste direct (réversible).
      setVpFor(r); setVpReason("");
      return;
    }
    if (key === "montage_accepte" && (!r.status?.montage_accepte || r.proof?.file || r.proof?.drive_link)) {
      // « Montage accepté » = ENVOI DE L'ÉPREUVE au client (demande du propriétaire,
      // 2026-10-06) : popup avec fichier choisi, image COLLÉE (Ctrl+V) ou LIEN
      // GOOGLE DRIVE (2026-07-02). Épreuve déjà envoyée : le popup sert à la
      // renvoyer ou à marquer la réponse du client à la main (quand il répond
      // par courriel). Décochage possible seulement sur les anciens dossiers
      // sans épreuve (comportement normal).
      setProofFor(r); setProofFile(null); setProofDriveLink("");
      return;
    }
    const value = !r.status?.[key];
    // Case « Ne pas envoyer de courriel au client » du dossier (persistante,
    // enregistrée sur le dossier) : TOUTES les étapes la respectent — aucun
    // courriel ne part
    let silent = value && !!r.no_email;
    // Étapes qui ÉCRIVENT au client (Accepté, Texte reçu, Livré) : boîte de
    // confirmation AVEC case « Ne pas envoyer au client » (demande du propriétaire)
    if (key === "paye" && value && !silent) {
      // « Payé » coché (demande du propriétaire, 2026-10-30) : popup avec 3
      // options de paiement (Virement Interac, Comptant, Code QR) — l'aperçu
      // du courriel s'affiche APRÈS le choix
      setPaiementFor(r);
      setPaiementMethod("");  // réinitialiser le choix
      return;
    }
    if (value && !silent && EMAILING_STEPS[key]) {
      // APERÇU MODIFIABLE DES PILULES (demande du propriétaire, 2026-10-22 —
      // « tout ce qui envoie un courriel au client fait apparaître un popup avec
      // le courriel à envoyer, modifiable s'il y a lieu ») : le courriel de
      // l'étape s'affiche TEL QU'IL PARTIRA avant de cocher — objet et note
      // modifiables, case « sans courriel » conservée dans le popup
      ouvrirApercuCourriel(r, "etape", key);
      return;
    }
    try {
      const { data } = await api.patch(`/admin/quote-requests/${r.id}/status`,
        { key, value, ...(silent ? { skip_email: true } : {}) });
      patchLocal(r.id, key, value);
      if (key === "accepte" && value && silent) {
        toast.success("« Accepté » coché — AUCUN courriel envoyé (case « sans courriel » cochée).");
      } else if (key === "accepte" && value && data.emailed) {
        toast.success("« Accepté » coché — courriel envoyé au client pour les textes de gravure et le logo (copie à commande@trophee.ca).");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Popup « Signature de livraison » (soumissions main propre / ramassage) :
  // signé → facture finale avec preuve signée au client ; sauté → raison tracée ;
  // photo de livraison facultative (renforce la preuve)
  const confirmerSignature = async (file, nom, photo, sansCourriel) => {
    setSignBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("name", nom);
      if (photo) form.append("photo", photo);
      if (sansCourriel) form.append("no_email", "true");
      await api.post(`/admin/quote-requests/${signFor.id}/livraison-signature`, form);
      patchLocal(signFor.id, "livre", true,
        { delivery_signature: { name: nom, at: new Date().toISOString(), skipped: false } });
      toast.success(sansCourriel
        ? `Livré — signé par ${nom}. AUCUN courriel envoyé au client (case cochée).`
        : `Livré — signé par ${nom}${photo ? ", photo jointe" : ""}. Facture finale envoyée au client (copie à info@trophee.ca).`);
      setSignFor(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSignBusy(false);
    }
  };

  const sansSignature = async (raisonTxt, sansCourriel) => {
    setSignBusy(true);
    try {
      const form = new FormData();
      form.append("skip", "true");
      form.append("reason", raisonTxt);
      if (sansCourriel) form.append("no_email", "true");
      await api.post(`/admin/quote-requests/${signFor.id}/livraison-signature`, form);
      patchLocal(signFor.id, "livre", true,
        { delivery_signature: { skipped: true, reason: raisonTxt, at: new Date().toISOString() } });
      toast.success(sansCourriel
        ? "Marquée « Livré » SANS signature ni courriel — raison tracée dans l'historique."
        : "Marquée « Livré » SANS signature — raison tracée dans l'historique du dossier.");
      setSignFor(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSignBusy(false);
    }
  };

  // Popup « Refusé » : motif + note obligatoires, courriel poli au client
  // (+ copie à commande@trophee.ca) puis l'étape se coche
  const confirmRefuse = async () => {
    if (!refuseMotif) { toast.error("Choisissez le motif du refus."); return; }
    if (!refuseNote.trim()) { toast.error("Une petite note de motif est obligatoire."); return; }
    // Case « sans courriel » (demande du propriétaire) : le refus est tracé, rien ne part
    let noEmail = refuseNoEmail;
    if (!noEmail) {
      const cf = await confirmEmail(refuseFor.email, "Le refus poli (avec le motif choisi) partira au client.", true);
      if (!cf.proceed) return;
      if (cf.skip) noEmail = true;
    }
    setRefuseBusy(true);
    try {
      await api.post(`/admin/quote-requests/${refuseFor.id}/refuse`,
        { motif: refuseMotif, note: refuseNote.trim(), ...(noEmail ? { skip_email: true } : {}) });
      patchLocal(refuseFor.id, "refuse", true,
        { refusal: { by: "admin", motif: refuseMotif, note: refuseNote.trim(), at: new Date().toISOString() } });
      toast.success(noEmail
        ? "Marquée « Refusée » — AUCUN courriel envoyé (motif tracé dans le dossier)."
        : `Marquée « Refusée » — courriel poli avec le motif envoyé à ${refuseFor.email} (copie à commande@trophee.ca).`);
      setRefuseFor(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRefuseBusy(false);
    }
  };

  const fermerPaiement = () => {
    setPaiementFor(null);
    setPaiementMethod("");
    setPaiementComplet(true);
    setPaiementMontant("");
    setPaiementUnite("$");
  };
  // Dépôt MANUEL sans courriel ni facture (demande du propriétaire, 2026-10-31)
  const ouvrirDepot = (f) => {
    setDepotFor(f);
    setDepotMontant("");
    setDepotUnite("$");
    setDepotMethode("comptant");
  };
  const enregistrerDepot = async () => {
    if (!depotFor) return;
    const v = Number(String(depotMontant).replace(",", "."));
    if (!(v > 0)) { toast.error("Inscrivez le montant du dépôt."); return; }
    setDepotBusy(true);
    try {
      const { data } = await api.post(`/admin/quote-requests/${depotFor.id}/depot-manuel`, {
        montant: v, unit: depotUnite, method: depotMethode, sans_courriel: true,
      });
      setItems((xs) => xs.map((x) => (x.id === depotFor.id ? { ...x, payment: data.payment } : x)));
      toast.success(`Dépôt de ${data.deposit.toFixed(2)} $ enregistré — balance ${Number(data.payment?.balance_due || 0).toFixed(2)} $. Aucun courriel envoyé.`);
      setDepotFor(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDepotBusy(false);
    }
  };
  const confirmerPaiement = async () => {
    if (!paiementMethod) { toast.error("Choisissez le mode de paiement."); return; }
    // Dépôt PARTIEL en $ OU en % pour TOUTES les méthodes (Interac, Comptant,
    // Code QR, Stripe — demande du propriétaire, 2026-10-31) : on convertit le %
    // en montant $ sur la balance à encaisser.
    const balance = Number(paiementFor.payment?.balance_due || paiementFor.payment?.total
      || paiementFor.payment?.amount || paiementFor.payment?.total_due || paiementFor.total || 0);
    let partiel = null;
    if (!paiementComplet) {
      const v = Number(String(paiementMontant).replace(",", "."));
      if (!(v > 0)) { toast.error("Inscrivez le montant ou le % du paiement partiel."); return; }
      partiel = paiementUnite === "%" ? Math.round(balance * v / 100 * 100) / 100 : v;
      if (!(partiel > 0)) { toast.error("Le montant du paiement partiel doit être supérieur à 0 $."); return; }
    }
    if (paiementMethod === "qr") {
      // Code QR : si paiement PARTIEL, on crée d'abord le lien Stripe du dépôt
      // (admin/vendeur seuls — le client ne choisit jamais un acompte lui-même)
      const f = paiementFor;
      fermerPaiement();
      if (partiel) {
        try {
          const { data } = await api.post(`/admin/quote-requests/${f.id}/payment-link`,
            { mode: "partial", unit: "$", value: partiel });
          setItems((xs) => xs.map((x) => (x.id === f.id
            ? { ...x, payment: { ...(x.payment || {}), url: data.url, amount: data.amount, status: "pending" } }
            : x)));
          toast.success(`Lien de dépôt de ${partiel.toFixed(2)} $ créé — le client paie par code QR.`);
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail));
          return;
        }
      }
      await showQr(f);
      return;
    }
    if (paiementMethod === "stripe") {
      // STRIPE : on envoie le % TEL QUEL au backend (calcul exact) sinon le $
      const f = paiementFor;
      setPaiementBusy(true);
      try {
        const body = partiel
          ? { mode: "partial", unit: paiementUnite, value: Number(String(paiementMontant).replace(",", ".")) }
          : { mode: "full" };
        const { data } = await api.post(`/admin/quote-requests/${f.id}/payment-link`, body);
        setItems((xs) => xs.map((x) => (x.id === f.id
          ? { ...x, payment: { ...(x.payment || {}), url: data.url, amount: data.amount,
                               partial: data.partial, total_due: data.total_due,
                               status: "pending", balance_due: data.balance_after ?? (x.payment || {}).balance_due } }
          : x)));
        fermerPaiement();
        await openPaymentReminder({ ...f, payment: { ...(f.payment || {}), url: data.url } });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail));
      } finally {
        setPaiementBusy(false);
      }
      return;
    }
    // Interac ou Comptant : APERÇU DU COURRIEL (règle permanente) — on affiche
    // le courriel de confirmation AVANT l'envoi, puis on envoie
    try {
      const params = new URLSearchParams({ type: "etape", message: "paye" });
      if (partiel) params.set("montant_partiel", partiel);
      const { data } = await api.get(`/admin/quote-requests/${paiementFor.id}/courriel/apercu?${params}`);
      setCourrielApercu({
        type: "etape", qid: paiementFor.id, name: data.nom || paiementFor.name,
        email: data.email || paiementFor.email, sujet: data.sujet, html: data.html,
        note: "", message: "paye", etape: "paye", fiche: paiementFor,
        sansCourriel: !!paiementFor.no_email, busy: false,
        paymentMethod: paiementMethod,  // "interac" ou "comptant"
        montantPartiel: partiel,
      });
      fermerPaiement();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // ---------- Flux « Épreuve » client (demande du propriétaire, 2026-10-06) ----------
  // Envoi de l'épreuve au client : fichier choisi OU image COLLÉE dans le popup
  // (Ctrl+V). « Montage accepté » se coche et la pilule passe au BLEU « Épreuve
  // envoyée » ; le client répond sur sa page sécurisée (lien du courriel) —
  // or = acceptée, rouge = modifications demandées jusqu'à une nouvelle épreuve.
  const [proofApercu, setProofApercu] = useState(null); // {sujet, note, html} — aperçu du courriel AVANT envoi (règle permanente)
  const [proofApercuBusy, setProofApercuBusy] = useState(false);
  const proofNoteTimer = useRef(null);
  // Invitation au site « mode de fonctionnement » (demande du propriétaire, 2026-11-01) :
  // envoyer le fonctionnement (soumission en ligne → prix → suivi → facturation) à
  // une ou plusieurs adresses — TOUJOURS avec aperçu avant envoi
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviteSujet, setInviteSujet] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [inviteHtml, setInviteHtml] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const inviteNoteTimer = useRef(null);

  const ouvrirInvitation = async () => {
    setInviteOpen(true);
    setInviteBusy(true);
    try {
      const { data } = await api.get("/admin/invitation-mode/apercu");
      setInviteSujet(data.subject);
      setInviteNote(data.note_defaut || "");
      setInviteHtml(data.html);
      if (data.note_defaut) {
        // Pré-remplit l'aperçu avec la note par défaut
        const { data: d2 } = await api.get("/admin/invitation-mode/apercu", { params: { note: data.note_defaut } });
        setInviteHtml(d2.html);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setInviteBusy(false);
    }
  };
  const changerNoteInvitation = (note) => {
    setInviteNote(note);
    clearTimeout(inviteNoteTimer.current);
    inviteNoteTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/invitation-mode/apercu", { params: { note } });
        setInviteHtml(data.html);
      } catch { /* aperçu non rafraîchi : pas grave */ }
    }, 600);
  };
  const envoyerInvitation = async () => {
    if (!inviteEmails.trim()) { toast.error("Inscrivez au moins une adresse courriel."); return; }
    setInviteBusy(true);
    try {
      const { data } = await api.post("/admin/invitation-mode",
        { emails: inviteEmails, sujet: inviteSujet, note: inviteNote });
      toast.success(`${data.sent} invitation${data.sent > 1 ? "s" : ""} envoyée${data.sent > 1 ? "s" : ""} — copie à info@trophee.ca.`);
      if (data.invalid?.length) toast.warning(`Adresses invalides ignorées : ${data.invalid.join(", ")}`);
      if (data.skipped?.length) toast.info(`Non envoyé (désabonné ou erreur) : ${data.skipped.join(", ")}`);
      setInviteOpen(false);
      setInviteEmails("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setInviteBusy(false);
    }
  };

  // APERÇU DU COURRIEL D'ÉPREUVE AVANT ENVOI (règle permanente du propriétaire,
  // 2026-11-01) : « chaque courriel au client se relit dans un popup avant de
  // partir » — objet modifiable + note en tête, iframe du HTML EXACT
  const ouvrirApercuEpreuve = async () => {
    const lien = proofDriveLink.trim();
    if (!proofFile && !lien) { toast.error("Choisissez le fichier de l'épreuve, collez une image (Ctrl+V) — ou collez un lien Google Drive."); return; }
    if (lien && !/^https:\/\/(drive|docs)\.google\.com\//i.test(lien)) {
      toast.error("Le lien doit commencer par https://drive.google.com/… — dans Drive : Partager → « Tous les utilisateurs disposant du lien »."); return;
    }
    setProofApercuBusy(true);
    try {
      const { data } = await api.get(`/admin/quote-requests/${proofFor.id}/proof/apercu`);
      setProofApercu({ sujet: data.subject, note: "", html: data.html });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProofApercuBusy(false);
    }
  };
  const changerNoteEpreuve = (note) => {
    setProofApercu((c) => ({ ...c, note }));
    clearTimeout(proofNoteTimer.current);
    proofNoteTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/quote-requests/${proofFor.id}/proof/apercu`, { params: { note } });
        setProofApercu((c) => (c ? { ...c, html: data.html } : c));
      } catch { /* aperçu non rafraîchi : pas grave */ }
    }, 600);
  };

  const sendProof = async () => {
    const lien = proofDriveLink.trim();
    if (!proofFile && !lien) { toast.error("Choisissez le fichier de l'épreuve, collez une image (Ctrl+V) — ou collez un lien Google Drive."); return; }
    if (lien && !/^https:\/\/(drive|docs)\.google\.com\//i.test(lien)) {
      toast.error("Le lien doit commencer par https://drive.google.com/… — dans Drive : Partager → « Tous les utilisateurs disposant du lien »."); return;
    }
    setProofBusy(true);
    try {
      const form = new FormData();
      if (proofFile) form.append("file", proofFile);
      if (lien) form.append("drive_link", lien);
      // Objet + note choisis dans l'aperçu (règle permanente — 2026-11-01)
      if (proofApercu?.sujet) form.append("sujet", proofApercu.sujet);
      if (proofApercu?.note) form.append("note", proofApercu.note);
      const { data } = await api.post(
        `/admin/quote-requests/${proofFor.id}/proof${proofFor.no_email ? "?skip_email=true" : ""}`, form);
      setItems((xs) => xs.map((x) => (x.id === proofFor.id
        ? { ...x, proof: data.proof,
            status: { ...(x.status || {}), montage_accepte: true },
            status_at: { ...(x.status_at || {}), montage_accepte: new Date().toISOString() } }
        : x)));
      toast.success(data.emailed
        ? `Épreuve envoyée à ${proofFor.email} — pilule bleue en attendant sa réponse.`
        : "Épreuve enregistrée SANS courriel (case « sans courriel » cochée) — pilule bleue.");
      setProofFor(null); setProofFile(null); setProofDriveLink(""); setProofApercu(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProofBusy(false);
    }
  };

  // Réponse du client reçue par COURRIEL ou téléphone : ajustement MANUEL de la
  // pilule (demande du propriétaire — le client n'est pas obligé d'utiliser sa page)
  const markProof = async (status) => {
    setProofBusy(true);
    try {
      await api.post(`/admin/quote-requests/${proofFor.id}/proof-status`, { status });
      setItems((xs) => xs.map((x) => (x.id === proofFor.id
        ? { ...x, proof: { ...(x.proof || {}), status, responded_at: new Date().toISOString() } }
        : x)));
      setProofFor((p) => (p ? { ...p, proof: { ...(p.proof || {}), status } } : p));
      toast.success(`Pilule ajustée : ${PROOF_LABELS[status]}.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setProofBusy(false);
    }
  };

  // Coller une image directement dans le popup (demande du propriétaire) :
  // capture d'écran ou image copiée → devient le fichier de l'épreuve
  const onProofPaste = (e) => {
    const f = [...(e.clipboardData?.files || [])][0];
    if (f) { e.preventDefault(); setProofFile(f); toast.success(`Image collée : ${f.name || "capture"} — prête à envoyer.`); }
  };

  // « Marquer comme vente perdue » (demande du propriétaire, 2026-10-06) : dossier
  // perdu HORS LIGNE (téléphone, courriel, sans nouvelles) — raison obligatoire,
  // entre dans le rapport « Ventes perdues » et arrête toutes les relances
  const ventePerdue = async () => {
    const r = vpFor;
    if (vpReason.trim().length < 3) { toast.error("Inscrivez la raison de la perte"); return; }
    setVpBusy(true);
    try {
      await api.post(`/admin/quote-requests/${r.id}/vente-perdue`, { reason: vpReason.trim() });
      toast.success("Vente perdue marquée — le dossier est au rapport « Ventes perdues » et les relances sont arrêtées");
      setVpFor(null);
      setVpReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setVpBusy(false);
    }
  };

  // « Proposer un rabais » : contre-offre (produits + gravure seulement) après un
  // refus dont le motif est « le prix » — UNE seule fois par soumission ; pourcentage
  // AU CHOIX de l'admin (5 % par défaut, 1-90 %). « Refusé » se décoche et le client
  // reçoit le nouveau prix par courriel (lien portail)
  const discountOffer = async () => {
    const r = discFor;
    const pct = parseFloat(String(discPct).replace(",", "."));
    if (!Number.isFinite(pct) || pct <= 0 || pct > 90) {
      toast.error("Le rabais doit être entre 1 et 90 %.");
      return;
    }
    const cf = await confirmEmail(r.email, `La contre-offre à −${pct} % (nouveaux prix, lien portail) partira au client.`);
    if (!cf.proceed) return;
    setDiscBusy(r.id);
    try {
      await flushDrafts(r);  // produits modifiés + prix saisis, enregistrés d'abord
      const { data } = await api.post(`/admin/quote-requests/${r.id}/discount-offer`, { pct });
      setItems((xs) => xs.map((x) => (x.id === r.id
        ? { ...x, pricing: data.pricing,
            discount: { pct: data.pct, offered_at: new Date().toISOString() },
            payment: data.payment_reset ? {} : x.payment,
            status: { ...(x.status || {}), refuse: false } }
        : x)));
      toast.success(`Contre-offre −${data.pct} % envoyée à ${r.email} (copie à commande@trophee.ca)`
        + (data.payment_reset ? " — ancien lien de paiement retiré, recréez-le au besoin." : "."));
      setDiscFor(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDiscBusy("");
    }
  };

  // Suppression définitive d'une soumission (bouton poubelle en fin de ligne).
  // Vente PAYÉE (demande du propriétaire) : supprimable EN CAS D'ANNULATION OU
  // DE REMBOURSEMENT — confirmation forte, car elle sort des rapports de taxes
  const deleteQuote = async (r) => {
    const payee = r.payment?.status === "paid" || r.status?.paye;
    if (!window.confirm(payee
      ? `Vente PAYÉE — supprimer la soumission de ${r.name} ?\n\nÀ faire SEULEMENT en cas d'annulation ou de remboursement : la vente sera retirée des rapports de taxes.`
      : `Supprimer définitivement la soumission de ${r.name} ?`)) return;
    try {
      await api.delete(`/admin/quote-requests/${r.id}`, { params: payee ? { force: true } : {} });
      setItems((xs) => xs.filter((x) => x.id !== r.id));
      toast.success("Soumission supprimée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // « Lien de paiement » : ouvre le popup (plein paiement OU ACOMPTE en $ ou % —
  // demande du propriétaire), crée le lien Stripe et le copie — le webhook Stripe
  // comptabilise l'acompte et coche « Payé » quand le solde atteint 0
  const [payFor, setPayFor] = useState(null);       // popup « Lien de paiement »
  const [payMode, setPayMode] = useState("full");   // full | partial
  const [payValue, setPayValue] = useState("");     // montant de l'acompte
  const [payUnit, setPayUnit] = useState("$");      // $ ou %
  // Remboursement complet ou partiel (demande du propriétaire)
  const [refundFor, setRefundFor] = useState(null);
  const [refundMode, setRefundMode] = useState("full");
  const [refundValue, setRefundValue] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const refundQuote = async () => {
    if (!refundFor) return;
    const paidTotal = Number(refundFor.payment?.paid_total || refundFor.payment?.amount || 0);
    const remaining = Math.round((paidTotal - Number(refundFor.payment?.refunded_total || 0)) * 100) / 100;
    const body = refundMode === "full" ? {}
      : { amount: parseFloat(String(refundValue).replace(",", ".")) || 0 };
    if (refundMode === "partial" && (!body.amount || body.amount <= 0)) {
      toast.error("Inscrivez le montant à rembourser");
      return;
    }
    const montant = refundMode === "full" ? remaining : body.amount;
    if (!window.confirm(`Rembourser ${montant.toFixed(2)} $ à ${refundFor.name} via Stripe ?\n\n${refundMode === "full" ? "Remboursement COMPLET — « Payé » sera décoché." : "Remboursement PARTIEL — le reste demeure payé."}\n\nLe client est remboursé sur sa carte automatiquement.`)) return;
    setRefundBusy(true);
    try {
      const { data } = await api.post(`/admin/quote-requests/${refundFor.id}/refund`, body);
      setItems((xs) => xs.map((x) => (x.id === refundFor.id
        ? { ...x, payment: { ...(x.payment || {}), refunded_total: data.refunded_total,
                             paid_total: data.paid_total, balance_due: data.balance_due,
                             status: data.full ? "refunded" : "partial" },
            status: { ...(x.status || {}), paye: false } }
        : x)));
      toast.success(data.full
        ? `Remboursement COMPLET de ${data.refunded.toFixed(2)} $ envoyé — « Payé » décoché.`
        : `Remboursement partiel de ${data.refunded.toFixed(2)} $ envoyé via Stripe.`);
      setRefundFor(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRefundBusy(false);
    }
  };
  const paymentLink = async (r, partial) => {
    setPayBusy(r.id);
    try {
      await flushDrafts(r);  // produits modifiés + prix saisis, enregistrés d'abord
      const body = partial ? { mode: "partial", value: partial.value, unit: partial.unit } : { mode: "full" };
      const { data } = await api.post(`/admin/quote-requests/${r.id}/payment-link`, body);
      setItems((xs) => xs.map((x) => (x.id === r.id
        ? { ...x, payment: { ...(x.payment || {}), url: data.url, amount: data.amount,
                              subtotal: data.subtotal, tps: data.tps, tvq: data.tvq,
                              partial: data.partial, total_due: data.total_due,
                              solde_ajuste_at: "",   // nouveau lien créé → pastille effacée
                              status: x.payment?.status || "pending" } }
        : x)));
      const desc = data.partial
        ? `Acompte de ${data.amount.toFixed(2)} $ (solde après : ${data.balance_after.toFixed(2)} $)`
        : `${data.amount.toFixed(2)} $ taxes incluses`;
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success(`Lien de paiement créé (${desc}) et copié — collez-le au client.`);
      } catch {
        toast.success(`Lien de paiement créé (${desc}).`);
      }
      setPayFor(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPayBusy("");
    }
  };

  // Aperçu du courriel de rappel de paiement (3 jours) — bouton « Voir le rappel »,
  // demande du propriétaire : prévisualiser avant que ça parte. N'envoie rien.
  const [reminderPreview, setReminderPreview] = useState(null);  // {subject, html, name}
  // Rappel / pas à pas manuels (demande du propriétaire, 2026-10-07) : aperçu
  // MODIFIABLE (objet + note d'accompagnement) avant l'envoi
  const [courrielEtape, setCourrielEtape] = useState(null);  // {qid, genre, etape, sujet, html, email, nom, note}
  const [courrielBusy, setCourrielBusy] = useState(false);

  const ouvrirCourrielEtape = async (r, genre) => {
    viderPieces();
    try {
      const { data } = await api.get(`/admin/quote-requests/${r.id}/courriel-etape`, { params: { genre } });
      setCourrielEtape({ qid: r.id, genre, sujet: data.sujet, note: "", ...data });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // La note tapée est refletée dans l'aperçu EXACTEMENT comme à l'envoi
  // (régénérée côté serveur, avec un petit délai pour ne pas marteler l'API)
  const noteTimer = useRef(null);
  const changerNoteCourriel = (note) => {
    setCourrielEtape((c) => ({ ...c, note }));
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/quote-requests/${courrielEtape.qid}/courriel-etape`,
          { params: { genre: courrielEtape.genre, note } });
        setCourrielEtape((c) => (c ? { ...c, html: data.html } : c));
      } catch { /* aperçu non rafraîchi : pas grave */ }
    }, 600);
  };

  const envoyerCourrielEtape = async () => {
    setCourrielBusy(true);
    try {
      const chemins = await televerserPieces();
      await api.post(`/admin/quote-requests/${courrielEtape.qid}/courriel-etape`, {
        genre: courrielEtape.genre, sujet: courrielEtape.sujet, note: courrielEtape.note,
        pieces: chemins,
      });
      viderPieces();
      toast.success(`Courriel envoyé à ${courrielEtape.email} — tracé dans l'historique.`);
      setCourrielEtape(null);
      load();  // l'historique de la fiche se met à jour
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCourrielBusy(false);
    }
  };
  const previewReminder = async (r) => {
    try {
      const { data } = await api.get(`/admin/quote-requests/${r.id}/payment-reminder/preview`);
      setReminderPreview({ ...data, name: r.name });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Pilule « Rappel de paiement » de la fiche (demande du propriétaire,
  // 2026-10-22) : APERÇU MODIFIABLE AVANT ENVOI — objet éditable + note
  // d'accompagnement facultative, régénérée côté serveur (comme le rappel /
  // pas à pas). Rien ne part tant que « Envoyer » n'est pas cliqué.
  const [reminderDraft, setReminderDraft] = useState(null);  // {qid, email, name, sujet, note, html}
  const [reminderBusy, setReminderBusy] = useState(false);
  const rappelNoteTimer = useRef(null);
  const openPaymentReminder = async (r) => {
    viderPieces();
    try {
      const { data } = await api.get(`/admin/quote-requests/${r.id}/payment-reminder/preview`);
      setReminderDraft({ qid: r.id, kind: "paiement", email: r.email, name: r.name, sujet: data.subject, note: "", html: data.html });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  // Pilule « Dû / passé dû » (demande du propriétaire, 2026-10-27) : courriel
  // selon l'ENTENTE de paiement de la fiche (COD = à réception, 15 jours,
  // 30 jours) — le texte dit « dû » ou « passé dû de N j » selon l'échéance ;
  // APERÇU MODIFIABLE avant envoi, comme tout courriel au client
  const openRelanceTerme = async (r) => {
    viderPieces();
    try {
      const { data } = await api.get(`/admin/quote-requests/${r.id}/rappel-terme/apercu`);
      setReminderDraft({ qid: r.id, kind: "terme", email: r.email, name: r.name,
                         sujet: data.subject, note: "", html: data.html,
                         terme: data.terme, echeance: data.echeance, retard: data.retard });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const termeLabel = (r) => ({ reception: "COD", "15j": "15 j", "30j": "30 j" }[r.payment_terms] || "COD");
  // La note tapée est reflétée dans l'aperçu EXACTEMENT comme à l'envoi
  // (régénérée côté serveur, avec un petit délai pour ne pas marteler l'API)
  const changerNoteRappel = (note) => {
    setReminderDraft((d) => ({ ...d, note }));
    clearTimeout(rappelNoteTimer.current);
    rappelNoteTimer.current = setTimeout(async () => {
      try {
        const d = reminderDraftRef.current;
        if (!d) return;
        const url = d.kind === "terme"
          ? `/admin/quote-requests/${d.qid}/rappel-terme/apercu`
          : `/admin/quote-requests/${d.qid}/payment-reminder/preview`;
        const { data } = await api.get(url, { params: { note } });
        setReminderDraft((x) => (x ? { ...x, html: data.html } : x));
      } catch { /* l'aperçu se rafraîchira à la prochaine frappe */ }
    }, 500);
  };
  const reminderDraftRef = useRef(null);
  useEffect(() => { reminderDraftRef.current = reminderDraft; }, [reminderDraft]);
  // Relance du SOLDE après dépôt (demande du propriétaire, 2026-10-22 — MANUEL
  // seulement) : le lien de paiement a été libéré au dépôt, on en crée donc un
  // frais pour le SOLDE restant AVANT d'ouvrir l'aperçu modifiable du rappel
  const relancerSolde = async (r) => {
    try {
      if (!r.payment?.url) {
        await api.post(`/admin/quote-requests/${r.id}/payment-link`);
      }
      await openPaymentReminder(r);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  // Après un AJOUT sur une vente payée (demande du propriétaire, 2026-10-23) :
  // crée le lien du nouveau solde (plein ou dépôt convenu en %) PUIS ouvre
  // l'aperçu modifiable du courriel — l'envoi reste un clic « Envoyer » explicite
  const [soldeOffre, setSoldeOffre] = useState(null);  // {qid, name, email, solde, mode, pct, busy}
  const preparerCourrielSolde = async () => {
    const o = soldeOffre;
    if (!o || o.busy) return;
    setSoldeOffre({ ...o, busy: true });
    try {
      const pct = Math.max(1, Math.min(99, Number(o.pct) || 50));
      const body = o.mode === "depot" ? { mode: "partial", unit: "%", value: pct } : {};
      const { data } = await api.post(`/admin/quote-requests/${o.qid}/payment-link`, body);
      setItems((xs) => xs.map((x) => (x.id === o.qid
        ? { ...x, payment: { ...(x.payment || {}), url: data.url, amount: data.amount,
                             status: "pending", balance_due: o.solde,
                             solde_ajuste_at: "" } }  // nouveau lien créé → pastille effacée
        : x)));
      const fiche = items.find((x) => x.id === o.qid) || { id: o.qid, name: o.name, email: o.email };
      setSoldeOffre(null);
      await openPaymentReminder({ ...fiche, payment: { ...(fiche.payment || {}), url: data.url } });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSoldeOffre({ ...o, busy: false });
    }
  };
  const envoyerRappelPaiement = async () => {
    if (!reminderDraft) return;
    setReminderBusy(true);
    try {
      const url = reminderDraft.kind === "terme"
        ? `/admin/quote-requests/${reminderDraft.qid}/rappel-terme`
        : `/admin/quote-requests/${reminderDraft.qid}/payment-reminder`;
      const chemins = await televerserPieces();
      const { data } = await api.post(url,
        { sujet: reminderDraft.sujet, note: reminderDraft.note, pieces: chemins });
      viderPieces();
      if (reminderDraft.kind === "terme") {
        setItems((xs) => xs.map((x) => (x.id === reminderDraft.qid ? { ...x, terme_relance_at: data.at } : x)));
        toast.success(`Courriel « paiement dû / passé dû » envoyé à ${reminderDraft.email} — tracé dans l'historique.`);
      } else {
        setItems((xs) => xs.map((x) => (x.id === reminderDraft.qid ? { ...x, pay_reminder_sent_at: data.at, pay_reminder_sent: true } : x)));
        toast.success(`Rappel de paiement envoyé à ${reminderDraft.email} — tracé dans l'historique.`);
      }
      setReminderDraft(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setReminderBusy(false);
    }
  };

  // « Vérifier chez Stripe » (demande du propriétaire) : si le client a payé SANS
  // que le webhook nous avertisse, le bouton interroge Stripe et coche « Payé »
  // + « Commandé », avec la facture PDF au client — comme le webhook l'aurait fait.
  const [verifyBusy, setVerifyBusy] = useState("");
  // Manifeste du jour : téléchargé en AJAX (demande du propriétaire — avant, le
  // lien ouvrait une page JSON vide « menant nulle part » quand il n'y a aucun départ)
  const [manifestBusy, setManifestBusy] = useState(false);
  const openManifest = async () => {
    setManifestBusy(true);
    try {
      const resp = await api.get("/admin/stallion/manifest", { responseType: "blob" });
      window.open(URL.createObjectURL(resp.data), "_blank", "noopener");
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info("Aucune étiquette aujourd'hui — le manifeste se génère quand il y a des départs.");
      } else {
        toast.error("Manifeste indisponible pour le moment — réessayez.");
      }
    } finally {
      setManifestBusy(false);
    }
  };
  const verifyPayment = async (r) => {
    setVerifyBusy(r.id);
    try {
      const { data } = await api.post(`/admin/quote-requests/${r.id}/verify-payment`);
      if (data.status === "paid") {
        setItems((xs) => xs.map((x) => (x.id === r.id
          ? { ...x, payment: { ...(x.payment || {}), status: "paid", paid_at: x.payment?.paid_at || new Date().toISOString() },
              status_detail: undefined }
          : x)));
        toast.success(data.already
          ? "Ce dossier est déjà marqué payé."
          : "Paiement confirmé chez Stripe — « Payé » est coché, facture envoyée au client.");
        window.location.reload();  // étape « Payé » fraîche du serveur
      } else {
        toast.info(`Stripe confirme : AUCUN paiement reçu sur ce lien (statut : ${data.status}). Le client n'a pas encore payé.`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setVerifyBusy("");
    }
  };

  // QR code de paiement EN PERSONNE (demande du propriétaire) : le client scanne
  // le code avec son téléphone et paie sur place — le paiement reste lié au
  // dossier, contrairement au Tap to Pay de l'app Stripe. Réutilise le lien
  // « Encaisser » (jamais de session Stripe en double).
  const [qrFor, setQrFor] = useState(null);   // {id, img, amount, name}
  const [qrBusy, setQrBusy] = useState("");
  const showQr = async (r) => {
    setQrBusy(r.id);
    try {
      const resp = await api.get(`/admin/quote-requests/${r.id}/payment-qr.png`, { responseType: "blob" });
      setQrFor({ id: r.id, img: URL.createObjectURL(resp.data),
                 amount: r.payment?.amount, name: r.name });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setQrBusy("");
    }
  };

  const confirmShip = async () => {
    if (shipMethod === "carrier" && !shipTracking.trim()) {
      toast.error("Inscrivez le numéro de l'envoi (transporteur).");
      return;
    }
    if (shipMethod === "carrier" && shipCarrier === "autre" && !shipCarrierOther.trim()) {
      toast.error("Inscrivez le nom du transporteur (« Autre »).");
      return;
    }
    const costVal = shipCost.trim() === "" ? null : Math.max(0, Number(shipCost.replace(",", ".")) || 0);
    const shipParams = { method: shipMethod, tracking: shipTracking.trim(), carrier: shipCarrier,
                         carrier_other: shipCarrier === "autre" ? shipCarrierOther.trim() : "",
                         shipping_cost: costVal };
    // APERÇU MODIFIABLE DE L'EXPÉDITION (demande du propriétaire, 2026-10-22) :
    // avec courriel, le popup montre l'avis TEL QU'IL PARTIRA (nº de suivi inclus)
    // — objet et note modifiables, case « sans courriel ». Le popup d'expédition
    // reste OUVERT en dessous : « Annuler » y ramène sans rien perdre.
    if (!shipNoEmail && !shipFor?.no_email) {
      await ouvrirApercuCourriel(shipFor, "expedie", "", shipParams);
      return;
    }
    setShipBusy(true);
    try {
      await api.post(`/admin/quote-requests/${shipFor.id}/ship`,
        { ...shipParams, notify_client: false });
      patchLocal(shipFor.id, "expedie", true,
        { shipping: { method: shipMethod, carrier: shipCarrier,
                      carrier_other: shipCarrier === "autre" ? shipCarrierOther.trim() : "",
                      tracking: shipTracking.trim(),
                      tracking_url: trackingUrl(shipCarrier, shipTracking.trim()),
                      cost: costVal || 0,
                      at: new Date().toISOString() } });
      const modeLabel = shipMethod === "carrier" ? "avec nº de suivi"
        : shipMethod === "pickup" ? "prête à être ramassée" : "livraison en main propre";
      toast.success(`Marquée « Expédiée » — ${modeLabel}. AUCUN courriel envoyé au client (case « sans courriel » cochée).`);
      setShipFor(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setShipBusy(false);
    }
  };

  if (items === null) {
    return <p className="text-sm text-ivory/50" data-testid="quotes-loading">Chargement…</p>;
  }

  const active = items.filter((r) => !r.status?.archive
    // Retirées par le client (demande du propriétaire, 2026-10-06) : hors « En cours »
    // — elles restent visibles dans la vue « Archivées » avec leur pilule grise
    && !r.status?.retire
    // Vendeur (demande du propriétaire) : les ventes payées et livrées DISPARAISSENT
    // de sa liste — il ne garde que le travail en cours
    && !(vendeur && (r.payment?.status === "paid" || r.status?.paye || r.status?.livre)));
  const archived = vendeur ? [] : items.filter((r) => r.status?.archive);
  // Vue archivées : UNIQUEMENT les archivées (demande du propriétaire 2026-10-10 —
  // avant, « items » mélangeait les soumissions en cours dans la vue archivée)
  const shown = (showArchived ? archived : active).filter(matchSearch);
  const rechercheEnCours = search.trim() !== "";
  // Recherche dans les ARCHIVÉES en repli (demande du propriétaire, 2026-10-22) :
  // quand la liste courante ne donne RIEN, la recherche fouille les archivées et
  // les montre avec un avis — fini la fausse impression que la fiche n'existe pas
  const trouveesArchivees = (!showArchived && rechercheEnCours && shown.length === 0)
    ? archived.filter(matchSearch) : [];
  const listeRecherche = (rechercheEnCours && shown.length === 0) ? trouveesArchivees : shown;
  // Résumé de l'interface avec PRIX TOTAUX (demande du propriétaire) : valeur des
  // dossiers en cours (après taxes, calculé en direct) + montants déjà encaissés.
  // Les dossiers TEST ne sont comptabilisés NULLE PART ailleurs que dans la section
  // TEST des Rapports (demande du propriétaire, 2026-10-06) — exclus des deux totaux.
  // DÉPÔTS (signalement du propriétaire, 2026-10-22) : un acompte reçu
  // (payment.paid_total) s'ENLÈVE du « En cours » et s'AJOUTE à l'« Encaissé » —
  // le total, lui, ne change pas (valeur des dossiers actifs).
  const depotRecu = (r) => Math.min(Number(r.payment?.paid_total || 0), totalTtc(r));
  // BALANCE À PAYER sur le résumé de la vente (demande du propriétaire,
  // 2026-10-23) : le reste à encaisser paraît directement sous les montants
  // de la carte, sans ouvrir la fiche. Priorité au balance_due recalculé
  // (fiche à dépôt modifiée) ; sinon total après taxes moins l'acompte reçu.
  const balanceAPayer = (r) => {
    if (r.payment?.status === "paid" || r.status?.paye) return 0;
    const bd = Number(r.payment?.balance_due || 0);
    if (bd > 0) return bd;
    return Math.max(0, totalTtc(r) - depotRecu(r));
  };
  // BALANCE EN ROUGE VIF (demande du propriétaire, 2026-10-24) : la date
  // « Livrer avant le » est dépassée sans paiement complet → rouge au lieu de bleu.
  const livraisonPassee = (r) => !!(r.needed_by
    && String(r.needed_by).slice(0, 10) < new Date().toISOString().slice(0, 10));
  // 3 DERNIÈRES FACTURES PAYÉES du même client dans le résumé du haut (demande
  // du propriétaire, 2026-10-24) : numéros cliquables → popup de consultation
  // rapide. Correspondance sur le 1er courriel de la fiche (liste virgules ok).
  const premierCourriel = (e) => String(e || "").split(",")[0].trim().toLowerCase();
  const dernieresFacturesPayees = (r) => {
    const c = premierCourriel(r.email);
    if (!c || !items) return [];
    return items.filter((x) => x.id !== r.id
        && premierCourriel(x.email) === c
        && (x.payment?.status === "paid" || x.status?.paye))
      .sort((a, b) => String(b.payment?.paid_at || b.created_at || "")
        .localeCompare(String(a.payment?.paid_at || a.created_at || "")))
      .slice(0, 3);
  };
  // TOTAL ENCAISSÉ À VIE du client (demande du propriétaire, 2026-10-24) :
  // tout ce que ce client a déjà payé, toutes fiches confondues (factures
  // soldées + dépôts reçus) — affiché sous les « Factures payées » du résumé.
  const totalEncaisseClient = (r) => {
    const c = premierCourriel(r.email);
    if (!c || !items) return 0;
    return items.filter((x) => !x.test_mode && premierCourriel(x.email) === c)
      .reduce((s, x) => s + (Number(x.payment?.paid_total || 0)
        || ((x.payment?.status === "paid" || x.status?.paye) ? totalTtc(x) : 0)), 0);
  };
  // TOUTES LES FICHES du client dans le popup (demande du propriétaire,
  // 2026-10-24) : pas seulement les 3 dernières payées — chaque fiche (soldée,
  // dépôt, en cours) est cliquable pour passer de l'une à l'autre.
  const toutesFichesClient = (r) => {
    const c = premierCourriel(r.email);
    if (!c || !items) return [];
    return items.filter((x) => premierCourriel(x.email) === c)
      .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
  };
  // NOTE INTERNE au client depuis le popup (demande du propriétaire, 2026-10-24) :
  // enregistrée sur la fiche affichée — jamais envoyée ni imprimée côté client
  const saveNotePopup = async () => {
    const f = factureRapide;
    if (!f || notePopupBusy) return;
    setNotePopupBusy(true);
    try {
      const note = notePopup.trim();
      await api.patch(`/admin/quote-requests/${f.id}/items`, { details: { note } });
      setItems((xs) => xs.map((x) => (x.id === f.id ? { ...x, note } : x)));
      setFactureRapide({ ...f, note });
      toast.success("Note interne enregistrée — jamais envoyée ni imprimée côté client.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setNotePopupBusy(false); }
  };
  const totalEnCours = active.filter((r) => !r.test_mode && !(r.payment?.status === "paid" || r.status?.paye))
    .reduce((s, r) => s + Math.max(0, totalTtc(r) - depotRecu(r)), 0);
  const totalEncaisse = active.filter((r) => !r.test_mode)
    .reduce((s, r) => s + ((r.payment?.status === "paid" || r.status?.paye)
      // paid_total d'abord : après un lien de SOLDE (ajout après vente,
      // 2026-10-23), payment.amount n'est que la NOUVELLE session en attente
      ? (Number(r.payment?.paid_total || 0) || (r.payment?.amount) || totalTtc(r))
      : depotRecu(r)), 0);
  // Classement (demande client) : par nom de famille (nom complet ou courriel à
  // défaut) ou par courriel, croissant ou décroissant — actives ET archivées
  const nameKey = (r) => (r.last_name || r.name || r.email || "").toLowerCase();
  const mailKey = (r) => (r.email || "").toLowerCase();
  const sorted = [...listeRecherche].sort((a, b) => {
    if (sortMode === "name_az") return nameKey(a).localeCompare(nameKey(b), "fr");
    if (sortMode === "name_za") return nameKey(b).localeCompare(nameKey(a), "fr");
    if (sortMode === "email_az") return mailKey(a).localeCompare(mailKey(b), "fr");
    if (sortMode === "email_za") return mailKey(b).localeCompare(mailKey(a), "fr");
    return 0;  // « recent » : déjà triées par date d'arrivée (backend)
  });

  // Cases à cocher + actions EN LOT (demande du propriétaire) : chaque vente a
  // sa case ; la barre au-dessus de la liste offre « Imprimer en lot » (tous),
  // « Archiver en lot » et « Effacer en lot » (admin). Les ventes PAYÉES ne
  // s'effacent pas (rapports de taxes) : ignorées et signalées.
  const selList = sorted.filter((r) => selected[r.id]);
  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  // « Nouvelle fiche » (demande du propriétaire, 2026-10-22) : fiche vide créée
  // directement dans l'onglet — la recherche est vidée (sinon la fiche serait
  // filtrée hors de la liste) et la fiche s'ouvre aussitôt pour être remplie
  const nouvelleFiche = async () => {
    setNewBusy(true);
    try {
      const { data } = await api.post("/admin/quote-requests/nouvelle");
      setSearch("");
      await load();
      setOpenId(data.id);
      toast.success("Fiche créée — remplissez-la directement ici (nom, courriel, articles).");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setNewBusy(false); }
  };

  // « Dupliquer » (demande du propriétaire, 2026-10-22) : copie coordonnées +
  // articles + prix d'une fiche — pour les clients qui recommandent la même
  // chose. La copie est neuve (aucune étape, aucun paiement) et s'ouvre aussitôt
  const dupliquerFiche = async (r) => {
    setDupBusy(r.id);
    try {
      const { data } = await api.post(`/admin/quote-requests/${r.id}/dupliquer`);
      setSearch("");
      await load();
      setOpenId(data.id);
      toast.success(`Fiche de ${r.name || "ce client"} dupliquée (articles inclus) — ajustez-la au besoin.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDupBusy(""); }
  };
  const batchPrint = () => {
    if (!selList.length) return;
    openPrint(`Soumissions — ${selList.length} dossier${selList.length > 1 ? "s" : ""}`,
      selList.map(quoteBodyHtml));
  };
  const batchArchive = async () => {
    const list = selList.filter((r) => !r.status?.archive);
    if (!list.length) { toast.error("Rien à archiver — déjà archivées."); return; }
    if (!window.confirm(`Archiver ${list.length} soumission${list.length > 1 ? "s" : ""} ?`)) return;
    setBatchBusy("archive");
    let ok = 0;
    for (const r of list) {
      try {
        await api.patch(`/admin/quote-requests/${r.id}/status`, { key: "archive", value: true });
        patchLocal(r.id, "archive", true);
        ok += 1;
      } catch { /* on poursuit avec les autres */ }
    }
    setBatchBusy("");
    setSelected({});
    toast.success(`${ok} soumission${ok > 1 ? "s" : ""} archivée${ok > 1 ? "s" : ""}.`);
  };
  // Relances de paiement EN LOT (demande du propriétaire) : le courriel de rappel
  // part aux ventes cochées impayées — sans attendre le rappel automatique (3 j)
  const batchRemind = async () => {
    if (!selList.length) return;
    if (!window.confirm(`Envoyer le rappel de paiement par courriel aux ventes cochées impayées ?\n(Les payées, refusées, archivées ou sans lien de paiement seront ignorées.)`)) return;
    setBatchBusy("remind");
    try {
      const { data } = await api.post("/admin/quote-requests/payment-reminders/selected",
        { ids: selList.map((r) => r.id) });
      const skipIds = new Set((data.skipped || []).map((s) => s.id));
      setItems((xs) => xs.map((x) => (selList.some((r) => r.id === x.id) && !skipIds.has(x.id)
        ? { ...x, pay_reminder_sent_at: data.at } : x)));
      toast.success(`${data.sent} rappel${data.sent > 1 ? "s" : ""} envoyé${data.sent > 1 ? "s" : ""}.`
        + (data.skipped?.length ? ` ${data.skipped.length} ignorée${data.skipped.length > 1 ? "s" : ""} (${data.skipped[0].reason}${data.skipped.length > 1 ? ", …" : ""}).` : ""));
      setSelected({});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBatchBusy("");
    }
  };
  const batchDelete = async () => {
    // Ventes PAYÉES incluses (demande du propriétaire) : permis EN CAS
    // D'ANNULATION OU DE REMBOURSEMENT — confirmation forte, elles sortent
    // des rapports de taxes
    const payees = selList.filter((r) => r.payment?.status === "paid" || r.status?.paye);
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ${selList.length} soumission${selList.length > 1 ? "s" : ""} ?`
      + (payees.length ? `\n\nATTENTION : ${payees.length} vente${payees.length > 1 ? "s" : ""} PAYÉE${payees.length > 1 ? "s" : ""} — à faire seulement en cas d'annulation ou de remboursement ; ${payees.length > 1 ? "elles seront retirées" : "elle sera retirée"} des rapports de taxes.` : ""))) return;
    setBatchBusy("delete");
    const done = new Set();
    for (const r of selList) {
      try {
        const payee = r.payment?.status === "paid" || r.status?.paye;
        await api.delete(`/admin/quote-requests/${r.id}`, { params: payee ? { force: true } : {} });
        done.add(r.id);
      } catch { /* on poursuit avec les autres */ }
    }
    setItems((xs) => xs.filter((x) => !done.has(x.id)));
    setBatchBusy("");
    setSelected({});
    toast.success(`${done.size} soumission${done.size > 1 ? "s" : ""} supprimée${done.size > 1 ? "s" : ""}.`);
  };

  return (
    <div data-testid="quotes-tab">
      {/* Bandeau anti-erreur (demande du propriétaire après 2 courriels de test aux
          liens invalides) : les courriels partis de l'APERÇU portent des jetons de la
          base de test — liens portail/paiement/impression invalides pour les clients */}
      {!["mrtrophee.ca", "www.mrtrophee.ca"].includes(window.location.hostname) && (
        <div data-testid="preview-env-warning"
          className="bg-red-500/15 border border-red-500/40 text-red-300 text-xs rounded-xl px-4 py-3 mb-5">
          <strong>Environnement de test (aperçu).</strong> N'envoyez JAMAIS de courriel à un vrai
          client depuis ici (Envoyer la soumission, Demander plus d'info, relances…) : ses liens
          (portail, paiement, impression) seront INVALIDES. Pour les vrais clients : <strong>mrtrophee.ca/admin</strong>.
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h2 className="font-serif text-2xl flex items-center gap-2">
          <Zap className="w-5 h-5 text-gold" /> Ventes &amp; Soumissions
        </h2>
        <span className="text-xs text-ivory/55" data-testid="quotes-count">
          {active.length} en cours · {archived.length} archivée{archived.length > 1 ? "s" : ""}
        </span>
        {/* Manifeste du jour (demande du propriétaire) : toutes les étiquettes
            Stallion du jour fusionnées en un seul PDF — réservé à l'admin.
            Téléchargé en AJAX : sans étiquette, un message poli au lieu d'une page vide */}
        {!vendeur && (
        <button onClick={openManifest} disabled={manifestBusy}
          data-testid="quotes-manifest"
          className="inline-flex items-center gap-1.5 text-xs rounded-full border border-gold/40 text-gold px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
          {manifestBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />} Manifeste du jour
        </button>
        )}
        {/* Nouvelle fiche À LA MAIN (demande du propriétaire, 2026-10-22) : crée
            une fiche vide directement dans l'onglet, SANS passer par « Soumission
            en 30 s » — elle s'ouvre aussitôt pour être remplie sur place */}
        {!vendeur && (
        <button onClick={nouvelleFiche} disabled={newBusy}
          data-testid="quotes-nouvelle-fiche"
          title="Créer une fiche vide directement ici (sans passer par « Soumission en 30 s »)"
          className="inline-flex items-center gap-1.5 text-xs rounded-full bg-gold text-ink font-semibold px-3 py-1.5 hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
          {newBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Nouvelle fiche
        </button>
        )}
        {/* Inviter au site (demande du propriétaire, 2026-11-01) : envoie le mode de
            fonctionnement (soumission en ligne → prix → suivi → facturation) à une
            ou plusieurs adresses — avec APERÇU avant envoi, comme toujours */}
        {!vendeur && (
        <button onClick={ouvrirInvitation} disabled={inviteBusy}
          data-testid="quotes-inviter-site"
          title="Envoyer le mode de fonctionnement du site (soumission en ligne, suivi, facturation) à une ou plusieurs adresses courriel — avec aperçu avant envoi"
          className="inline-flex items-center gap-1.5 text-xs rounded-full border border-gold/40 text-gold px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
          {inviteBusy && inviteOpen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Inviter au site
        </button>
        )}
        {/* Recherche rapide (demande du propriétaire, 2026-10-22) : filtre les
            fiches PENDANT la frappe — nom, courriel, téléphone (tirets/espaces
            ignorés), ville, nº de facture, produit… Insensible aux accents */}
        <div className="relative" data-testid="quotes-search-box">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ivory/40 pointer-events-none" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            ref={searchRef}
            onKeyDown={(e) => {
              // Touche Échap (demande du propriétaire, 2026-10-22) : vide la
              // recherche d'un coup quand la boîte est active
              if (e.key === "Escape") { setSearch(""); e.target.blur(); }
            }}
            title="Recherche rapide — Ctrl+F pour y revenir, Échap pour vider"
            data-testid="quotes-search" placeholder="Rechercher : nom, courriel, téléphone…  (Ctrl+F)"
            className="bg-ink border border-ivory/15 rounded-lg pl-8 pr-7 py-1.5 text-xs text-ivory w-64 max-w-[62vw] placeholder:text-ivory/30 focus:border-gold focus:outline-none transition-colors duration-300" />
          {search && (
            <button type="button" onClick={() => setSearch("")} data-testid="quotes-search-clear"
              title="Effacer la recherche"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-ivory/40 hover:text-gold transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {search.trim() && (
          <span className="text-[11px] text-gold" data-testid="quotes-search-count">
            {listeRecherche.length} fiche{listeRecherche.length > 1 ? "s" : ""} trouvée{listeRecherche.length > 1 ? "s" : ""}
            {trouveesArchivees.length > 0 ? " (dans les archivées)" : ""}
          </span>
        )}
        <label className="ml-auto flex items-center gap-2 text-xs text-ivory/55">
          Classer :
          <select data-testid="quotes-sort" value={sortMode} onChange={(e) => setSortMode(e.target.value)}
            className="bg-ink border border-ivory/15 rounded-lg px-2.5 py-1.5 text-xs text-ivory focus:border-gold focus:outline-none transition-colors duration-300">
            <option value="recent">Récentes d'abord</option>
            <option value="name_az">Nom de famille A → Z</option>
            <option value="name_za">Nom de famille Z → A</option>
            <option value="email_az">Courriel A → Z</option>
            <option value="email_za">Courriel Z → A</option>
          </select>
        </label>
        {/* Filtre « Lignes sans coûtant » (demande du propriétaire, 2026-10-20) :
            ne montre que les dossiers avec au moins une ligne sans coûtant connu —
            admin, ou vendeur dont la case « Voir les prix coûtants » est cochée */}
        {(!vendeur || coutsAutorise) && (
          <button type="button" data-testid="quotes-filtre-sans-coutant"
            onClick={() => {
              const v = !sansCoutantRef.current;
              sansCoutantRef.current = v;
              setSansCoutant(v);
              load();
            }}
            title="Ne montre que les soumissions ayant au moins une ligne sans prix coûtant connu — les articles à renseigner"
            className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors duration-300 ${
              sansCoutant
                ? "bg-amber-400 text-ink border-amber-400 font-semibold"
                : "border-amber-400/40 text-amber-300/80 hover:border-amber-300 hover:text-amber-200"}`}>
            Lignes sans coûtant
          </button>
        )}
        {archived.length > 0 && (
          <button onClick={() => setShowArchived((v) => !v)} data-testid="quotes-toggle-archived"
            className="text-xs text-ivory/60 hover:text-gold underline underline-offset-2 transition-colors">
            {showArchived ? "Masquer les archivées" : `Voir les archivées (${archived.length})`}
          </button>
        )}
      </div>

      {/* Résumé de l'interface avec PRIX TOTAUX (demande du propriétaire) —
          trois cartes bien en vue en haut de l'onglet */}
      <div className="grid grid-cols-3 gap-2.5 mb-6" data-testid="quotes-summary">
        <div className="bg-surface border border-gold/30 rounded-xl px-3 py-3 sm:px-5 sm:py-4" data-testid="summary-en-cours">
          <p className="text-[10px] uppercase tracking-widest text-ivory/65">En cours</p>
          <p className="font-serif text-lg sm:text-2xl text-gold mt-0.5 truncate">{totalEnCours.toFixed(2)} $</p>
          <p className="text-[10px] text-ivory/55 mt-0.5">{active.filter((r) => !(r.payment?.status === "paid" || r.status?.paye)).length} à encaisser</p>
        </div>
        <div className="bg-surface border border-emerald-400/30 rounded-xl px-3 py-3 sm:px-5 sm:py-4" data-testid="summary-encaisse">
          <p className="text-[10px] uppercase tracking-widest text-ivory/65">Encaissé</p>
          <p className="font-serif text-lg sm:text-2xl text-emerald-300 mt-0.5 truncate">{totalEncaisse.toFixed(2)} $</p>
          <p className="text-[10px] text-ivory/55 mt-0.5">{active.filter((r) => r.payment?.status === "paid" || r.status?.paye).length} payée{active.filter((r) => r.payment?.status === "paid" || r.status?.paye).length > 1 ? "s" : ""}</p>
        </div>
        <div className="bg-surface border border-ivory/15 rounded-xl px-3 py-3 sm:px-5 sm:py-4" data-testid="summary-total">
          <p className="text-[10px] uppercase tracking-widest text-ivory/65">Total</p>
          <p className="font-serif text-lg sm:text-2xl text-champagne mt-0.5 truncate">{(totalEnCours + totalEncaisse).toFixed(2)} $</p>
          <p className="text-[10px] text-ivory/55 mt-0.5">{active.length} en cours</p>
        </div>
      </div>

      {/* Ligne de statut de l'actualisation automatique (demande du propriétaire,
          2026-10-08) : preuve visible que la page se rafraîchit seule toutes les 30 s */}
      <div className="flex items-center gap-2 -mt-3 mb-5" data-testid="quotes-auto-refresh">
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
        </span>
        <p className="text-[10px] text-ivory/45">
          Actualisation automatique toutes les 30 s{majLe
            ? ` · à jour à ${majLe.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "America/Montreal" })}`
            : "…"}
        </p>
      </div>

      {sorted.length === 0 && (
        rechercheEnCours ? (
          <p className="text-sm text-ivory/50 bg-surface border border-gold/15 rounded-xl p-7" data-testid="quotes-empty">
            Aucune fiche ne correspond à « {search.trim()} » — ni en cours, ni dans les archivées.
          </p>
        ) : (
          <p className="text-sm text-ivory/50 bg-surface border border-gold/15 rounded-xl p-7" data-testid="quotes-empty">
            Aucune soumission pour l'instant — le bouton doré « Soumission en 30 secondes » du site
            fait tout arriver ici et par courriel à commande@trophee.ca.
          </p>
        )
      )}

      {/* Repli ARCHIVÉES (demande du propriétaire, 2026-10-22) : la recherche n'a
          rien donné en cours, mais a trouvé dans les archivées — on le dit */}
      {trouveesArchivees.length > 0 && (
        <p className="text-xs text-amber-200 bg-amber-500/10 border border-amber-400/30 rounded-xl px-4 py-2.5 mb-4"
          data-testid="quotes-search-archived-note">
          Aucune fiche en cours — {trouveesArchivees.length} résultat{trouveesArchivees.length > 1 ? "s" : ""} trouvé{trouveesArchivees.length > 1 ? "s" : ""} dans les <strong>archivées</strong> :
        </p>
      )}

      {/* Barre des actions EN LOT (demande du propriétaire) : « Tout sélectionner »
          toujours visible ; les actions apparaissent quand au moins une case est cochée */}
      {sorted.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4" data-testid="quotes-batch-bar">
          <label className="flex items-center gap-2 text-xs text-ivory/60 cursor-pointer select-none">
            <input type="checkbox" data-testid="quotes-select-all"
              checked={sorted.length > 0 && sorted.every((r) => selected[r.id])}
              onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(sorted.map((r) => [r.id, true])) : {})}
              className="w-4 h-4 accent-[#d4af37] cursor-pointer" />
            Tout sélectionner
          </label>
          {selList.length > 0 && (
            <>
              <span className="text-xs text-gold font-semibold" data-testid="quotes-selected-count">
                {selList.length} sélectionnée{selList.length > 1 ? "s" : ""}
              </span>
              <button onClick={batchPrint} data-testid="quotes-batch-print"
                className="inline-flex items-center gap-1.5 text-xs rounded-full border border-ivory/25 text-ivory/80 px-3.5 py-1.5 hover:border-gold hover:text-gold transition-colors duration-300">
                <Printer className="w-3.5 h-3.5" /> Imprimer en lot
              </button>
              {!vendeur && (<>
                <button onClick={batchRemind} disabled={!!batchBusy} data-testid="quotes-batch-remind"
                  title="Rappel de paiement par courriel aux ventes cochées impayées — sans attendre le rappel automatique (3 jours)"
                  className="inline-flex items-center gap-1.5 text-xs rounded-full border border-sky-400/40 text-sky-300 px-3.5 py-1.5 hover:bg-sky-400 hover:text-ink transition-colors duration-300 disabled:opacity-60">
                  {batchBusy === "remind" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Relancer en lot
                </button>
                <button onClick={batchArchive} disabled={!!batchBusy} data-testid="quotes-batch-archive"
                  className="inline-flex items-center gap-1.5 text-xs rounded-full border border-gold/40 text-gold px-3.5 py-1.5 hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
                  {batchBusy === "archive" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />} Archiver en lot
                </button>
                <button onClick={batchDelete} disabled={!!batchBusy} data-testid="quotes-batch-delete"
                  className="inline-flex items-center gap-1.5 text-xs rounded-full border border-red-500/40 text-red-300 px-3.5 py-1.5 hover:bg-red-500 hover:text-ivory transition-colors duration-300 disabled:opacity-60">
                  {batchBusy === "delete" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Effacer en lot
                </button>
              </>)}
            </>
          )}
        </div>
      )}

      <div className="space-y-4">
        {sorted.map((r) => {
          const open = openId === r.id;
          const isArchived = !!r.status?.archive;
          return (
            <div key={r.id} data-testid={`quote-row-${r.id}`}
              className={`bg-surface border rounded-xl p-5 ${isArchived ? "border-ivory/10 opacity-60" : "border-gold/15"}`}>
              <div className="flex flex-wrap items-start gap-x-4 gap-y-1">
                <input type="checkbox" data-testid={`quote-select-${r.id}`}
                  checked={!!selected[r.id]} onChange={() => toggleSelect(r.id)}
                  title="Sélectionner pour une action en lot (imprimer, archiver, effacer)"
                  className="mt-1 w-4 h-4 shrink-0 accent-[#d4af37] cursor-pointer" />
                {/* Bloc texte : sur TÉLÉPHONE il prend TOUTE la largeur (basis) pour
                    que la date, la pilule « Relancé » et les icônes passent sous lui —
                    correctif visuel mobile (capture du propriétaire, 2026-10-06) */}
                {/* Bloc texte : largeur minimale garantie (240 px) — les icônes
                    restent EN HAUT À DROITE sur la ligne du nom (photo du
                    propriétaire, 2026-10-22) et ne passent sous le texte qu'en
                    très étroit (<640 px), sans jamais l'écraser */}
                <div className="min-w-[240px] flex-1">
                  <p className="font-semibold text-ivory" data-testid={`quote-name-${r.id}`}>
                    {r.name}
                    {/* « livrer avant le » EN LIGNE après le nom, comme sur la
                        photo de référence du propriétaire (2026-10-22) */}
                    {r.needed_by && (
                      <span className="ml-2 text-xs text-amber-300 font-normal whitespace-nowrap">
                        livrer avant le {r.needed_by}
                      </span>
                    )}
                    {/* AVIS DE RETARD (demande du propriétaire, 2026-10-22) : la
                        date estimée du transporteur dépasse le « livrer avant le » */}
                    {retardEstime(r) && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-red-500/20 text-red-300 border border-red-500/40 rounded-full px-2 py-0.5 align-middle"
                        data-testid={`quote-retard-${r.id}`}
                        title={`Livraison estimée par le transporteur : ${fmtDateCourte(retardEstime(r))} — APRÈS le « livrer avant le » du ${r.needed_by}`}>
                        Retard prévu · estimée {fmtDateCourte(retardEstime(r))}
                      </span>
                    )}
                    {nouveaux[r.id] && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-gold text-ink font-bold rounded-full px-2 py-0.5 align-middle animate-pulse"
                        data-testid={`quote-nouveau-${r.id}`} title="Arrivée depuis la dernière actualisation — le badge s'efface à l'ouverture du dossier">
                        Nouveau
                      </span>
                    )}
                    {r.test_mode && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-orange-500/20 text-orange-300 border border-orange-500/40 rounded-full px-2 py-0.5 align-middle"
                        data-testid={`quote-test-badge-${r.id}`} title="Dossier de formation — exclu des rapports">
                        Formation
                      </span>
                    )}
                    {/* Badge « Vente » (demande du propriétaire) : une fois la commande
                        payée, son type « soumission » devient « vente » (facture) */}
                    {(r.payment?.status === "paid" || r.status?.paye) && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full px-2 py-0.5 align-middle"
                        data-testid={`quote-sale-badge-${r.id}`} title="Vente payée — le document client devient une facture">
                        Vente
                      </span>
                    )}
                    {/* ACCUSÉS CLIENT (demande du propriétaire, 2026-10-27) :
                        « Lu » = courriel de soumission ouvert (pixel invisible) ;
                        « Portail ouvert » = le client a visité sa page de réponse */}
                    {r.courriel_ouvert_at && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-violet-500/15 text-violet-300 border border-violet-500/40 rounded-full px-2 py-0.5 align-middle"
                        data-testid={`quote-lu-${r.id}`}
                        title={`Courriel de soumission ouvert le ${String(r.courriel_ouvert_at).slice(0, 16).replace("T", " ")}${r.courriel_ouvert_count > 1 ? ` — ${r.courriel_ouvert_count} ouvertures` : ""}`}>
                        Lu
                      </span>
                    )}
                    {r.portail_ouvert_at && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest bg-teal-500/15 text-teal-300 border border-teal-500/40 rounded-full px-2 py-0.5 align-middle"
                        data-testid={`quote-portail-${r.id}`}
                        title={`Portail de réponse ouvert le ${String(r.portail_ouvert_at).slice(0, 16).replace("T", " ")}${r.portail_ouvert_count > 1 ? ` — ${r.portail_ouvert_count} visites` : ""}`}>
                        Portail ouvert
                      </span>
                    )}
                  </p>
                  {/* Rangée de PILULES sous le nom (correctif lisibilité, capture du
                      propriétaire 2026-10-22) : chaque pilule reste ENTIÈRE sur sa
                      ligne (plus de « SERA / RELÂCHÉE AU PAIEMENT / REÇU » fragmenté
                      dans le titre) — flex-wrap les garde lisibles à toute largeur */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                    {/* RANGÉE DE VIGNETTES de toutes les images jointes (demande
                        du propriétaire, 2026-10-27) : sur la carte, sans ouvrir
                        la fiche ; 1 clic = visionneuse avec le LIEN à copier ;
                        au-delà de 8, un compteur « +N » prend le relais */}
                    {imagesJointes(r).slice(0, 8).map((p, j) => (
                      <button key={j} type="button"
                        onClick={() => setZoomImg({ url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`, nom: `Image jointe ${j + 1} — ${r.name}` })}
                        data-testid={`quote-thumb-${r.id}-${j}`}
                        title={`Image jointe ${j + 1} — cliquer pour voir plus gros et copier le lien`}
                        className="block w-9 h-9 rounded-lg border border-gold/40 overflow-hidden hover:border-gold transition-colors duration-300 cursor-zoom-in shrink-0">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`}
                          alt={`Image jointe ${j + 1} — ${r.name}`} loading="lazy"
                          className="w-full h-full object-contain bg-ink" />
                      </button>
                    ))}
                    {imagesJointes(r).length > 8 && (
                      <span className="text-[10px] text-ivory/50 shrink-0" data-testid={`quote-thumb-plus-${r.id}`}
                        title={`${imagesJointes(r).length} images jointes au total — ouvrez la fiche pour toutes les voir`}>
                        +{imagesJointes(r).length - 8}
                      </span>
                    )}
                    {/* AVIS DE PAIEMENT sur chaque fiche (demande du propriétaire,
                        2026-10-21) : « la commande sera relâchée une fois le
                        paiement reçu » — visible d'un coup d'œil sans ouvrir */}
                    {r.payment?.status === "refunded" ? (
                      <span className="text-[9px] uppercase tracking-widest bg-red-500/15 text-red-300 border border-red-400/40 rounded-full px-2 py-0.5"
                        data-testid={`quote-payment-notice-${r.id}`} title="Remboursement complété — la commande ne doit pas être relâchée">
                        Remboursée — ne pas relâcher
                      </span>
                    ) : (r.payment?.status === "paid" || r.status?.paye) ? (
                      <span className="text-[9px] uppercase tracking-widest bg-emerald-500/10 text-emerald-200 border border-emerald-500/30 rounded-full px-2 py-0.5"
                        data-testid={`quote-payment-notice-${r.id}`} title="Paiement reçu — la commande peut être relâchée">
                        Payée — peut être relâchée
                      </span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-widest bg-amber-500/10 text-amber-200 border border-amber-400/40 rounded-full px-2 py-0.5"
                        data-testid={`quote-payment-notice-${r.id}`} title="Avis de paiement — politique de la maison">
                        Sera relâchée au paiement reçu
                      </span>
                    )}
                    {/* Nº de facture noté sur le dossier (demande du propriétaire) */}
                    {r.invoice_no && (
                      <span className="text-[9px] uppercase tracking-widest border border-gold/40 text-gold rounded-full px-2 py-0.5"
                        data-testid={`quote-invoice-no-${r.id}`} title="Numéro de facture (comptabilité)">
                        Facture {r.invoice_no}
                      </span>
                    )}
                    {/* Date de livraison prévue (modifiable dans la fiche) */}
                    {r.delivery_date && (
                      <span className="text-[9px] uppercase tracking-widest border border-sky-400/40 text-sky-300 rounded-full px-2 py-0.5"
                        data-testid={`quote-delivery-date-${r.id}`} title="Date de livraison prévue">
                        Livraison {r.delivery_date}
                      </span>
                    )}
                    {/* ICÔNE « note interne » (demande du propriétaire, 2026-10-27) :
                        visible dès la carte quand le client a une note — le texte
                        paraît au survol, jamais côté client */}
                    {r.note && (
                      <span className="text-gold" data-testid={`quote-note-indice-${r.id}`}
                        title={`Note interne : ${r.note}`}>
                        <StickyNote className="w-3.5 h-3.5" />
                      </span>
                    )}
                    {/* PASTILLE ROUGE « Paiement dû » (demande du propriétaire,
                        2026-10-27) : acceptée + lien de paiement créé + toujours
                        pas payée + AUCUN dépôt reçu (les fiches à dépôt restent
                        bleues — règle permanente). Le rappel amical part tout
                        seul au 48 h (réglable dans Paramètres → Relances) */}
                    {r.status?.accepte && r.payment?.url
                      && !(r.payment?.status === "paid" || r.status?.paye)
                      && !r.status?.refuse && !r.status?.archive
                      && !(Number(r.payment?.paid_total || 0) > 0) && (
                      <span className="text-[9px] uppercase tracking-widest border border-red-500 bg-red-500/15 text-red-400 font-bold rounded-full px-2 py-0.5"
                        data-testid={`quote-paiement-du-${r.id}`}
                        title="Paiement en attente — le rappel amical part automatiquement au 48 h">
                        Paiement dû
                      </span>
                    )}
                    {/* PASTILLE ROUGE « Passé dû de N j » (demande du propriétaire,
                        2026-10-27) : retard selon l'ENTENTE (COD, 15 j, 30 j),
                        calculé côté serveur — visible SANS ouvrir la fiche ;
                        la pilule « Dû / passé dû » (fiche dépliée) envoie la relance */}
                    {(r.terme_retard || 0) > 0 && (
                      <span className="text-[9px] uppercase tracking-widest border border-red-500 bg-red-500/20 text-red-300 font-bold rounded-full px-2 py-0.5"
                        data-testid={`quote-passe-du-${r.id}`}
                        title={`Paiement passé dû de ${r.terme_retard} jour${r.terme_retard > 1 ? "s" : ""} selon l'entente (${r.terme_jours ? `${r.terme_jours} jours net` : "COD — à réception"}) — ouvrez la fiche : pilule « Dû / passé dû » pour la relance par courriel`}>
                        Passé dû de {r.terme_retard} j
                      </span>
                    )}
                    {/* PASTILLE DORÉE « GROS CLIENT » (demande du propriétaire,
                        2026-10-24) : au-delà du seuil réglable en Paramètres
                        (10 000 $ par défaut) encaissés à vie, toutes fiches
                        confondues — visible dès la carte, sans rien ouvrir */}
                    {totalEncaisseClient(r) >= seuilOr && (
                      <span className="text-[9px] uppercase tracking-widest border border-gold bg-gold text-ink font-bold rounded-full px-2 py-0.5"
                        data-testid={`quote-gros-client-${r.id}`}
                        title={`Client au-delà de ${seuilOr.toFixed(0)} $ encaissés à vie : ${totalEncaisseClient(r).toFixed(2)} $`}>
                        Client or
                      </span>
                    )}
                    {/* Catégories de vente des choix (statistiques à long terme —
                        choisies dans la fiche, admin ; jamais visibles par les clients) */}
                    {[...new Set((r.items || []).map((it) => it.sales_category).filter(Boolean))].length > 0 && (
                      <span className="text-[9px] uppercase tracking-widest border border-violet-400/40 text-violet-300 rounded-full px-2 py-0.5"
                        data-testid={`quote-category-${r.id}`} title="Catégories de vente des choix (statistiques — Rapports)">
                        {[...new Set((r.items || []).map((it) => it.sales_category).filter(Boolean))]
                          .map(categoryLabel).join(" · ")}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-ivory/55">
                    {[r.organization, r.city].filter(Boolean).join(" · ")}
                  </p>
                  {/* Adresse complète du client (demande du propriétaire, 2026-10-07) —
                      visible SANS ouvrir la fiche */}
                  {r.address1 && (
                    <p className="text-xs text-ivory/55 mt-0.5" data-testid={`quote-address-${r.id}`}>
                      {adresseComplete(r)}
                    </p>
                  )}
                  <p className="text-xs text-ivory/60 mt-0.5">
                    <a href={`tel:+${(r.phone || "").replace(/[^0-9]/g, "")}`}
                      className="text-amber-300 hover:text-gold font-semibold transition-colors">{r.phone}</a>
                    {" · "}
                    <a href={`mailto:${r.email}`} className="text-amber-300 hover:text-gold transition-colors">{r.email}</a>
                  </p>
                  {/* Résumé visible SANS ouvrir la fiche — demande du propriétaire :
                      nombre d'articles + total avant et après taxes, calculé en direct */}
                  <p className="text-xs text-ivory/60 mt-1" data-testid={`quote-resume-${r.id}`}>
                    {itemsOf(r).length} article{itemsOf(r).length > 1 ? "s" : ""}
                    {" · "}<strong className="text-gold">{grandNet(r).toFixed(2)} $</strong> avant taxes
                    {" · "}<strong className="text-gold">{totalTtc(r).toFixed(2)} $</strong> après taxes
                    {/* BALANCE À PAYER en clair sur le résumé (demande du
                        propriétaire, 2026-10-23/24) : le DÉPÔT reçu paraît avec
                        la balance (« dépôt de X $ — balance de Y $ ») ; bleu ciel
                        tant qu'il reste à encaisser, ROUGE VIF si le « Livrer
                        avant le » est dépassé sans paiement complet, vert
                        « soldée » sinon. Pastille « solde ajusté » quand la
                        balance d'une fiche à dépôt vient d'être recalculée. */}
                    {(() => {
                      const bal = balanceAPayer(r);
                      const dep = depotRecu(r);
                      if (bal <= 0) {
                        return (<>
                          {" · "}<strong className="text-emerald-300"
                            data-testid={`quote-balance-${r.id}`}>soldée</strong>
                        </>);
                      }
                      const enRetard = livraisonPassee(r);
                      // PASTILLE BLEUE POUR LES DÉPÔTS (demande du propriétaire,
                      // 2026-10-27) : une fiche AVEC dépôt reste TOUJOURS bleue,
                      // même en retard — le rouge vif ne s'applique qu'aux fiches
                      // SANS dépôt dont le « Livrer avant le » est dépassé
                      const rouge = enRetard && dep <= 0;
                      return (<>
                        {" · "}
                        <strong className={rouge ? "text-red-400" : "text-sky-300"}
                          data-testid={`quote-balance-${r.id}`}
                          title={enRetard
                            ? `Livrer avant le ${String(r.needed_by).slice(0, 10)} — date dépassée sans paiement complet`
                            : ""}>
                          {dep > 0
                            ? <>dépôt de {dep.toFixed(2)} $ — balance de {bal.toFixed(2)} $</>
                            : <>balance à payer {bal.toFixed(2)} $</>}
                        </strong>
                        {r.payment?.solde_ajuste_at && (
                          <span className="ml-1.5 text-[9px] uppercase tracking-wider rounded-full px-2 py-0.5 border border-amber-400/40 text-amber-300 align-middle"
                            data-testid={`quote-solde-ajuste-${r.id}`}
                            title={`Balance modifiée le ${String(r.payment.solde_ajuste_at).slice(0, 10)} — l'ancien lien de paiement est invalidé : renvoyez le lien du nouveau solde`}>
                            solde ajusté
                          </span>
                        )}
                      </>);
                    })()}
                  </p>
                  {/* 3 DERNIÈRES FACTURES PAYÉES du même client (demande du
                      propriétaire, 2026-10-24) : numéros cliquables → popup de
                      consultation rapide, sans quitter la liste — suivi du
                      TOTAL ENCAISSÉ À VIE de ce client */}
                  {(() => {
                    const facs = dernieresFacturesPayees(r);
                    const totalVie = totalEncaisseClient(r);
                    if (!facs.length && totalVie <= 0) return null;
                    return (
                      <p className="text-[11px] text-ivory/50 mt-0.5"
                        data-testid={`quote-factures-payees-${r.id}`}>
                        {facs.length > 0 && (
                          <>
                            Factures payées :{" "}
                            {facs.map((f, i) => (
                              <span key={f.id}>
                                {i > 0 && " · "}
                                <button type="button" onClick={() => ouvrirFacture(f)}
                                  data-testid={`quote-facture-rapide-${r.id}-${i}`}
                                  title={`Consultation rapide — payée le ${fmtDate(f.payment?.paid_at || f.created_at)}`}
                                  className="text-emerald-300 hover:text-gold underline decoration-dotted underline-offset-2 transition-colors">
                                  {f.invoice_no || String(f.id).slice(0, 8)}
                                </button>
                              </span>
                            ))}
                          </>
                        )}
                        {totalVie > 0 && (
                          <span data-testid={`quote-total-client-${r.id}`}>
                            {facs.length > 0 ? " — " : ""}total encaissé à vie :{" "}
                            <strong className="text-emerald-300">{totalVie.toFixed(2)} $</strong>
                          </span>
                        )}
                      </p>
                    );
                  })()}
                  {/* Date + « Relancé » en VERSION COMPACTE sous les montants sur les
                      écrans < 1024 px (en grand écran ils sont en haut à droite, à
                      côté des icônes, comme sur la photo du propriétaire) */}
                  <p className="lg:hidden mt-0.5 text-[11px] text-ivory/40">
                    reçue le {fmtDate(r.created_at)}
                    {r.pay_reminder_sent_at && (
                      <span data-testid={`quote-reminded-compact-${r.id}`}
                        title="Rappel de paiement automatique envoyé au client (soumission acceptée, non payée après 3 jours)"
                        className="ml-2 text-[9px] uppercase tracking-wider rounded-full px-2 py-0.5 border border-amber-400/40 text-amber-300 align-middle">
                        Relancé {r.pay_reminder_sent_at.slice(0, 10)}
                      </span>
                    )}
                  </p>
                </div>
                {/* Grand écran (≥1024 px) : date et « Relancé » en haut à droite,
                    juste avant les icônes — comme sur la photo du propriétaire */}
                <span className="hidden lg:inline text-xs text-ivory/45 shrink-0">{fmtDate(r.created_at)}</span>
                {r.pay_reminder_sent_at && (
                  <span data-testid={`quote-reminded-${r.id}`}
                    title="Rappel de paiement automatique envoyé au client (soumission acceptée, non payée après 3 jours)"
                    className="hidden lg:inline-flex text-[10px] uppercase tracking-wider rounded-full px-2.5 py-0.5 border border-amber-400/40 text-amber-300 shrink-0">
                    Relancé {r.pay_reminder_sent_at.slice(0, 10)}
                  </span>
                )}
                {/* Rangée d'icônes TOUJOURS en haut à droite sur la ligne du nom
                    (photo du propriétaire, 2026-10-22) ; elle ne passe sous le
                    texte, alignée à droite, qu'en très étroit (<640 px) */}
                <div className="flex items-center gap-1.5 shrink-0 ml-auto max-sm:mt-1.5 max-sm:w-full max-sm:justify-end max-sm:flex-wrap">
                  {/* Pilule « Encaisser le solde » (demande du propriétaire,
                      2026-10-31) : fiche avec balance à payer → crée le lien du
                      SOLDE puis ouvre l'aperçu modifiable du courriel de relance,
                      AVANT tout envoi au client */}
                  {!vendeur && balanceAPayer(r) > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); relancerSolde(r); }}
                      data-testid={`quote-encaisser-solde-${r.id}`}
                      title={`Encaisser la balance de ${balanceAPayer(r).toFixed(2)} $ — crée le lien du solde puis affiche le courriel avant envoi`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-sky-400/50 bg-sky-500/10 text-sky-300 text-[11px] font-semibold hover:border-sky-300 hover:text-sky-200 hover:bg-sky-500/20 transition-colors duration-300">
                      <CreditCard className="w-3.5 h-3.5" />
                      Encaisser le solde
                    </button>
                  )}
                  {/* Dépôt MANUEL (demande du propriétaire, 2026-10-31) : enregistrer
                      un acompte reçu hors ligne SANS envoyer de courriel ni de facture */}
                  {!vendeur && (
                    <button onClick={(e) => { e.stopPropagation(); ouvrirDepot(r); }}
                      data-testid={`quote-depot-manuel-${r.id}`}
                      title="Enregistrer un dépôt manuel reçu — SANS courriel ni facture au client"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-400/50 bg-emerald-500/10 text-emerald-300 text-[11px] font-semibold hover:border-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/20 transition-colors duration-300">
                      <HandCoins className="w-3.5 h-3.5" />
                      Dépôt manuel
                    </button>
                  )}
                  <button onClick={() => setOpenId(open ? null : r.id)} data-testid={`quote-view-${r.id}`}
                    title={open ? "Masquer les détails" : "Voir la soumission"}
                    className="p-2 rounded-lg border border-ivory/15 text-ivory/60 hover:border-gold/50 hover:text-gold transition-colors duration-300">
                    {open ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={() => printQuote(r)} data-testid={`quote-print-${r.id}`}
                    title="Imprimer la soumission"
                    className="p-2 rounded-lg border border-ivory/15 text-ivory/60 hover:border-gold/50 hover:text-gold transition-colors duration-300">
                    <Printer className="w-4 h-4" />
                  </button>
                  {/* Actions d'écriture — réservées à l'admin (vendeur : lecture seule) */}
                  {!vendeur && (<>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/quote-requests/${r.id}/pdf`}
                    data-testid={`quote-pdf-${r.id}`} title="Télécharger la soumission en PDF (dossiers)"
                    className="p-2 rounded-lg border border-ivory/15 text-ivory/60 hover:border-gold/50 hover:text-gold transition-colors duration-300">
                    <FileDown className="w-4 h-4" />
                  </a>
                  {/* Bouton Formation (demande du propriétaire) : un dossier
                      marqué Formation est exclu des rapports de ventes et de taxes */}
                  <button onClick={() => toggleTest(r)} data-testid={`quote-test-${r.id}`}
                    title={r.test_mode
                      ? "Retirer la marque Formation — la vente comptera de nouveau dans les rapports"
                      : "Marquer Formation — la vente sera exclue des rapports"}
                    className={`p-2 rounded-lg border transition-colors duration-300 ${
                      r.test_mode
                        ? "border-orange-500/60 bg-orange-500/15 text-orange-300"
                        : "border-ivory/15 text-ivory/60 hover:border-orange-400/60 hover:text-orange-300"}`}>
                    <FlaskConical className="w-4 h-4" />
                  </button>
                  {/* Dupliquer la fiche (demande du propriétaire, 2026-10-22) :
                      coordonnées + articles + prix copiés — pour les clients qui
                      recommandent la même chose. La copie s'ouvre aussitôt */}
                  <button onClick={() => dupliquerFiche(r)} disabled={dupBusy === r.id}
                    data-testid={`quote-dupliquer-${r.id}`}
                    title="Dupliquer cette fiche (coordonnées et articles) — pour un client qui recommande la même chose"
                    className="p-2 rounded-lg border border-ivory/15 text-ivory/60 hover:border-gold/50 hover:text-gold transition-colors duration-300 disabled:opacity-60">
                    {dupBusy === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button onClick={() => toggleStep(r, "archive")} data-testid={`quote-archive-${r.id}`}
                    title={isArchived ? "Désarchiver la soumission" : "Archiver la soumission"}
                    className={`p-2 rounded-lg border transition-colors duration-300 ${
                      isArchived ? "border-gold/60 text-gold hover:border-gold"
                                 : "border-ivory/15 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
                    {isArchived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                  </button>
                  {r.shipping && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/quote-requests/${r.id}/delivery-slip`}
                      data-testid={`quote-slip-${r.id}`} title="Télécharger le bon de livraison (PDF)"
                      className="p-2 rounded-lg border border-ivory/15 text-ivory/60 hover:border-gold/50 hover:text-gold transition-colors duration-300">
                      <ScrollText className="w-4 h-4" />
                    </a>
                  )}
                  <button onClick={() => deleteQuote(r)} data-testid={`quote-delete-${r.id}`}
                    title={(r.payment?.status === "paid" || r.status?.paye)
                      ? "Vente payée — supprimable en cas d'annulation ou de remboursement (retirée des rapports de taxes)"
                      : "Supprimer définitivement la soumission"}
                    className={`p-2 rounded-lg border transition-colors duration-300 ${
                      (r.payment?.status === "paid" || r.status?.paye)
                        ? "border-ivory/10 text-ivory/25 cursor-not-allowed"
                        : "border-red-500/30 text-red-400/80 hover:bg-red-500 hover:text-ivory"}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                  </>)}
                </div>
              </div>

              {/* VENDEUR (demande du propriétaire) : détail complet en LECTURE SEULE —
                  il voit les dossiers cochés pour lui, mais ne modifie rien */}
              {open && (vendeur ? (
                <VendeurQuoteDetails r={r} />
              ) : (
                <div className="mt-4 pt-4 border-t border-ivory/10" data-testid={`quote-details-${r.id}`}>
                  {/* Infos client MODIFIABLES (demande du propriétaire — « tout modifier
                      dans cette section ») : nom, organisation, ville, téléphone,
                      courriel, « livrer avant le » — permis même après paiement */}
                  <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Client (modifiable)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4" data-testid={`quote-client-fields-${r.id}`}>
                    {DETAIL_FIELDS.map(([k, lab]) => {
                      // Cases OBLIGATOIRES (demande du propriétaire, 2026-10-22) :
                      // nom complet, adresse, ville, code postal, téléphone,
                      // courriel ET « livrer avant le » — marquées d'un astérisque,
                      // exigées à l'enregistrement (admin comme vendeur)
                      const OBLIG = ["name", "address1", "city", "postal_code", "phone", "email", "needed_by"];
                      return (
                      <label key={k} className="block">
                        <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">
                          {lab}{OBLIG.includes(k) && <span className="text-gold"> *</span>}
                        </span>
                        <input type={k === "needed_by" ? "date" : k === "email" ? "email" : "text"}
                          multiple={k === "email" || undefined}
                          required={OBLIG.includes(k)}
                          value={detailsOf(r)[k]}
                          onChange={(e) => setDetail(r, k, k === "phone" ? formatPhone(e.target.value) : e.target.value)}
                          data-testid={`quote-detail-${k}-${r.id}`}
                          className="bg-ink border border-ivory/15 rounded-lg px-2.5 py-1.5 text-xs w-full focus:border-gold focus:outline-none transition-colors duration-300 [color-scheme:dark]" />
                        {k === "email" && (
                          <span className="block text-[9px] text-ivory/40 mt-0.5" data-testid={`quote-detail-email-hint-${r.id}`}>
                            Plusieurs courriels possibles — séparez-les par des virgules : tous recevront les courriels de la fiche.
                          </span>
                        )}
                      </label>
                      );
                    })}
                  </div>
                  {/* Terme de PAIEMENT (demande du propriétaire, 2026-10-21) :
                      choix du propriétaire sur la fiche — sinon le terme convenu
                      au dossier client s'applique. Imprimé sur le PDF et le
                      courriel client. Enregistré aussitôt choisi. */}
                  <label className={`block max-w-md ${r.payment_terms === "manuel" ? "mb-1" : "mb-4"}`}>
                    <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">
                      Terme de paiement (PDF et courriel client)
                    </span>
                    <select value={r.payment_terms || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "manuel") {
                          // Mode MANUEL : on montre le champ texte aussitôt, SANS
                          // appeler l'API — l'enregistrement se fait avec le bouton
                          // « Enregistrer » (le texte des modalités est obligatoire)
                          setItems((xs) => xs.map((x) => (x.id === r.id ? { ...x, payment_terms: "manuel" } : x)));
                          toast.info("Précisez les modalités (ex. : Dépôt 50 %, balance à la livraison) puis « Enregistrer ».");
                        } else {
                          changerTerme(r, v);
                        }
                      }}
                      disabled={busyId === r.id + ":t"}
                      data-testid={`quote-terme-paiement-${r.id}`}
                      className="bg-ink border border-ivory/15 rounded-lg px-2.5 py-1.5 text-xs w-full focus:border-gold focus:outline-none transition-colors duration-300">
                      <option value="">Dossier client — sinon « relâchée au paiement reçu »</option>
                      <option value="reception">Relâchée une fois le paiement reçu</option>
                      <option value="15j">15 jours net</option>
                      <option value="30j">30 jours net</option>
                      <option value="manuel">Manuel — à préciser (ex. : dépôt 50 %, balance à la livraison)</option>
                    </select>
                  </label>
                  {/* Mode MANUEL (demande du propriétaire, 2026-10-22) : texte libre
                      des modalités — « l'option du dépôt » appartient au propriétaire.
                      Imprimé tel quel sur le PDF et le courriel client */}
                  {r.payment_terms === "manuel" && (
                    <div className="mb-4 max-w-md">
                      <div className="flex items-center gap-2">
                        <input type="text" defaultValue={r.payment_terms_custom || ""}
                          key={`${r.id}-${r.payment_terms_custom || ""}`}
                          placeholder="ex. : Dépôt 50 %, balance à la livraison"
                          maxLength={200}
                          list={`termes-modeles-${r.id}`}
                          data-testid={`quote-terme-manuel-${r.id}`}
                          onKeyDown={(e) => { if (e.key === "Enter") changerTerme(r, "manuel", e.target.value); }}
                          className="bg-ink border border-amber-400/40 rounded-lg px-2.5 py-1.5 text-xs flex-1 focus:border-gold focus:outline-none transition-colors duration-300" />
                        {/* Modèles conservés (demande du propriétaire, 2026-10-22) :
                            proposés à la saisie, réutilisables d'une fiche à l'autre */}
                        <datalist id={`termes-modeles-${r.id}`}>
                          {termPresets.map((p) => <option key={p} value={p} />)}
                        </datalist>
                        <button type="button"
                          onClick={(e) => changerTerme(r, "manuel", e.currentTarget.parentElement.querySelector("input").value)}
                          disabled={busyId === r.id + ":t"}
                          data-testid={`quote-terme-manuel-save-${r.id}`}
                          className="px-3 py-1.5 rounded-full text-xs border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors whitespace-nowrap disabled:opacity-60">
                          Enregistrer
                        </button>
                        <button type="button"
                          onClick={(e) => garderModeleTerme(e.currentTarget.parentElement.querySelector("input").value)}
                          title="Conserver ce texte comme modèle réutilisable — il sera proposé dans toutes les fiches"
                          data-testid={`quote-terme-manuel-preset-${r.id}`}
                          className="px-3 py-1.5 rounded-full text-xs border border-ivory/25 text-ivory/60 hover:border-gold hover:text-gold transition-colors whitespace-nowrap">
                          Garder en modèle
                        </button>
                      </div>
                      {termPresets.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`quote-terme-modeles-${r.id}`}>
                          <span className="text-[9px] uppercase tracking-widest text-ivory/40">Modèles :</span>
                          {termPresets.map((p) => (
                            <button key={p} type="button"
                              onClick={() => changerTerme(r, "manuel", p)}
                              title="Appliquer ce modèle à la fiche"
                              data-testid={`quote-terme-modele-${r.id}-${p.slice(0, 12)}`}
                              className="text-[10px] px-2.5 py-1 rounded-full border border-amber-400/40 text-amber-300 hover:bg-amber-400 hover:text-ink transition-colors">
                              {p.length > 42 ? p.slice(0, 42) + "…" : p}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Blocs « Facturé à » et « Livrer à » (demande du propriétaire,
                      2026-10-07) : adresses distinctes FACULTATIVES — vides = le
                      client ci-dessus. Imprimées sur la soumission, la facture
                      et le bon de livraison (main propre incluse). */}
                  {[["facture_a", "Facturé à"], ["livraison_a", "Livrer à"]].map(([bloc, titre]) => (
                    <fieldset key={bloc} className="mb-4 rounded-lg border border-ivory/12 px-3 pb-3 pt-1"
                      data-testid={`quote-${bloc}-${r.id}`}>
                      <legend className="text-[10px] uppercase tracking-widest text-gold px-1">{titre}</legend>
                      <p className="text-[10px] text-ivory/40 mb-2">Laisser vide si identique au client ci-dessus.</p>
                      <label className="flex items-center gap-2 mb-2.5 cursor-pointer select-none w-fit"
                        data-testid={`quote-${bloc}-copier-label-${r.id}`}>
                        <input type="checkbox" className="w-3.5 h-3.5 accent-[#E6C981]"
                          checked={adresseCopiee(r, bloc)}
                          onChange={(e) => copierAdresseClient(r, bloc, e.target.checked)}
                          data-testid={`quote-${bloc}-copier-${r.id}`} />
                        <span className="text-[11px] text-ivory/70">Copier l'adresse du client ci-dessus</span>
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {[["name", "Nom"], ["address1", "Adresse"], ["address2", "Adresse (suite)"],
                          ["city", "Ville"], ["province", "Prov."], ["postal_code", "Code postal"]].map(([k, lab]) => (
                          <label key={k} className="block">
                            <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">{lab}</span>
                            <input type="text"
                              value={(detailsOf(r)[bloc] || {})[k] || ""}
                              onChange={(e) => setDetailBloc(r, bloc, k, e.target.value)}
                              data-testid={`quote-${bloc}-${k}-${r.id}`}
                              className="bg-ink border border-ivory/15 rounded-lg px-2.5 py-1.5 text-xs w-full focus:border-gold focus:outline-none transition-colors duration-300" />
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ))}
                  {/* Texte choisi par le client + pièces jointes (demande du propriétaire) :
                      textes de gravure/impression (réponse du portail ou demande initiale)
                      et fichiers joints — lecture seule, regroupés sous les coordonnées */}
                  {(() => {
                    const { textes, fichiers } = gravureOf(r);
                    if (!textes.length && !fichiers.length) return null;
                    return (
                      <div className="mb-4 rounded-lg border border-gold/20 bg-gold/5 p-3" data-testid={`quote-client-files-${r.id}`}>
                        <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Texte choisi par le client et pièces jointes</p>
                        {textes.length > 0 && (
                          <ul className="space-y-1 mb-1.5">
                            {textes.map((t, i) => (
                              <li key={i} className="text-xs text-ivory/85" data-testid={`quote-client-text-${r.id}-${i}`}>
                                <span className="text-ivory/50">{t.nom} : </span>
                                {t.methode && <strong className="text-gold" data-testid={`quote-client-method-${r.id}-${i}`}>{t.methode} — </strong>}
                                {t.texte && <span className="whitespace-pre-wrap">« {t.texte} »</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                        {fichiers.length > 0 && (
                          <div className="flex flex-wrap gap-3" data-testid={`quote-client-files-list-${r.id}`}>
                            {fichiers.map((f, i) => {
                              const isImg = /\.(png|jpe?g|webp|gif|svg)$/i.test(f.url);
                              return (
                                <div key={i} className="flex items-center gap-2">
                                  {isImg && (
                                    <button onClick={() => setZoomImg(f)}
                                      data-testid={`quote-client-thumb-${r.id}-${i}`}
                                      title={`${f.nom} — cliquer pour agrandir`}
                                      className="block w-11 h-11 rounded-lg border border-gold/30 overflow-hidden hover:border-gold hover:scale-105 transition-all duration-300 cursor-zoom-in shrink-0">
                                      <img src={f.url} alt={f.nom} loading="lazy"
                                        className="w-full h-full object-contain bg-ink" />
                                    </button>
                                  )}
                                  <a href={f.url} target="_blank" rel="noreferrer" download
                                    data-testid={`quote-client-file-${r.id}-${i}`}
                                    title={`Télécharger : ${f.nom}`}
                                    className="inline-flex items-center gap-1 text-xs text-gold hover:underline">
                                    <FileDown className="w-3 h-3" /> {f.nom}
                                  </a>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Cases « Visible par le vendeur X » (demande du propriétaire) :
                      le vendeur ne voit QUE les dossiers cochés pour lui (+ ceux de
                      ses clients assignés) dans son portail /vendeur */}
                  {vendeurs.length > 0 && (
                    <div className="mb-4" data-testid={`quote-vendeurs-${r.id}`}>
                      <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Visible par les vendeurs</p>
                      <div className="flex flex-wrap gap-2">
                        {vendeurs.map((v) => {
                          const on = (r.visible_vendeurs || []).includes(v.id);
                          return (
                            <label key={v.id} data-testid={`quote-vendeur-${v.id}-${r.id}`}
                              className={`flex items-center gap-2 text-xs rounded-full border px-3 py-1.5 cursor-pointer transition-colors duration-300 ${on ? "border-emerald-400/60 text-emerald-300 bg-emerald-400/10" : "border-ivory/15 text-ivory/60 hover:border-gold/40"}`}>
                              <input type="checkbox" className="accent-emerald-400" checked={on}
                                onChange={() => toggleVendeurVisible(r, v.id)} />
                              {v.name || v.email}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Étiquette Stallion Express (demande du propriétaire) : une fois
                      la vente payée, création + impression de l'étiquette ici même,
                      puis suivi automatique du colis (« Livré » se coche tout seul) */}
                  {(r.payment?.status === "paid" || r.status?.paye) && (
                    <div className="mb-4" data-testid={`quote-stallion-${r.id}`}>
                      <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Expédition Stallion</p>
                      <StallionLabel kind="quote" docId={r.id}
                        prefill={(() => {
                          // « Livrer à » confirmé par le client PRIME sur l'adresse de
                          // facturation (demande du propriétaire 2026-10-10) ; sinon la
                          // fiche client (adresse complète obligatoire depuis 2026-10-10)
                          const lv = (r.livraison_meme_adresse === false && (r.livraison_a || {}).address1)
                            ? r.livraison_a : null;
                          return { name: lv?.name || r.name || "",
                                   address1: lv?.address1 || r.address1 || "",
                                   address2: lv?.address2 || r.address2 || "",
                                   city: lv?.city || r.city || "",
                                   province: lv?.province || r.province || "QC",
                                   postal_code: lv?.postal_code || r.postal_code || "",
                                   phone: r.phone || "", email: r.email || "" };
                        })()}
                        stallion={r.stallion}
                        onSaved={(s) => setItems((xs) => xs.map((x) => (x.id === r.id ? { ...x, stallion: s } : x)))} />
                    </div>
                  )}
                  <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Produits et prix par ligne</p>
                  <datalist id={`quote-cat-list-${r.id}`}>
                    {catalogueOptions.map((c) => <option key={c} value={c} />)}
                  </datalist>
                  {/* COLLAGE D'IMAGE DE PRODUIT (correctif 2026-10-30 — « la miniature
                      n'accepte pas le copier-coller ») : un Ctrl+V N'IMPORTE OÙ dans
                      la zone des lignes dépose l'image sur la DERNIÈRE LIGNE TOUCHÉE
                      (la case image de la ligne accepte aussi le collage direct).
                      Le collage de TEXTE dans les champs reste normal. */}
                  <div onPaste={(e) => {
                    const f = [...(e.clipboardData?.files || [])][0];
                    if (!f || !f.type?.startsWith("image/")) return;
                    e.preventDefault();
                    const cible = (derniereLigneRef.current || "").startsWith(`${r.id}:`)
                      ? parseInt(derniereLigneRef.current.split(":")[1], 10) : 0;
                    const idx = Math.max(0, Math.min(cible, itemsOf(r).length - 1));
                    toast.success(`Image collée sur la ligne ${idx + 1} — « Enregistrer les modifications » pour la garder.`);
                    uploadItemFile(r, idx, f);
                  }}>
                  {r.payment?.status === "paid" ? (
                    <p className="text-[11px] text-emerald-300/80 mb-2" data-testid={`quote-items-locked-${r.id}`}>
                      Vente payée — les lignes déjà payées sont verrouillées, mais vous pouvez AJOUTER des produits : la balance à facturer sera ajustée automatiquement.
                    </p>
                  ) : (r.client_response?.at) && (
                    <p className="text-[11px] text-amber-300/80 mb-2" data-testid={`quote-items-warn-${r.id}`}>
                      Le client a déjà envoyé sa sélection : modifier ou retirer des lignes peut décaler sa réponse (elle est par position).
                    </p>
                  )}
                  <div className="space-y-3 mb-3">
                    {itemsOf(r).map((it, i) => {
                      const p = draftOf(r)[i] || {};
                      // Verrou PAR LIGNE (ajout après la vente, demande du
                      // propriétaire 2026-10-22) : seules les lignes PAYÉES
                      // (items_count à l'encaissement) sont figées ; les lignes
                      // ajoutées ensuite restent modifiables
                      const nPaid = r.payment?.status === "paid"
                        ? (Number(r.payment?.items_count) || itemsOf(r).length)
                        : 0;
                      const locked = i < nPaid;
                      const fld = "bg-ink border border-ivory/15 rounded-lg px-2.5 py-1.5 text-xs w-full focus:border-gold focus:outline-none transition-colors duration-300 disabled:opacity-50";
                      return (
                        <div key={i} className="rounded-lg border border-ivory/10 p-3" data-testid={`quote-pricing-${r.id}-${i}`}
                          onFocusCapture={() => { derniereLigneRef.current = `${r.id}:${i}`; }}
                          onClickCapture={() => { derniereLigneRef.current = `${r.id}:${i}`; }}>
                          <div className="flex items-center gap-2 mb-2">
                            <p className="text-xs text-ivory/70 flex-1 min-w-0">
                              <span className="text-gold/80 uppercase tracking-wider">Choix {i + 1}</span>
                              {" — "}{it.catalogue || "Hors catalogue"}
                            </p>
                            {/* MINIATURE DU PRODUIT en tête de ligne (demande du
                                propriétaire, 2026-10-30) : emplacement dédié et
                                visible — la 1re image jointe de la ligne s'y
                                affiche (clic = agrandir) ; vide, la case accepte
                                un CLIC (choisir un fichier) ou un COLLAGE (Ctrl+V) ;
                                elle alimente aussi la miniature du courriel client */}
                            {(() => {
                              const imgLigne = attachmentsOf(it)[0];
                              return imgLigne ? (
                                <button type="button"
                                  onClick={() => setZoomImg({ url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${imgLigne}`, nom: it.product_name || `Choix ${i + 1}` })}
                                  data-testid={`quote-item-thumb-${r.id}-${i}`}
                                  title="Image du produit — cliquer pour agrandir"
                                  className="block w-12 h-12 shrink-0 rounded-lg border border-gold/40 overflow-hidden hover:border-gold transition-colors duration-300 cursor-zoom-in">
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${imgLigne}`} alt="Image du produit"
                                    loading="lazy" className="w-full h-full object-contain bg-ink" />
                                </button>
                              ) : (
                                <button type="button"
                                  onClick={() => document.getElementById(`quote-item-thumb-input-${r.id}-${i}`)?.click()}
                                  onPaste={(e) => {
                                    const f = [...(e.clipboardData?.items || [])].find((x) => x.type?.startsWith("image/"));
                                    if (f) { e.preventDefault(); uploadItemFile(r, i, f.getAsFile()); }
                                  }}
                                  data-testid={`quote-item-thumb-add-${r.id}-${i}`}
                                  title="Image du produit — cliquer pour choisir un fichier, ou coller une image (Ctrl+V) n'importe où dans cette ligne"
                                  className="w-12 h-12 shrink-0 rounded-lg border-2 border-dashed border-gold/40 text-gold/60 hover:border-gold hover:text-gold transition-colors duration-300 flex flex-col items-center justify-center gap-0.5">
                                  {logoBusy === `${r.id}:${i}`
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : <><ImagePlus className="w-4 h-4" /><span className="text-[8px] uppercase tracking-wide">image</span></>}
                                </button>
                              );
                            })()}
                            <input type="file" accept="image/*" className="hidden"
                              id={`quote-item-thumb-input-${r.id}-${i}`}
                              data-testid={`quote-item-thumb-input-${r.id}-${i}`}
                              onChange={(e) => uploadItemLogo(r, i, e)} />
                            {!locked && (
                              <button type="button" onClick={() => removeItem(r, i)} disabled={itemsOf(r).length <= 1}
                                data-testid={`quote-item-del-${r.id}-${i}`} title="Retirer cette ligne de produit"
                                className="p-1.5 text-ivory/40 hover:text-red-400 transition-colors disabled:opacity-25 disabled:cursor-not-allowed">
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                          {/* Ligne de produit MODIFIABLE : catalogue, description, nº de produit, quantité */}
                          <div className="grid grid-cols-2 sm:grid-cols-[9.5rem_1fr_7.5rem_4.5rem] gap-2 mb-2">
                            <label className="block">
                              <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">Catalogue</span>
                              <input type="text" list={`quote-cat-list-${r.id}`} value={it.catalogue || ""} disabled={locked}
                                onChange={(e) => setItem(r, i, "catalogue", e.target.value)}
                                data-testid={`quote-item-catalogue-${r.id}-${i}`} placeholder="Hors catalogue"
                                className={fld} />
                            </label>
                            <label className="block">
                              <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">Produit / description</span>
                              <input type="text" value={it.product_name || ""} disabled={locked}
                                onChange={(e) => setItem(r, i, "product_name", e.target.value)}
                                data-testid={`quote-item-name-${r.id}-${i}`} placeholder="ex. : Plaque personnalisée"
                                className={fld} />
                            </label>
                            <label className="block">
                              <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">Nº produit</span>
                              <input type="text" value={it.product_no || ""} disabled={locked}
                                onChange={(e) => { setItem(r, i, "product_no", e.target.value); proposerImageCatalogue(r, i, e.target.value); }}
                                data-testid={`quote-item-no-${r.id}-${i}`} placeholder="ex. : RF2121SG"
                                title="Nº connu ? L'image du catalogue se propose d'elle-même dans la vignette de la ligne"
                                className={fld} />
                            </label>
                            <label className="block">
                              <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">Qté</span>
                              <input type="text" inputMode="numeric" value={it.qty ?? 1} disabled={locked}
                                onChange={(e) => setItem(r, i, "qty", e.target.value)}
                                data-testid={`quote-item-qty-${r.id}-${i}`}
                                className={fld} />
                            </label>
                          </div>
                          {/* Catégorie de vente PAR CHOIX (demande du propriétaire) :
                              interne admin, JAMAIS visible par les clients — placée
                              SOUS LES PRIX ; modifiable même après paiement */}
                          <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                            {[["product", "Prix produit /un."], ["engraving", "Gravure-impr. /un."],
                              ["insert", "Insertion (pastille 1-2 po)"], ["matrix", "Frais de matrice"],
                              ["transport", "Transport"]].map(([k, lab]) => (
                              <label key={k} className="block">
                                <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">{lab}</span>
                                <input type="number" min="0" step="0.01" inputMode="decimal"
                                  value={p[k] ?? ""} onChange={(e) => setPrice(r, i, k, e.target.value)}
                                  data-testid={`quote-price-${k}-${r.id}-${i}`} placeholder="0.00" className={fld} />
                              </label>
                            ))}
                            {/* Rabais de ligne : % ou $ fixe (bascule) — déduit du total de ligne */}
                            <div className="block">
                              <span className="block text-[9px] uppercase tracking-wider text-emerald-400/70 mb-1">Rabais</span>
                              <div className="flex items-center gap-1">
                                <input type="number" min="0" step="0.01" inputMode="decimal"
                                  value={rabaisModeOf(p) === "pct" ? (p.rabais_pct ?? "") : (p.rabais_amt ?? "")}
                                  onChange={(e) => setRabais(r, i, e.target.value)}
                                  data-testid={`quote-price-rabais-${r.id}-${i}`} placeholder="0" className={fld} />
                                <button type="button" onClick={() => toggleRabaisMode(r, i)}
                                  title="Basculer entre rabais en % et en $"
                                  data-testid={`quote-rabais-mode-${r.id}-${i}`}
                                  className="shrink-0 w-8 self-stretch rounded-lg border border-emerald-500/40 text-emerald-400 text-xs font-bold hover:bg-emerald-500 hover:text-ink transition-colors duration-300">
                                  {rabaisModeOf(p) === "pct" ? "%" : "$"}
                                </button>
                              </div>
                            </div>
                          </div>
                          {/* Prix coûtant à côté du prix client (demande du propriétaire,
                              2026-10-20) : admin + vendeur autorisé — jamais côté client */}
                          <CoutantLigne qid={r.id} index={i} qty={num(it.qty) || 1} />
                          {/* Prix de détail SUGGÉRÉ (coûtant × multiplicateur, arrondi au
                              ,95 $ supérieur) — ADMIN SEULEMENT (demande du propriétaire) ;
                              « Appliquer » remplit le prix produit de la ligne */}
                          {!vendeur && !locked && (
                            <SuggPrixLigne qid={r.id} index={i} multiplier={markup}
                              onApply={(v) => setPrice(r, i, "product", v.toFixed(2))} />
                          )}
                          {/* Catégorie de vente PAR CHOIX : sous les prix (demande
                              du propriétaire) — à gauche, Total ligne à droite */}
                          <div className="flex flex-wrap items-end justify-between gap-3 mt-2">
                            <label className="block w-full sm:w-64">
                              <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">Catégorie de vente (interne)</span>
                              <select value={it.sales_category || ""}
                                onChange={(e) => setItem(r, i, "sales_category", e.target.value)}
                                data-testid={`quote-item-category-${r.id}-${i}`}
                                className={fld}>
                                <option value="">(Non classée)</option>
                                {SALES_CATEGORIES.map(([key, lab]) => (
                                  <option key={key} value={key}>{lab}</option>
                                ))}
                              </select>
                            </label>
                            <p className="text-right text-xs text-champagne" data-testid={`quote-line-total-${r.id}-${i}`}>
                              Total ligne : <strong>{lineTotal(it, p).toFixed(2)} $</strong>
                              {rabaisLabel(p) && (
                                <span className="text-emerald-400 text-[10px] ml-1.5" data-testid={`quote-rabais-label-${r.id}-${i}`}>
                                  rabais {rabaisLabel(p)} appliqué
                                </span>
                              )}
                            </p>
                          </div>
                          {/* NOTE de ligne (demande du propriétaire, 2026-10-22) :
                              espace libre placé JUSTE AVANT « Texte gravure /
                              impression » — note interne à l'atelier, jamais
                              imprimée sur les documents du client */}
                          <label className="block mt-2">
                            <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">Note</span>
                            <textarea rows={2} maxLength={2000} value={it.note || ""}
                              onChange={(e) => setItem(r, i, "note", e.target.value)}
                              data-testid={`quote-item-note-${r.id}-${i}`}
                              placeholder="Note interne sur cette ligne (production, attention particulière…)"
                              className={`${fld} resize-y`} />
                          </label>
                          {/* NOTE AU CLIENT (demande du propriétaire, 2026-10-22) :
                              contrairement à la note interne, celle-ci est IMPRIMÉE
                              sur la soumission PDF du client, sous la ligne */}
                          <label className="block mt-2">
                            <span className="block text-[9px] uppercase tracking-wider text-gold/70 mb-1">Note au client — imprimée sur la soumission</span>
                            <textarea rows={2} maxLength={2000} value={it.note_client || ""}
                              onChange={(e) => setItem(r, i, "note_client", e.target.value)}
                              data-testid={`quote-item-note-client-${r.id}-${i}`}
                              placeholder="ex. : Prix valide 30 jours — livraison incluse"
                              className={`${fld} resize-y border-gold/30`} />
                          </label>
                          {/* Texte + type d'inscription + logo (demande du propriétaire) :
                              ajout/correction MANUELLE par l'admin — jamais verrouillé,
                              même après paiement (documentation, aucun montant) */}
                          <div className="grid sm:grid-cols-[1fr_11rem] gap-2 mt-2">
                            <label className="block">
                              <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">Texte gravure / impression</span>
                              <textarea rows={2} maxLength={2000} value={it.details || ""}
                                onChange={(e) => setItem(r, i, "details", e.target.value)}
                                data-testid={`quote-item-details-${r.id}-${i}`}
                                placeholder="ex. : Tournoi des Étoiles 2026 — 1re place"
                                className={`${fld} resize-y`} />
                            </label>
                            {/* Aperçu atelier LIGNE PAR LIGNE (demande du propriétaire
                                2026-10-10) — se met à jour pendant la saisie */}
                            {extraitTexteGravure(it).trim() && (
                              <div className="mt-1.5 rounded-lg border border-gold/25 bg-gold/5 px-3 py-2 sm:col-span-2"
                                data-testid={`quote-gravure-apercu-${r.id}-${i}`}>
                                <span className="block text-[9px] uppercase tracking-wider text-gold/70 mb-0.5">Aperçu atelier — gravure ligne par ligne</span>
                                <GravureLignes texte={extraitTexteGravure(it)} testid={`quote-gravure-${r.id}-${i}`} />
                              </div>
                            )}
                            <label className="block">
                              <span className="block text-[9px] uppercase tracking-wider text-ivory/45 mb-1">Type d'inscription</span>
                              <select value={it.method || ""}
                                onChange={(e) => setItem(r, i, "method", e.target.value)}
                                data-testid={`quote-item-method-${r.id}-${i}`}
                                className={fld}>
                                <option value="">(Non précisé)</option>
                                <option value="laser">Gravure au laser</option>
                                <option value="serigraphie">Impression sérigraphie</option>
                              </select>
                            </label>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {/* TOUTES les pièces jointes de la ligne : miniature
                                agrandissable + retrait (✕) — ajout illimité (10 max) */}
                            {attachmentsOf(it).map((p, j) => (
                              <span key={j} className="relative inline-block">
                                <button type="button"
                                  onClick={() => setZoomImg({ url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`, nom: `${it.product_name || `Choix ${i + 1}`} — pièce jointe ${j + 1}` })}
                                  data-testid={`quote-item-logo-view-${r.id}-${i}-${j}`}
                                  title={`Pièce jointe ${j + 1} — cliquer pour agrandir`}
                                  className="block w-9 h-9 rounded-lg border border-gold/30 overflow-hidden hover:border-gold transition-colors duration-300 cursor-zoom-in">
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt={`Pièce jointe ${j + 1}`}
                                    loading="lazy" className="w-full h-full object-contain bg-ink" />
                                </button>
                                <button type="button"
                                  onClick={() => setItemAttachments(r, i, attachmentsOf(it).filter((_, k) => k !== j))}
                                  data-testid={`quote-item-logo-del-${r.id}-${i}-${j}`}
                                  title="Retirer cette pièce jointe (puis « Enregistrer les modifications »)"
                                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500/90 text-ivory text-[9px] leading-none flex items-center justify-center hover:bg-red-400 transition-colors">
                                  ×
                                </button>
                              </span>
                            ))}
                            <label className={`inline-flex items-center gap-1.5 border border-dashed border-ivory/25 text-ivory/60 px-3 py-1.5 rounded-full text-[11px] cursor-pointer hover:border-gold/60 hover:text-gold transition-colors duration-300 ${logoBusy === `${r.id}:${i}` ? "opacity-50 pointer-events-none" : ""}`}
                              title="Joindre une pièce jointe à cette ligne (image ou PDF, 25 Mo max — plusieurs possibles)">
                              {logoBusy === `${r.id}:${i}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                              Joindre une pièce jointe
                              <input type="file" accept="image/*,.pdf" className="hidden"
                                data-testid={`quote-item-logo-${r.id}-${i}`}
                                onChange={(e) => uploadItemLogo(r, i, e)} />
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>{/* fin de la zone de collage d'image de produit */}
                  {/* Pièces jointes GÉNÉRALES de la soumission (demande du
                      propriétaire, 2026-10-27) — envoyées par le client EN MÊME
                      TEMPS que sa demande, en plus des pièces par article */}
                  {(r.attachments || []).length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`quote-pieces-${r.id}`}>
                      <span className="text-[9px] uppercase tracking-wider text-ivory/45">Pièces jointes de la soumission :</span>
                      {(r.attachments || []).map((p, j) => (
                        <button key={j} type="button"
                          onClick={() => setZoomImg({ url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`, nom: `Pièce jointe de la soumission ${j + 1}` })}
                          data-testid={`quote-piece-view-${r.id}-${j}`}
                          title={`Pièce jointe de la soumission ${j + 1} — cliquer pour agrandir`}
                          className="block w-9 h-9 rounded-lg border border-gold/30 overflow-hidden hover:border-gold transition-colors duration-300 cursor-zoom-in">
                          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt={`Pièce jointe ${j + 1}`}
                            loading="lazy" className="w-full h-full object-contain bg-ink" />
                        </button>
                      ))}
                    </div>
                  )}
                  {/* AJOUT APRÈS LA VENTE (demande du propriétaire, 2026-10-22) :
                      offert même sur une vente payée — la balance sera ajustée */}
                  <button type="button" onClick={() => addItem(r)}
                    data-testid={`quote-item-add-${r.id}`}
                    className="inline-flex items-center gap-1.5 border border-dashed border-gold/40 text-gold px-4 py-2 rounded-full text-xs hover:bg-gold/10 transition-colors duration-300 mb-3">
                    <Plus size={13} /> Ajouter un produit
                  </button>
                  {/* Rabais GLOBAL : toute la soumission (gros volumes), en plus des
                      rabais par ligne — % ou $ fixe, déduit avant les taxes */}
                  <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`quote-global-rabais-row-${r.id}`}>
                    <span className="text-[10px] uppercase tracking-wider text-emerald-400/80">
                      Rabais global (toute la soumission)
                    </span>
                    <input type="number" min="0" step="0.01" inputMode="decimal"
                      value={globalOf(r).value}
                      onChange={(e) => setGlobal(r, { value: e.target.value })}
                      data-testid={`quote-global-rabais-${r.id}`} placeholder="0"
                      className="bg-ink border border-emerald-500/30 rounded-lg px-2.5 py-1.5 text-xs w-24 focus:border-emerald-400 focus:outline-none transition-colors duration-300" />
                    <button type="button"
                      onClick={() => setGlobal(r, { mode: globalOf(r).mode === "pct" ? "amt" : "pct" })}
                      title="Basculer entre rabais global en % et en $"
                      data-testid={`quote-global-rabais-mode-${r.id}`}
                      className="w-8 h-[30px] rounded-lg border border-emerald-500/40 text-emerald-400 text-xs font-bold hover:bg-emerald-500 hover:text-ink transition-colors duration-300">
                      {globalOf(r).mode === "pct" ? "%" : "$"}
                    </button>
                    {globalLabel(r) && (
                      <span className="text-[10px] text-emerald-300/80">
                        appliqué après les rabais de ligne, avant taxes
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <p className="text-sm text-ivory/80" data-testid={`quote-grand-total-${r.id}`}>
                      Grand total : <strong className="text-gold">
                        {grandNet(r).toFixed(2)} $
                      </strong> <span className="text-ivory/45 text-xs">avant taxes</span>
                      {globalLabel(r) && (
                        <span className="text-emerald-400 text-[10px] ml-1.5" data-testid={`quote-global-rabais-label-${r.id}`}>
                          rabais global {globalLabel(r)} appliqué
                        </span>
                      )}
                    </p>
                    {/* Total APRÈS taxes dans la fiche + lien de téléchargement de la
                        facture PDF (demande du propriétaire) — taxes calculées en direct
                        sur les prix affichés (mêmes taux que Stripe et le PDF) */}
                    <p className="text-sm text-ivory/80" data-testid={`quote-total-ttc-${r.id}`}>
                      Après taxes : <strong className="text-gold">{totalTtc(r).toFixed(2)} $</strong>
                      {r.tax_exempt
                        ? <span className="text-emerald-400 text-xs font-semibold"> — EXEMPT DE TAXES</span>
                        : <span className="text-ivory/45 text-xs"> (TPS {tpsOf(r).toFixed(2)} $ · TVQ {tvqOf(r).toFixed(2)} $)</span>}
                    </p>
                    {/* Case « Exempt de taxes » (demande du propriétaire) : TPS/TVQ à 0 $
                        pour les clients exemptés — impossible une fois la vente payée ; un
                        lien de paiement en attente est invalidé (à recréer sans taxes) */}
                    {!(r.payment?.status === "paid" || r.status?.paye) && (
                      <button onClick={() => toggleTaxExempt(r)} disabled={busyId === r.id + ":tx"}
                        data-testid={`quote-tax-exempt-${r.id}`}
                        title="TPS/TVQ à 0 $ pour les clients exemptés (organismes, Premières Nations, gouvernements)"
                        className={`inline-flex items-center gap-1.5 border px-4 py-2 rounded-full text-xs transition-colors duration-300 ${
                          r.tax_exempt ? "bg-emerald-400 text-ink border-emerald-400 font-bold"
                                       : "border-ivory/25 text-ivory/80 hover:border-emerald-400 hover:text-emerald-300"}`}>
                        {busyId === r.id + ":tx" && <Loader2 className="w-3 h-3 animate-spin" />}
                        {r.tax_exempt ? "Exempt de taxes ✓" : "Exempt de taxes"}
                      </button>
                    )}
                    {r.tax_exempt && (r.payment?.status === "paid" || r.status?.paye) && (
                      <span className="text-[10px] uppercase tracking-widest border border-emerald-500/40 text-emerald-300 rounded-full px-2.5 py-1"
                        data-testid={`quote-tax-exempt-paid-${r.id}`}>Exempt de taxes</span>
                    )}
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/quote-requests/${r.id}/pdf`}
                      target="_blank" rel="noreferrer"
                      data-testid={`quote-invoice-pdf-${r.id}`}
                      title="Télécharger la facture en PDF (client, prix, rabais, TPS/TVQ, total)"
                      className="inline-flex items-center gap-1.5 border border-ivory/25 text-ivory/80 px-4 py-2 rounded-full text-xs hover:border-gold hover:text-gold transition-colors duration-300">
                      <FileDown className="w-3.5 h-3.5" /> Facture PDF
                    </a>
                    <button onClick={() => savePricing(r)} disabled={busyId === r.id + ":p"}
                      data-testid={`quote-save-pricing-${r.id}`}
                      className="inline-flex items-center gap-1.5 border border-gold/40 text-gold px-4 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
                      {busyId === r.id + ":p" && <Loader2 className="w-3 h-3 animate-spin" />} Enregistrer les modifications
                    </button>
                    <button onClick={() => sendQuote(r)} disabled={busyId === r.id + ":s"}
                      data-testid={`quote-send-${r.id}`}
                      className="inline-flex items-center gap-1.5 bg-gold text-ink font-bold px-4 py-2 rounded-full text-xs hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                      {busyId === r.id + ":s" && <Loader2 className="w-3 h-3 animate-spin" />}
                      {r.quote_sent_at ? "Renvoyer la soumission au client" : "Envoyer la soumission au client"}
                    </button>
                    {r.quote_sent_at && (
                      <span className="text-[10px] text-emerald-300/80" data-testid={`quote-sent-at-${r.id}`}>
                        envoyée le {fmtDate(r.quote_sent_at)}
                      </span>
                    )}
                    {/* Rappel + pas à pas MANUELS (demande du propriétaire, 2026-10-07) :
                        le contenu suit l'étape du dossier ; aperçu MODIFIABLE
                        (objet + note) avant l'envoi */}
                    {r.quote_sent_at && !r.payment?.status?.includes("paid") && (
                      <>
                        <button onClick={() => ouvrirCourrielEtape(r, "rappel")}
                          data-testid={`quote-rappel-${r.id}`}
                          title="Envoyer un rappel au client (contenu selon son étape) — aperçu avant l'envoi"
                          className="inline-flex items-center gap-1.5 border border-sky-500/50 text-sky-300 px-4 py-2 rounded-full text-xs hover:bg-sky-500 hover:text-ink transition-colors duration-300">
                          <Bell className="w-3.5 h-3.5" /> Envoyer un rappel
                        </button>
                        <button onClick={() => ouvrirCourrielEtape(r, "pasapas")}
                          data-testid={`quote-pasapas-${r.id}`}
                          title="Envoyer le pas à pas FR+EN (selon son étape) aux clients peu à l'aise avec internet — aperçu avant l'envoi"
                          className="inline-flex items-center gap-1.5 border border-violet-400/50 text-violet-300 px-4 py-2 rounded-full text-xs hover:bg-violet-500 hover:text-ink transition-colors duration-300">
                          <ListChecks className="w-3.5 h-3.5" /> Étapes pas à pas
                        </button>
                      </>
                    )}
                    <button onClick={() => { setPayFor(r); setPayMode("full"); setPayValue(""); setPayUnit("$"); }} disabled={payBusy === r.id}
                      data-testid={`quote-paylink-${r.id}`}
                      className="inline-flex items-center gap-1.5 border border-emerald-500/50 text-emerald-300 px-4 py-2 rounded-full text-xs hover:bg-emerald-500 hover:text-ink transition-colors duration-300 disabled:opacity-60">
                      {payBusy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                      Lien de paiement
                    </button>
                    {r.payment?.status === "partial" && (
                      <span className="text-[10px] text-sky-300" data-testid={`quote-partial-info-${r.id}`}>
                        Acompte reçu {Number(r.payment.paid_total || 0).toFixed(2)} $ — solde {Number(r.payment.balance_due || 0).toFixed(2)} $
                      </span>
                    )}
                    {/* Dépôt reçu = relances de paiement EN PAUSE automatiquement
                        (demande du propriétaire, 2026-10-16) — ce bouton réactive
                        la demande de paiement quand l'atelier veut relancer le solde */}
                    {r.payment?.status === "partial" && (
                      <button type="button"
                        data-testid={`quote-payreminder-toggle-${r.id}`}
                        onClick={async () => {
                          const resume = !r.payment?.reminders_resumed;
                          try {
                            await api.post(`/admin/quote-requests/${r.id}/payment-reminders`, { resume });
                            setItems((xs) => xs.map((x) => (x.id === r.id
                              ? { ...x, payment: { ...(x.payment || {}), reminders_resumed: resume } } : x)));
                            toast.success(resume
                              ? "Relances de paiement RÉACTIVÉES — le client sera relancé pour le solde."
                              : "Relances de paiement remises EN PAUSE pour ce dossier.");
                          } catch (err) {
                            toast.error(formatApiError(err.response?.data?.detail));
                          }
                        }}
                        className={`inline-flex items-center gap-1.5 text-[10px] px-3 py-1.5 rounded-full border transition-colors duration-300 ${
                          r.payment?.reminders_resumed
                            ? "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500 hover:text-ink"
                            : "border-amber-400/50 text-amber-300 hover:bg-amber-400 hover:text-ink"}`}>
                        {r.payment?.reminders_resumed
                          ? "Relances du solde : ACTIVES — remettre en pause"
                          : "Relances du solde : EN PAUSE (dépôt reçu) — réactiver"}
                      </button>
                    )}
                    {/* « Solde en un clic » (demande du propriétaire, 2026-10-16) :
                        crée le lien du SOLDE et l'envoie au client par courriel */}
                    {r.payment?.status === "partial" && !r.no_email && (
                      <button type="button"
                        data-testid={`quote-solde-send-${r.id}`}
                        disabled={payBusy === r.id}
                        onClick={async () => {
                          if (!window.confirm(`Créer le lien de paiement du SOLDE (${Number(r.payment.balance_due || 0).toFixed(2)} $) et l'envoyer par courriel à ${r.email} ?`)) return;
                          setPayBusy(r.id);
                          try {
                            await flushDrafts(r);
                            await api.post(`/admin/quote-requests/${r.id}/payment-link`, { mode: "full" });
                            const { data } = await api.post(`/admin/quote-requests/${r.id}/email-payment-link`, {});
                            toast.success(`Lien du solde (${data.solde.toFixed(2)} $) envoyé au client par courriel.`);
                            load();
                          } catch (err) {
                            toast.error(formatApiError(err.response?.data?.detail));
                          } finally {
                            setPayBusy("");
                          }
                        }}
                        className="inline-flex items-center gap-1.5 text-[10px] px-3 py-1.5 rounded-full bg-gold text-ink font-semibold hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                        {payBusy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                        Envoyer le lien du solde au client
                      </button>
                    )}
                    {/* Marge du dossier payé — admin seulement (coûtants) */}
                    {!vendeur && r.payment?.status === "paid" && <MargeDossier r={r} />}
                    {/* Lien du reçu Stripe (demande du propriétaire, 2026-10-06) :
                        preuve de paiement par carte, affiché dès qu'il existe */}
                    {r.payment?.receipt_url && (
                      <a href={r.payment.receipt_url} target="_blank" rel="noreferrer"
                        data-testid={`quote-receipt-${r.id}`}
                        title="Reçu Stripe du paiement par carte — s'ouvre dans un nouvel onglet"
                        className="inline-flex items-center gap-1 text-[10px] text-gold hover:text-champagne underline underline-offset-2 transition-colors">
                        <ScrollText className="w-3 h-3" /> Reçu Stripe
                      </a>
                    )}
                    {/* Remboursement complet ou partiel (demande du propriétaire) */}
                    {(r.payment?.status === "paid" || r.payment?.status === "partial") && (
                      <button onClick={() => { setRefundFor(r); setRefundMode("full"); setRefundValue(""); }}
                        data-testid={`quote-refund-${r.id}`}
                        className="inline-flex items-center gap-1 text-[10px] border border-red-400/40 text-red-300 px-3 py-1.5 rounded-full hover:bg-red-400 hover:text-ink transition-colors duration-300">
                        <RotateCcw className="w-3 h-3" /> Rembourser
                      </button>
                    )}
                    {r.payment?.url && (
                      r.payment.status === "paid" ? (
                        <span className="text-[10px] text-emerald-300" data-testid={`quote-paid-info-${r.id}`}>
                          Payé {fmtDate(r.payment.paid_at)} — {Number(r.payment.amount || 0).toFixed(2)} $
                        </span>
                      ) : (
                        <>
                          <button onClick={async () => {
                            try { await navigator.clipboard.writeText(r.payment.url); toast.success("Lien de paiement copié."); } catch { /* presse-papiers indisponible */ }
                          }}
                            data-testid={`quote-paylink-copy-${r.id}`}
                            className="text-[10px] text-ivory/60 hover:text-gold underline underline-offset-2 transition-colors">
                            paiement en attente — {Number(r.payment.amount || 0).toFixed(2)} $ — copier le lien
                          </button>
                          {/* « Vérifier chez Stripe » — le client dit avoir payé mais
                              le dossier n'est pas passé à « Payé » (webhook manqué) */}
                          <button onClick={() => verifyPayment(r)} disabled={verifyBusy === r.id}
                            data-testid={`quote-verify-payment-${r.id}`}
                            title="Le client dit avoir payé ? Interroge Stripe directement — si le paiement est confirmé, « Payé » se coche et la facture part au client"
                            className="inline-flex items-center gap-1 text-[10px] text-emerald-300 hover:text-emerald-100 underline underline-offset-2 transition-colors disabled:opacity-60">
                            {verifyBusy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            Vérifier chez Stripe
                          </button>
                          {/* QR code de paiement en personne — le client scanne et paie */}
                          <button onClick={() => showQr(r)} disabled={qrBusy === r.id}
                            data-testid={`quote-payment-qr-${r.id}`}
                            title="Paiement en personne : le client scanne ce QR code avec son téléphone et paie — le dossier passe à « Payé » automatiquement"
                            className="inline-flex items-center gap-1 text-[10px] text-gold hover:text-champagne underline underline-offset-2 transition-colors disabled:opacity-60">
                            {qrBusy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                            QR code
                          </button>
                          {/* Aperçu du rappel de paiement automatique (3 jours) — n'envoie rien */}
                          <button onClick={() => previewReminder(r)}
                            data-testid={`quote-reminder-preview-${r.id}`}
                            title="Voir le courriel de rappel que le client recevra si la soumission reste impayée"
                            className="inline-flex items-center gap-1 text-[10px] text-amber-300/90 hover:text-amber-200 underline underline-offset-2 transition-colors">
                            <Eye className="w-3 h-3" /> {r.pay_reminder_sent_at ? "Revoir le rappel envoyé" : "Voir le rappel"}
                          </button>
                          {/* Pilule « Rappel de paiement » (demande du propriétaire,
                              2026-10-22) : ouvre un APERÇU MODIFIABLE (objet + note)
                              avant l'envoi du courriel gentil de rappel au client */}
                          <button onClick={() => openPaymentReminder(r)}
                            data-testid={`quote-reminder-send-${r.id}`}
                            title="Rappel de paiement au client — aperçu modifiable (objet + note) avant l'envoi"
                            className="inline-flex items-center gap-1.5 border border-amber-400/60 text-amber-300 px-4 py-2 rounded-full text-xs hover:bg-amber-400 hover:text-ink transition-colors duration-300 disabled:opacity-60">
                            <Mail className="w-3.5 h-3.5" /> Rappel de paiement
                          </button>
                          {/* Pilule « Dû / passé dû » (demande du propriétaire,
                              2026-10-27) : courriel selon l'ENTENTE de paiement
                              (COD, 15 jours, 30 jours) — « dû » à l'échéance,
                              « passé dû de N j » après — aperçu modifiable */}
                          <button onClick={() => openRelanceTerme(r)}
                            data-testid={`quote-relance-terme-${r.id}`}
                            title={`Courriel « paiement dû / passé dû » selon l'entente (${termeLabel(r)}${r.payment_terms === "manuel" && r.payment_terms_custom ? ` : ${r.payment_terms_custom}` : ""}) — aperçu modifiable avant l'envoi`}
                            className="inline-flex items-center gap-1.5 border border-red-400/60 text-red-300 px-4 py-2 rounded-full text-xs hover:bg-red-400 hover:text-ink transition-colors duration-300 disabled:opacity-60">
                            <AlarmClock className="w-3.5 h-3.5" /> Dû / passé dû ({termeLabel(r)})
                          </button>
                          {r.terme_relance_at && (
                            <span className="text-[10px] text-red-300/80" data-testid={`quote-relance-terme-at-${r.id}`}>
                              dû/passé dû envoyé le {fmtDate(r.terme_relance_at)}
                            </span>
                          )}
                          {r.pay_reminder_sent_at && (
                            <span className="text-[10px] text-amber-300/80" data-testid={`quote-reminder-sent-at-${r.id}`}>
                              relancé le {fmtDate(r.pay_reminder_sent_at)}
                            </span>
                          )}
                        </>
                      )
                    )}
                    <button onClick={() => requestAddresses(r)}
                      disabled={busyId === r.id}
                      data-testid={`quote-address-request-${r.id}`}
                      title="Courriel au client avec le lien pour confirmer ses adresses de facturation et de livraison sur sa page de soumission"
                      className="inline-flex items-center gap-1.5 border border-emerald-500/50 text-emerald-300 px-4 py-2 rounded-full text-xs hover:bg-emerald-500 hover:text-ink transition-colors duration-300 disabled:opacity-60">
                      {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />} SVP confirmer votre adresse
                    </button>
                    {r.addr_request_last && (
                      <span className="text-[10px] text-emerald-300/80" data-testid={`quote-address-request-at-${r.id}`}>
                        demandées le {fmtDate(r.addr_request_last)}
                      </span>
                    )}
                    <button onClick={() => { setAskFor(r); setAskMsg(""); }}
                      data-testid={`quote-ask-info-${r.id}`}
                      title="Poser une question au client par courriel — ses coordonnées sont rappelées dans le message pour vérification"
                      className="inline-flex items-center gap-1.5 border border-sky-500/50 text-sky-300 px-4 py-2 rounded-full text-xs hover:bg-sky-500 hover:text-ink transition-colors duration-300">
                      <HelpCircle className="w-3.5 h-3.5" /> Demander plus d'info
                    </button>
                    {r.ask_info_last && (
                      <span className="text-[10px] text-sky-300/80" data-testid={`quote-ask-info-at-${r.id}`}>
                        demandée le {fmtDate(r.ask_info_last)}
                      </span>
                    )}
                  </div>

                  {/* Réponse du client : produits cochés, textes de gravure, logo */}
                  {r.client_response?.at && (
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 mb-3"
                      data-testid={`quote-response-${r.id}`}>
                      <p className="text-[11px] uppercase tracking-widest text-emerald-300 mb-2">
                        Réponse du client — {fmtDate(r.client_response.at)}
                      </p>
                      {/* RÉCAP EN UN COUP D'ŒIL (demande du propriétaire,
                          2026-10-28) : les modèles COCHÉS (nom + quantité) ;
                          les non retenus restent visibles en grisé */}
                      {(() => {
                        const coches = r.client_response.selected || [];
                        const nomDe = (i) => {
                          const it = (r.items || [])[i] || {};
                          return `${it.product_name || `Choix nº ${i + 1}`}${num(it.qty) > 1 ? ` × ${it.qty}` : ""}`;
                        };
                        const nonRetenus = (r.items || []).map((_, i) => i).filter((i) => !coches.includes(i));
                        return (
                          <>
                            <p className="text-xs font-semibold text-emerald-200 mb-1" data-testid={`quote-response-recap-${r.id}`}>
                              Modèles cochés : {coches.length ? coches.map(nomDe).join("  •  ") : "—"}
                            </p>
                            {nonRetenus.length > 0 && (
                              <p className="text-[11px] text-ivory/40 mb-1.5" data-testid={`quote-response-nonretenus-${r.id}`}>
                                Non retenu{nonRetenus.length > 1 ? "s" : ""} : {nonRetenus.map(nomDe).join("  •  ")}
                              </p>
                            )}
                          </>
                        );
                      })()}
                      <ul className="space-y-1 text-xs text-ivory/75">
                        {(r.client_response.selected || []).map((i) => (
                          <li key={i}>
                            <strong>Choix {i + 1}</strong>{(r.items || [])[i]?.product_name ? ` — ${(r.items || [])[i].product_name}` : ""} ({(r.items || [])[i]?.catalogue})
                            {(r.client_response.texts || {})[String(i)]
                              ? <span className="block text-ivory/55 italic">« {(r.client_response.texts || {})[String(i)]} »</span>
                              : null}
                          </li>
                        ))}
                      </ul>
                      {r.client_response.logo && (
                        <a href={r.client_response.logo} target="_blank" rel="noreferrer"
                          data-testid={`quote-logo-${r.id}`}
                          className="inline-block mt-2 text-xs text-gold underline underline-offset-2 hover:text-champagne">
                          Logo : {r.client_response.logo_name || "voir le fichier"}
                        </a>
                      )}
                    </div>
                  )}

                  {/* Refus avec motif (admin ou client) + bouton contre-offre −5 %
                      si le motif est « le prix » (une seule fois par soumission) */}
                  {r.refusal?.at && (
                    <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 mb-3"
                      data-testid={`quote-refusal-${r.id}`}>
                      <p className="text-[11px] uppercase tracking-widest text-red-300 mb-1.5">
                        Refus {r.refusal.by === "client" ? "du client" : "de l'atelier"} — {fmtDate(r.refusal.at)}
                      </p>
                      <p className="text-xs text-ivory/75">
                        <strong>Motif : {(REFUSAL_MOTIFS.find(([k]) => k === r.refusal.motif) || [])[1] || r.refusal.motif}</strong>
                        <span className="block text-ivory/55 italic mt-0.5">« {r.refusal.note} »</span>
                      </p>
                      {r.refusal.motif === "prix" && !r.discount?.offered_at && (
                        <button onClick={() => { setDiscFor(r); setDiscPct(discPctDef); }}
                          data-testid={`quote-discount-${r.id}`}
                          className="mt-2.5 inline-flex items-center gap-1.5 bg-gold text-ink font-bold px-4 py-2 rounded-full text-xs hover:bg-champagne transition-colors duration-300">
                          <Percent className="w-3.5 h-3.5" />
                          Proposer un rabais (produits + gravure) au client
                        </button>
                      )}
                      {r.discount?.offered_at && (
                        <p className="mt-2 text-[10px] text-emerald-300/80" data-testid={`quote-discount-at-${r.id}`}>
                          Rabais {r.discount.pct} % proposé le {fmtDate(r.discount.offered_at)} — « Refusé » décoché, nouveaux prix enregistrés.
                        </p>
                      )}
                    </div>
                  )}

                  {r.shipping && (
                    <p className="text-xs text-emerald-300/90" data-testid={`quote-shipping-${r.id}`}>
                      Expédition : {r.shipping.method === "hand"
                        ? "livraison en main propre"
                        : r.shipping.method === "pickup"
                          ? "prête à être ramassée"
                          : <>{carrierLabel(r.shipping)} — suivi{" "}
                              {(r.shipping.tracking_url || trackingUrl(r.shipping.carrier, r.shipping.tracking)) ? (
                                <a href={r.shipping.tracking_url || trackingUrl(r.shipping.carrier, r.shipping.tracking)}
                                  target="_blank" rel="noreferrer" data-testid={`quote-shipping-link-${r.id}`}
                                  className="underline underline-offset-2 hover:text-gold transition-colors">
                                  {r.shipping.tracking}
                                </a>
                              ) : r.shipping.tracking}
                              {/* Prix coûtant : admin SEULEMENT (jamais chez le vendeur) */}
                              {!vendeur && r.shipping.cost > 0 && (
                                <span className="ml-2 text-amber-300/80" data-testid={`quote-ship-cost-${r.id}`}>
                                  · coûtant {Number(r.shipping.cost).toFixed(2)} $
                                </span>
                              )}
                            </>} ({fmtDate(r.shipping.at)})
                    </p>
                  )}

                  {/* Signature de livraison (main propre / ramassage) : preuve dans
                      la fiche — image + nom + date, ou raison du saut */}
                  {r.delivery_signature && (
                    <div className="mt-2 rounded-lg border border-gold/25 bg-gold/5 p-3" data-testid={`quote-signature-${r.id}`}>
                      {r.delivery_signature.skipped ? (
                        <p className="text-xs text-amber-300/90">
                          Livrée SANS signature le {fmtDate(r.delivery_signature.at)} — raison : {r.delivery_signature.reason}
                        </p>
                      ) : (
                        <div className="flex items-center gap-3">
                          <img src={`/api/files/${r.delivery_signature.path}`} alt="Signature du client"
                            className="h-12 rounded bg-white px-2 py-1 border border-ivory/15" />
                          {r.delivery_signature.photo_path && (
                            <img src={`/api/files/${r.delivery_signature.photo_path}`} alt="Photo de livraison"
                              className="h-12 w-12 object-cover rounded border border-ivory/15" />
                          )}
                          <p className="text-xs text-ivory/80">
                            Reçu par <strong>{r.delivery_signature.name}</strong><br />
                            <span className="text-ivory/50">{fmtDate(r.delivery_signature.at)}</span>
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Badge « VENTE SAUVÉE » (demande du propriétaire, 2026-10-06) :
                      le client a accepté la contre-offre — l'atelier a aussi reçu
                      un courriel immédiat « Alerte prix chauds » */}
                  {r.discount?.accepted_at && (
                    <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-emerald-400/60 bg-emerald-500/15 text-emerald-300 px-3 py-1 text-[11px] font-bold"
                      data-testid={`quote-saved-sale-${r.id}`}>
                      <Check className="w-3.5 h-3.5" />
                      Vente sauvée — contre-offre de −{r.discount.pct} % acceptée par le client le {fmtDate(r.discount.accepted_at)}
                    </p>
                  )}

                  {/* Historique complet : TOUTES les dates de la soumission, en ordre
                      chronologique (réception, envoi, étapes cochées, réponse client,
                      refus, contre-offre, paiement) — demande client */}
                  <div className="mt-3 rounded-lg border border-ivory/10 p-3" data-testid={`quote-history-${r.id}`}>
                    <p className="text-[11px] uppercase tracking-widest text-gold mb-2">Historique</p>
                    <ul className="space-y-1">
                      {[
                        ["Soumission reçue", r.created_at],
                        ["Soumission envoyée au client", r.quote_sent_at],
                        ...STEPS.filter(([k]) => k !== "refuse" && r.status_at?.[k])
                          .map(([k, lab]) => [stepLabel(r, k, lab), r.status_at[k]]),
                        ["Réponse du client (produits, textes, logo)", r.client_response?.at],
                        [r.refusal?.at
                          ? `Refus ${r.refusal.by === "client" ? "du client" : "de l'atelier"} — ${(REFUSAL_MOTIFS.find(([k]) => k === r.refusal.motif) || [])[1] || ""}`
                          : null, r.refusal?.at],
                        [`Contre-offre −${r.discount?.pct || 5} % envoyée`, r.discount?.offered_at],
                        ["Contre-offre acceptée par le client — VENTE SAUVÉE", r.discount?.accepted_at],
                        ["Lien de paiement créé", r.payment?.created_at],
                        ["Paiement reçu", r.payment?.paid_at],
                        ["Rappel de paiement automatique envoyé au client", r.pay_reminder_sent_at],
                        // Modifications de contenu (client, produits, prix) — qui, quand, quoi
                        ...(r.edit_history || []).map((h) => [`${h.summary} — par ${h.by}`, h.at]),
                      ].filter(([, at]) => at)
                        .sort((a, b) => String(a[1]).localeCompare(String(b[1])))
                        .map(([lab, at], i) => (
                          <li key={i} className="text-[11px] text-ivory/55 flex justify-between gap-3">
                            <span>{lab}</span>
                            <span className="text-ivory/40 whitespace-nowrap">{fmtDate(at)}</span>
                          </li>
                        ))}
                    </ul>
                  </div>
                </div>
              ))}

              {/* Étapes cliquables — réservées à l'admin (vendeur : lecture seule) */}
              {!vendeur && (
              <>
              {/* Raison du retrait par le client (demande du propriétaire, 2026-10-06) :
                  affichée sous la pilule grise « Retirée par le client » */}
              {r.status?.retire && r.retire_reason && (
                <p className="mt-2 text-xs text-ivory/60 border-l-2 border-neutral-400/50 pl-2"
                  data-testid={`quote-retire-reason-${r.id}`}>
                  Raison du retrait : « {r.retire_reason} »
                </p>
              )}
              {/* Case « sans courriel » PERSISTANTE (demande du propriétaire,
                  2026-10-16) : enregistrée sur le dossier — elle survit au
                  rechargement, bloque TOUTES les étapes ET met les courriels
                  automatiques (relances de paiement et d'épreuve) en pause */}
              <label className="mt-3 flex items-center gap-2 text-[11px] text-ivory/60 cursor-pointer select-none w-fit hover:text-gold transition-colors"
                data-testid={`quote-silent-${r.id}`}>
                <input type="checkbox" checked={!!r.no_email}
                  onChange={async (e) => {
                    const v = e.target.checked;
                    try {
                      await api.patch(`/admin/quote-requests/${r.id}/status`, { key: "no_email", value: v });
                      setItems((xs) => xs.map((x) => (x.id === r.id ? { ...x, no_email: v } : x)));
                      toast.success(v
                        ? "Case enregistrée : plus AUCUN courriel ne partira pour ce dossier (relances automatiques en pause)."
                        : "Case décochée : les courriels reprennent pour ce dossier.");
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    }
                  }}
                  className="w-3.5 h-3.5 accent-[#d4af37] cursor-pointer" />
                Ne pas envoyer de courriel au client (toutes les étapes)
              </label>
              {/* « Marquer comme vente perdue » RETIRÉ (demande du propriétaire,
                  2026-10-22) : la pilule « Retirée par le client » fait la même chose —
                  elle ouvre maintenant le popup de raison (rapport « Ventes perdues »,
                  relances arrêtées) */}
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`quote-steps-${r.id}`}>
                {STEPS.map(([key, lab]) => {
                  // Pilule FUSIONNÉE Accepté/Refusé (demande du propriétaire,
                  // 2026-10-22) : UNE seule pilule — moitié dorée « Accepté »,
                  // moitié rouge « Refusé »
                  if (key === "refuse") return null;
                  if (key === "accepte") {
                    const onA = !!r.status?.accepte;
                    const onR = !!r.status?.refuse;
                    // Pilule de DÉCISION (demande du propriétaire, 2026-10-22) :
                    // par DÉFAUT « Accepté » SEUL et SANS COULEUR, SANS le mot
                    // « Refusé » ; UN CLIC ouvre le petit choix Accepter (doré) /
                    // Refuser (rouge) juste sous la pilule. Une fois décidée, la
                    // pilule devient DORÉE « Accepté » ou ROUGE « Refusé » —
                    // un clic sur la pilule colorée décoche l'état.
                    if (onA) return (
                      <button key="decision" onClick={() => toggleStep(r, "accepte")}
                        data-testid={`quote-step-accepte-${r.id}`}
                        title="Accepté — cliquer pour décocher"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-gold text-ink transition-colors duration-300">
                        <Check className="w-3 h-3" />Accepté
                      </button>
                    );
                    if (onR) return (
                      <button key="decision" onClick={() => toggleStep(r, "refuse")}
                        data-testid={`quote-step-refuse-${r.id}`}
                        title="Refusé — cliquer pour décocher"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold bg-red-500 text-white transition-colors duration-300">
                        <Check className="w-3 h-3" />Refusé
                      </button>
                    );
                    return (
                      <span key="decision" className="relative inline-block">
                        <button onClick={() => setDecisionFor(decisionFor === r.id ? null : r.id)}
                          data-testid={`quote-step-accepte-${r.id}`}
                          title="Décision — cliquer pour choisir : Accepter (doré) ou Refuser (rouge)"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-ivory/20 text-[11px] text-ivory/60 hover:border-gold/50 hover:text-gold transition-colors duration-300">
                          Accepté
                        </button>
                        {/* Petit choix sous la pilule : le mot « Refusé » ne s'affiche
                            qu'ICI, au moment de décider */}
                        {decisionFor === r.id && (
                          <>
                            <button className="fixed inset-0 z-[60] cursor-default" aria-label="Fermer"
                              onClick={() => setDecisionFor(null)} />
                            <span className="absolute left-0 top-full mt-1.5 z-[70] flex rounded-full border border-ivory/20 bg-ink shadow-xl shadow-black/50 overflow-hidden text-[11px]"
                              data-testid={`quote-decision-pop-${r.id}`}>
                              <button onClick={() => { setDecisionFor(null); toggleStep(r, "accepte"); }}
                                data-testid={`quote-decision-accepter-${r.id}`}
                                className="px-3 py-1.5 text-gold hover:bg-gold hover:text-ink font-semibold transition-colors duration-300">
                                Accepter
                              </button>
                              <button onClick={() => { setDecisionFor(null); toggleStep(r, "refuse"); }}
                                data-testid={`quote-decision-refuser-${r.id}`}
                                className="px-3 py-1.5 border-l border-ivory/20 text-red-300 hover:bg-red-500 hover:text-white font-semibold transition-colors duration-300">
                                Refuser
                              </button>
                            </span>
                          </>
                        )}
                      </span>
                    );
                  }
                  const on = !!r.status?.[key];
                  // Flux épreuve : la pilule « Montage accepté » suit la réponse du
                  // client — BLEU envoyée, ROUGE modifications demandées, OR acceptée
                  const pStat = key === "montage_accepte" ? r.proof?.status : null;
                  // Paiement PARTIEL reçu : la pastille « Payé » devient BLEUE
                  // (acompte — demande du propriétaire 2026-10-27 : « bleu pas
                  // verte ») ; DORÉE au complet.
                  // CEINTURE DE SÉCURITÉ (2026-10-28 — bogue « pastille disparue ») :
                  // les fiches dont le statut « partial » avait été ÉCRASÉ par la
                  // création du lien du solde (avant le correctif serveur) se
                  // reconnaissent à paid_total > 0 < total_due — la pastille bleue
                  // RÉAPPARAÎT sans toucher aux données
                  const _pt = Number(r.payment?.paid_total || 0);
                  const _td = Number(r.payment?.total_due || 0);
                  const partiel = key === "paye" && !on && (
                    r.payment?.status === "partial"
                    || (_pt > 0.004 && _td > 0 && _pt < _td - 0.004));
                  // Pilule « Payé » = VERT FLUO une fois la commande soldée
                  // (demande du propriétaire, 2026-11-02 : « plus clair ») — le
                  // paiement reçu saute aux yeux d'un coup d'œil. L'acompte
                  // reste BLEU (règle permanente).
                  const cls = key === "retire" && on
                    ? "bg-neutral-500/80 text-white border-neutral-400 font-bold"  // retrait client : pilule grise
                    : partiel
                    ? "bg-sky-500 text-white border-sky-500 font-bold"
                    : key === "paye" && on
                    ? "bg-[#4dff1e] text-ink border-[#4dff1e] font-bold"
                    : pStat === "modifications"
                    ? "bg-red-500 text-white border-red-500 font-bold"
                    : pStat === "envoyee"
                    ? "bg-sky-500 text-white border-sky-500 font-bold"
                    : on ? "bg-gold text-ink border-gold font-bold"
                    : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold";
                  return (
                    <span key={key} className="inline-flex items-center gap-1.5">
                    <button onClick={() => toggleStep(r, key)}
                      data-testid={`quote-step-${key}-${r.id}`}
                      title={partiel
                        ? `Acompte reçu : ${Number(r.payment?.paid_total || 0).toFixed(2)} $ — solde : ${Number(r.payment?.balance_due || 0).toFixed(2)} $`
                        : ""}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] transition-colors duration-300 ${cls}`}>
                      {(on || pStat || partiel) && <Check className="w-3 h-3" />}
                      {key === "expedie" && <Truck className="w-3 h-3" />}
                      {key === "paye" && <CreditCard className="w-3 h-3" />}
                      {stepLabel(r, key, lab)}
                      {partiel && <span className="text-[9px] opacity-80">(acompte)</span>}
                    </button>
                    {/* DÉPÔT EN CLAIR (demande du propriétaire, 2026-10-22) : le
                        détail « dépôt reçu — solde » paraît À CÔTÉ de la pastille
                        Payé, sans ouvrir la fiche */}
                    {partiel && (
                      <span className="text-[10px] text-sky-300 whitespace-nowrap"
                        data-testid={`quote-step-paye-detail-${r.id}`}>
                        dépôt reçu {Number(r.payment?.paid_total || 0).toFixed(2)} $ — solde{" "}
                        {Number(r.payment?.balance_due ?? Math.max(0, (_td || totalTtc(r)) - _pt)).toFixed(2)} $
                      </span>
                    )}
                    {/* RABAIS DE BIENVENUE sur la fiche (demande du propriétaire,
                        2026-10-23) : WELCOME10 appliqué à la 1re soumission payée
                        d'un abonné infolettre — visible sans ouvrir la fiche */}
                    {Number(r.payment?.welcome_rabais?.amount || 0) > 0 && (
                      <span className="text-[10px] text-emerald-300 whitespace-nowrap"
                        data-testid={`quote-welcome-rabais-${r.id}`}>
                        bienvenue −{Number(r.payment.welcome_rabais.amount).toFixed(2)} $
                      </span>
                    )}
                    {/* RAPPEL DE SOLDE — MANUEL SEULEMENT (demande du propriétaire,
                        2026-10-22) : quand le dépôt date de 7 jours ou plus, un
                        macaron le signale ; le CLIC ouvre l'aperçu modifiable du
                        rappel de solde — RIEN n'est automatique */}
                    {partiel && (() => {
                      const t = Date.parse(r.payment?.paid_at || "");
                      if (!t) return null;
                      const jours = Math.floor((Date.now() - t) / 86400000);
                      if (jours < 7) return null;
                      return (
                        <button type="button" onClick={() => relancerSolde(r)}
                          data-testid={`quote-relance-solde-${r.id}`}
                          title="Ouvre l'aperçu modifiable du rappel de solde — envoi manuel seulement"
                          className="text-[10px] text-amber-300 border border-amber-500/40 bg-amber-500/10 rounded-full px-2 py-0.5 hover:bg-amber-500/25 transition-colors whitespace-nowrap">
                          dépôt depuis {jours} j — relancer le solde
                        </button>
                      );
                    })()}
                    </span>
                  );
                })}
              </div>
              {/* Date exacte d'acceptation de l'épreuve + avertissement coché,
                  visible dans l'admin (demande du propriétaire 2026-10-10) */}
              {r.proof?.status === "acceptee" && r.proof?.responded_at && (
                <p className="text-[10px] text-emerald-300/90 mt-1.5" data-testid={`quote-proof-accepted-at-${r.id}`}>
                  Épreuve acceptée le {fmtDate(r.proof.responded_at)}
                  {r.proof.warning_accepted ? " — avertissement « variations de gravure » lu et coché" : ""}
                </p>
              )}
              </>
              )}
            </div>
          );
        })}
      </div>

      {/* Visionneuse de pièce jointe (demande du propriétaire) : clic sur la
          miniature de gravure dans la carte → image agrandie + téléchargement direct */}
      {zoomImg && (
        <div className="fixed inset-0 z-[97] flex items-center justify-center p-4" data-testid="gravure-lightbox">
          <button className="absolute inset-0 bg-ink/90 backdrop-blur-sm" onClick={() => setZoomImg(null)} aria-label="Fermer" />
          <div className="relative max-w-3xl w-full flex flex-col items-center">
            <img src={zoomImg.url} alt={zoomImg.nom} data-testid="gravure-lightbox-img"
              className="max-w-full max-h-[78vh] object-contain rounded-xl border border-gold/30 bg-ink shadow-2xl shadow-black/60" />
            <div className="flex items-center gap-4 mt-3">
              <p className="text-xs text-ivory/60 max-w-xs truncate">{zoomImg.nom}</p>
              <a href={zoomImg.url} download target="_blank" rel="noreferrer"
                data-testid="gravure-lightbox-download"
                className="inline-flex items-center gap-1.5 bg-gold text-ink text-xs font-bold px-4 py-1.5 rounded-full hover:bg-champagne transition-colors duration-300">
                <FileDown className="w-3.5 h-3.5" /> Télécharger
              </a>
              <button onClick={() => setZoomImg(null)} data-testid="gravure-lightbox-close"
                className="text-xs text-ivory/50 hover:text-gold transition-colors duration-300">
                Fermer
              </button>
            </div>
            {/* LIEN DE L'IMAGE à partager (demande du propriétaire, 2026-10-27 :
                « cette miniature sera envoyée par moi-même via le lien de
                l'image ») — affiché en clair + copie en 1 clic */}
            <div className="flex items-center gap-2 mt-2 w-full max-w-xl bg-ink/80 border border-ivory/15 rounded-full pl-4 pr-1.5 py-1.5">
              <p className="text-[11px] text-ivory/55 truncate flex-1 select-all" data-testid="gravure-lightbox-lien">{zoomImg.url}</p>
              <button type="button" data-testid="gravure-lightbox-copier"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(zoomImg.url);
                    toast.success("Lien de l'image copié — collez-le où vous voulez.");
                  } catch { toast.error("Copie impossible — sélectionnez le lien à la main."); }
                }}
                className="inline-flex items-center gap-1.5 border border-gold/50 text-gold text-xs font-bold px-3.5 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 shrink-0">
                <Copy className="w-3.5 h-3.5" /> Copier le lien
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation avant courriel (demande du propriétaire) : boîte MAISON avec
          case « Ne pas envoyer de courriel au client » — remplace le window.confirm
          natif, qui ne peut pas contenir de case à cocher */}
      {emailAsk && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center p-4" data-testid="email-confirm-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => closeEmailAsk(false)} aria-label="Annuler" />
          <div data-lenis-prevent className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 shadow-2xl shadow-black/60">
            <p className={`text-sm mb-1.5 ${emailAskSkip && emailAsk.allowSkip ? "text-emerald-300" : "text-amber-300"}`}>
              {emailAskSkip && emailAsk.allowSkip
                ? "AUCUN courriel ne partira au client — l'action sera marquée seulement."
                : `⚠ Ce clic ENVERRA un courriel à ${emailAsk.email}.`}
            </p>
            <p className="text-xs text-ivory/70 mb-4">{emailAsk.quoi}</p>
            {emailAsk.allowSkip && (
              <label className="flex items-center gap-2 text-xs text-ivory/70 cursor-pointer select-none mb-5 hover:text-gold transition-colors"
                data-testid="email-confirm-skip-label">
                <input type="checkbox" data-testid="email-confirm-skip" checked={emailAskSkip}
                  onChange={(e) => setEmailAskSkip(e.target.checked)}
                  className="w-4 h-4 accent-[#d4af37] cursor-pointer" />
                Ne pas envoyer de courriel au client
              </label>
            )}
            <div className="flex gap-3">
              <button onClick={() => closeEmailAsk(true)} data-testid="email-confirm-ok"
                className="flex-1 bg-gold text-ink font-bold px-5 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors">
                {emailAskSkip && emailAsk.allowSkip ? "Continuer sans courriel" : "Confirmer"}
              </button>
              <button onClick={() => closeEmailAsk(false)} data-testid="email-confirm-cancel"
                className="px-5 py-2.5 rounded-full text-sm border border-ivory/20 text-ivory/70 hover:border-gold/50 hover:text-gold transition-colors">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup « Rembourser » : COMPLET ou PARTIEL (demande du propriétaire) —
          remboursement réel via Stripe, tracé dans l'historique */}
      {refundFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="refund-quote-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setRefundFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setRefundFor(null)} data-testid="refund-quote-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Rembourser — {refundFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-6">
              Payé à ce jour : {Number(refundFor.payment?.paid_total || refundFor.payment?.amount || 0).toFixed(2)} $
              {Number(refundFor.payment?.refunded_total || 0) > 0 && ` · déjà remboursé : ${Number(refundFor.payment.refunded_total).toFixed(2)} $`}
              {" · reste : "}
              {(Number(refundFor.payment?.paid_total || refundFor.payment?.amount || 0) - Number(refundFor.payment?.refunded_total || 0)).toFixed(2)} $
            </p>
            <div className="space-y-3 mb-5">
              <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                refundMode === "full" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}
                data-testid="refund-mode-full">
                <input type="radio" name="refund-mode" className="accent-[#d4af37]"
                  checked={refundMode === "full"} onChange={() => setRefundMode("full")} />
                <span className="text-sm">Remboursement complet (décoche « Payé »)</span>
              </label>
              <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                refundMode === "partial" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}
                data-testid="refund-mode-partial">
                <input type="radio" name="refund-mode" className="accent-[#d4af37]"
                  checked={refundMode === "partial"} onChange={() => setRefundMode("partial")} />
                <span className="text-sm">Remboursement partiel (montant en $)</span>
              </label>
            </div>
            {refundMode === "partial" && (
              <input type="number" min="0" step="0.01" inputMode="decimal" value={refundValue}
                onChange={(e) => setRefundValue(e.target.value)} placeholder="Montant à rembourser ($)"
                data-testid="refund-quote-amount"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm mb-5 focus:border-gold focus:outline-none transition-colors duration-300" />
            )}
            <button onClick={refundQuote} disabled={refundBusy} data-testid="refund-quote-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-red-400 text-ink font-bold px-6 py-3.5 rounded-full hover:bg-red-300 transition-colors duration-300 disabled:opacity-60">
              {refundBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              {refundMode === "full" ? "Rembourser tout" : "Rembourser ce montant"}
            </button>
          </div>
        </div>
      )}

      {/* Popup « CONSULTATION RAPIDE » d'une facture payée du même client
          (demande du propriétaire, 2026-10-24) : ouvert depuis les numéros
          « Factures payées » du résumé du haut — lecture seule + PDF */}
      {factureRapide && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="facture-rapide-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setFactureRapide(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setFactureRapide(null)} data-testid="facture-rapide-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-1 pr-8">
              <h3 className="font-serif text-2xl" data-testid="facture-rapide-titre">
                Facture {factureRapide.invoice_no || "—"}
              </h3>
              {/* Pastille « Client or » aussi EN TÊTE du popup (demande du
                  propriétaire, 2026-10-27) — même seuil réglable que les cartes */}
              {totalEncaisseClient(factureRapide) >= seuilOr && (
                <span className="text-[9px] uppercase tracking-widest border border-gold bg-gold text-ink font-bold rounded-full px-2 py-0.5"
                  data-testid="facture-rapide-client-or"
                  title={`Client au-delà de ${seuilOr.toFixed(0)} $ encaissés à vie : ${totalEncaisseClient(factureRapide).toFixed(2)} $`}>
                  Client or
                </span>
              )}
            </div>
            <p className="text-xs text-ivory/55 mb-5">
              {factureRapide.name} — {(factureRapide.payment?.status === "paid" || factureRapide.status?.paye) ? "payée" : "créée"} le {fmtDate(factureRapide.payment?.paid_at || factureRapide.created_at)}
            </p>
            <div className="space-y-1.5 mb-5">
              {itemsOf(factureRapide).map((it, i) => (
                <div key={i} className="flex justify-between gap-3 text-sm"
                  data-testid={`facture-rapide-ligne-${i}`}>
                  <span className="text-ivory/80">{it.qty || 1} × {it.product_name || "Article"}</span>
                  <span className="text-gold font-semibold whitespace-nowrap">
                    {lineTotal(it, (factureRapide.pricing || [])[i] || {}).toFixed(2)} $
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t border-ivory/10 pt-3 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-ivory/55">Avant taxes</span>
                <strong className="text-gold">{grandNet(factureRapide).toFixed(2)} $</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-ivory/55">Après taxes</span>
                <strong className="text-gold">{totalTtc(factureRapide).toFixed(2)} $</strong>
              </div>
              <div className="flex justify-between">
                <span className="text-ivory/55">Encaissé</span>
                <strong className="text-emerald-300" data-testid="facture-rapide-encaisse">
                  {(Number(factureRapide.payment?.paid_total || 0)
                    || ((factureRapide.payment?.status === "paid" || factureRapide.status?.paye)
                        ? totalTtc(factureRapide) : 0)).toFixed(2)} $
                </strong>
              </div>
            </div>
            {/* TOUTES LES FICHES DU CLIENT (demande du propriétaire, 2026-10-24) :
                pas seulement les 3 dernières — chaque fiche est cliquable pour
                passer de l'une à l'autre dans le même popup */}
            {(() => {
              const toutes = toutesFichesClient(factureRapide);
              if (toutes.length <= 1) return null;
              return (
                <div className="mt-5 border-t border-ivory/10 pt-3" data-testid="facture-rapide-toutes">
                  <p className="text-[10px] uppercase tracking-widest text-ivory/45 mb-2"
                    data-testid="facture-rapide-toutes-titre">
                    Toutes les fiches de ce client ({toutes.length})
                    {/* SOUS-TOTAL À VIE du client en tête (demande du propriétaire,
                        2026-10-24) : tout ce qu'il a déjà payé, toutes fiches */}
                    {totalEncaisseClient(factureRapide) > 0 && (
                      <span className="normal-case tracking-normal"
                        data-testid="facture-rapide-total-vie">
                        {" "}— total encaissé à vie :{" "}
                        <strong className="text-emerald-300">{totalEncaisseClient(factureRapide).toFixed(2)} $</strong>
                      </span>
                    )}
                  </p>
                  <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                    {toutes.map((f, i) => {
                      const bal = balanceAPayer(f);
                      const active = f.id === factureRapide.id;
                      const etat = bal <= 0
                        ? `soldée · ${totalTtc(f).toFixed(2)} $`
                        : (Number(f.payment?.paid_total || 0) > 0
                          ? `dépôt — balance ${bal.toFixed(2)} $`
                          : `en cours · ${totalTtc(f).toFixed(2)} $`);
                      return (
                        <button key={f.id} type="button" disabled={active}
                          onClick={() => ouvrirFacture(f)}
                          data-testid={`facture-rapide-fiche-${i}`}
                          title={active ? "Fiche affichée" : "Consulter cette fiche"}
                          className={`w-full flex items-center justify-between gap-2 text-left text-xs rounded-lg px-2.5 py-1.5 border transition-colors duration-300 ${
                            active ? "border-gold/50 bg-gold/10 cursor-default"
                                   : "border-ivory/10 hover:border-gold/40 hover:bg-gold/5"}`}>
                          <span className="text-ivory/75 truncate inline-flex items-center gap-1 min-w-0">
                            {f.invoice_no || `Fiche ${String(f.id).slice(0, 8)}`}
                            {/* Rappel visuel « note interne » sur les AUTRES fiches du
                                client (demande du propriétaire, 2026-10-27) — texte au survol */}
                            {f.note && (
                              <span className="text-gold shrink-0" data-testid={`facture-rapide-note-indice-${i}`}
                                title={`Note interne : ${f.note}`}>
                                <StickyNote className="w-3 h-3" />
                              </span>
                            )}
                            <span className="text-ivory/40"> — {fmtDate(f.payment?.paid_at || f.created_at)}</span>
                          </span>
                          <span className={`whitespace-nowrap font-semibold ${
                            bal <= 0 ? "text-emerald-300"
                                     : (Number(f.payment?.paid_total || 0) > 0 ? "text-sky-300" : "text-ivory/50")}`}>
                            {etat}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            {/* NOTE INTERNE au client, directement depuis le popup (demande du
                propriétaire, 2026-10-24) : jamais envoyée ni imprimée côté client */}
            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-widest text-ivory/45 block mb-1.5">
                Note interne sur ce client (jamais envoyée ni imprimée)
              </label>
              <textarea rows={2} maxLength={2000} value={notePopup}
                onChange={(e) => setNotePopup(e.target.value)}
                data-testid="facture-rapide-note"
                placeholder="ex. « préfère être appelé l'après-midi », « récupère au comptoir »…"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors duration-300" />
              <button onClick={saveNotePopup} disabled={notePopupBusy}
                data-testid="facture-rapide-note-save"
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs border border-ivory/25 text-ivory/70 hover:border-gold/50 hover:text-gold transition-colors duration-300 disabled:opacity-60">
                {notePopupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Enregistrer la note
              </button>
            </div>
            <button onClick={() => printQuote(factureRapide)} data-testid="facture-rapide-print"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3 rounded-full hover:bg-gold/90 transition-colors duration-300">
              <Printer className="w-4 h-4" /> Imprimer le PDF
            </button>
            {/* PDF GROUPÉ de toutes les factures payées du client (demande du
                propriétaire, 2026-10-24) : un seul fichier pour le comptable —
                offert dès qu'au moins 2 factures payées existent */}
            {(() => {
              const nPayees = toutesFichesClient(factureRapide)
                .filter((x) => x.payment?.status === "paid" || x.status?.paye).length;
              if (nPayees < 2) return null;
              return (
                <>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/quote-requests/${factureRapide.id}/factures-groupees.pdf`}
                  data-testid="facture-rapide-pdf-groupe"
                  title={`Télécharger les ${nPayees} factures payées de ce client en un seul PDF`}
                  className="mt-2.5 w-full inline-flex items-center justify-center gap-2 border border-gold/40 text-gold font-semibold px-6 py-3 rounded-full hover:bg-gold/10 transition-colors duration-300">
                  <FileDown className="w-4 h-4" /> Imprimer toutes les factures payées ({nPayees})
                </a>
                {/* ENVOI AU COMPTABLE — TOUJOURS par l'aperçu MODIFIABLE avant
                    l'envoi (règle permanente du propriétaire, 2026-10-27 : « les
                    courriels doivent être modifiables avant l'envoi ») ; le PDF
                    groupé est régénéré et joint à l'envoi */}
                <button onClick={() => { const f = factureRapide; setFactureRapide(null); ouvrirApercuCourriel(f, "comptable"); }}
                  data-testid="facture-rapide-comptable"
                  title="Aperçu modifiable du courriel au comptable (adresse réglée dans Paramètres) — le PDF groupé est joint à l'envoi"
                  className="mt-2.5 w-full inline-flex items-center justify-center gap-2 border border-ivory/25 text-ivory/75 font-semibold px-6 py-3 rounded-full hover:border-gold/50 hover:text-gold transition-colors duration-300">
                  <Mail className="w-4 h-4" /> Envoyer le PDF groupé au comptable
                </button>
                </>
              );
            })()}
            {/* RENVOI DU REÇU en un clic (demande du propriétaire, 2026-10-24) :
                ouvre l'aperçu modifiable du courriel « facture » (règle
                permanente — popup avant tout courriel client) puis envoie le
                PDF régénéré au client, copie à l'atelier — fiches PAYÉES seulement */}
            {(factureRapide.payment?.status === "paid" || factureRapide.status?.paye) && (
              <button onClick={() => { const f = factureRapide; setFactureRapide(null); ouvrirApercuCourriel(f, "facture"); }}
                data-testid="facture-rapide-renvoyer"
                className="mt-2.5 w-full inline-flex items-center justify-center gap-2 border border-gold/40 text-gold font-semibold px-6 py-3 rounded-full hover:bg-gold/10 transition-colors duration-300">
                <Mail className="w-4 h-4" /> Renvoyer le reçu au client
              </button>
            )}
          </div>
        </div>
      )}

      {/* Popup « Lien de paiement » : plein paiement OU ACOMPTE (montant en $ ou %
          du solde — demande du propriétaire) */}
      {payFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="paylink-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setPayFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setPayFor(null)} data-testid="paylink-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Lien de paiement — {payFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-6">Le lien Stripe est créé puis copié — collez-le au client.</p>
            <div className="space-y-3 mb-5">
              <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                payMode === "full" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}
                data-testid="paylink-mode-full">
                <input type="radio" name="pay-mode" className="accent-[#d4af37]"
                  checked={payMode === "full"} onChange={() => setPayMode("full")} />
                <span className="text-sm">Paiement complet{payFor.payment?.paid_total > 0 ? " du solde restant" : ""}</span>
              </label>
              <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                payMode === "partial" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}
                data-testid="paylink-mode-partial">
                <input type="radio" name="pay-mode" className="accent-[#d4af37]"
                  checked={payMode === "partial"} onChange={() => setPayMode("partial")} />
                <span className="text-sm">Dépôt selon l'entente (acompte en $ ou %)</span>
              </label>
            </div>
            {payMode === "partial" && (
              <div className="mb-5">
                {/* Dépôt XX % SELON L'ENTENTE (demande du propriétaire, 2026-10-22) :
                    choix rapides 25/50/75 % ou tout autre % — le montant est calculé
                    en direct sur le solde restant */}
                <div className="flex gap-2 mb-2">
                  <input type="number" min="0" step="0.01" inputMode="decimal" value={payValue}
                    onChange={(e) => setPayValue(e.target.value)} placeholder="Montant ou %"
                    data-testid="paylink-partial-value"
                    className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300" />
                  <div className="flex rounded-lg border border-ivory/15 overflow-hidden" data-testid="paylink-unit">
                    {["$", "%"].map((u) => (
                      <button key={u} type="button" onClick={() => setPayUnit(u)}
                        data-testid={`paylink-unit-${u === "$" ? "cad" : "pct"}`}
                        className={`px-4 text-sm font-bold transition-colors duration-300 ${payUnit === u ? "bg-gold text-ink" : "text-ivory/60 hover:text-gold"}`}>
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  {[25, 50, 75].map((p) => (
                    <button key={p} type="button"
                      onClick={() => { setPayUnit("%"); setPayValue(String(p)); }}
                      data-testid={`paylink-preset-${p}`}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors duration-300 ${
                        payUnit === "%" && payValue === String(p)
                          ? "border-gold bg-gold/15 text-gold"
                          : "border-ivory/15 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
                      Dépôt {p} %
                    </button>
                  ))}
                </div>
                {(() => {
                  const v = parseFloat(String(payValue).replace(",", "."));
                  if (!v || v <= 0) return null;
                  const solde = Math.max(0, totalTtc(payFor) - Number(payFor.payment?.paid_total || 0));
                  const charge = payUnit === "%" ? solde * v / 100 : v;
                  if (charge <= 0) return null;
                  return (
                    <p className="text-xs text-emerald-300/90" data-testid="paylink-partial-estimate">
                      Le client paiera <strong>{charge.toFixed(2)} $</strong>
                      {" — "}solde après : <strong>{Math.max(0, solde - charge).toFixed(2)} $</strong>
                    </p>
                  );
                })()}
              </div>
            )}
            <button
              onClick={() => {
                if (payMode === "partial") {
                  const v = parseFloat(String(payValue).replace(",", "."));
                  if (!v || v <= 0) { toast.error("Inscrivez le montant de l'acompte"); return; }
                  paymentLink(payFor, { value: v, unit: payUnit });
                } else {
                  paymentLink(payFor);
                }
              }}
              disabled={payBusy === payFor.id} data-testid="paylink-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 text-ink font-bold px-6 py-3.5 rounded-full hover:bg-emerald-400 transition-colors duration-300 disabled:opacity-60">
              {payBusy === payFor.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              {payMode === "partial" ? "Créer le lien de dépôt" : "Créer le lien de paiement"}
            </button>
          </div>
        </div>
      )}

      {/* Popup « Signature de livraison » (main propre / ramassage, demande du
          propriétaire 2026-10-08) : le client signe à l'écran avant « Livré » */}
      {signFor && (
        <SignatureLivraison
          titre={`Signature — ${signFor.name}`}
          sousTitre={signFor.shipping?.method === "pickup"
            ? "Ramassage à l'atelier : faites signer le client, puis la facture finale part automatiquement (copie à info@trophee.ca)."
            : "Livraison en main propre : faites signer le client, puis la facture finale part automatiquement (copie à info@trophee.ca)."}
          busy={signBusy}
          sansCourrielDefaut={!!signFor.no_email}
          onSigner={confirmerSignature}
          onSansSignature={sansSignature}
          onFermer={() => setSignFor(null)} />
      )}

      {/* Popup « Expédié » : transporteur (nº de suivi) ou livraison en main propre —
          envoie un courriel au client + copie à commande@trophee.ca */}
      {shipFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="ship-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setShipFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShipFor(null)} data-testid="ship-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Expédier — {shipFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-6">
              Un APERÇU modifiable du courriel (nº de suivi inclus) s'ouvrira avant l'envoi au
              client ({shipFor.email}) — rien ne part sans « Envoyer » dans le popup.
            </p>
            <div className="space-y-3 mb-5">
              <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                shipMethod === "carrier" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}
                data-testid="ship-method-carrier">
                <input type="radio" name="ship-method" className="accent-[#d4af37]"
                  checked={shipMethod === "carrier"} onChange={() => setShipMethod("carrier")} />
                <Truck className="w-4 h-4 text-gold" />
                <span className="text-sm">Transporteur (avec nº de suivi)</span>
              </label>
              <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                shipMethod === "hand" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}
                data-testid="ship-method-hand">
                <input type="radio" name="ship-method" className="accent-[#d4af37]"
                  checked={shipMethod === "hand"} onChange={() => setShipMethod("hand")} />
                <Hand className="w-4 h-4 text-gold" />
                <span className="text-sm">Livraison en main propre</span>
              </label>
              {/* Demande du propriétaire : choix « Prêt à être ramassé » en plus */}
              <label className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                shipMethod === "pickup" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}
                data-testid="ship-method-pickup">
                <input type="radio" name="ship-method" className="accent-[#d4af37]"
                  checked={shipMethod === "pickup"} onChange={() => setShipMethod("pickup")} />
                <Package className="w-4 h-4 text-gold" />
                <span className="text-sm">Prêt à être ramassé</span>
              </label>
            </div>
            {shipMethod === "carrier" && (
              <div className="mb-5 space-y-3">
                <div>
                  <label htmlFor="ship-carrier" className="block text-xs uppercase tracking-widest text-ivory/60 mb-1.5">
                    Transporteur *
                  </label>
                  <select id="ship-carrier" data-testid="ship-carrier" value={shipCarrier}
                    onChange={(e) => setShipCarrier(e.target.value)}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300">
                    <option value="postes_canada">Postes Canada</option>
                    <option value="ups">UPS</option>
                    <option value="fedex">FedEx</option>
                    <option value="purolator">Purolator</option>
                    <option value="dhl">DHL</option>
                    <option value="gls">GLS</option>
                    <option value="ics">ICS Courier</option>
                    <option value="autre">Autre (nom à inscrire)</option>
                  </select>
                </div>
                {shipCarrier === "autre" && (
                  <div>
                    <label htmlFor="ship-carrier-other" className="block text-xs uppercase tracking-widest text-ivory/60 mb-1.5">
                      Nom du transporteur *
                    </label>
                    <input id="ship-carrier-other" data-testid="ship-carrier-other" value={shipCarrierOther}
                      onChange={(e) => setShipCarrierOther(e.target.value)} maxLength={60}
                      placeholder="ex. Transport Bourret"
                      className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300" />
                  </div>
                )}
                <div>
                  <label htmlFor="ship-tracking" className="block text-xs uppercase tracking-widest text-ivory/60 mb-1.5">
                    Numéro de l'envoi *
                  </label>
                  <input id="ship-tracking" data-testid="ship-tracking" value={shipTracking}
                    onChange={(e) => setShipTracking(e.target.value)} maxLength={60}
                    placeholder="ex. 1Z999AA10123456784"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300" />
                  {/* Lien de vérification créé avec le nº de suivi (demande du
                      propriétaire) — le même part dans le courriel au client */}
                  {shipTracking.trim() && trackingUrl(shipCarrier, shipTracking.trim()) && (
                    <a href={trackingUrl(shipCarrier, shipTracking.trim())} target="_blank" rel="noreferrer"
                      data-testid="ship-tracking-link-preview"
                      className="inline-block mt-1.5 text-xs text-gold hover:text-champagne underline underline-offset-2 transition-colors">
                      Vérifier ce numéro chez {CARRIER_LABELS[shipCarrier]} ↗
                    </a>
                  )}
                </div>
                {/* Prix COÛTANT de l'envoi — visible par l'ADMIN SEULEMENT (demande
                    du propriétaire) : jamais montré au client ni aux vendeurs */}
                {!vendeur && (
                  <div>
                    <label htmlFor="ship-cost" className="block text-xs uppercase tracking-widest text-amber-300/80 mb-1.5">
                      Prix coûtant de l'envoi ($) — admin seulement
                    </label>
                    <input id="ship-cost" data-testid="ship-cost" value={shipCost} inputMode="decimal"
                      onChange={(e) => setShipCost(e.target.value.replace(/[^0-9.,]/g, ""))}
                      placeholder="ex. 8.02"
                      className="w-full bg-ink border border-amber-400/30 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300" />
                  </div>
                )}
              </div>
            )}
            {/* Case « ne pas envoyer de courriel au client » (demande du propriétaire) —
                l'étape est marquée, mais rien ne part au client */}
            <label className="flex items-center gap-2.5 mb-5 cursor-pointer text-xs text-ivory/75"
              data-testid="ship-no-email-label">
              <input type="checkbox" className="w-4 h-4 accent-[#d4af37]"
                data-testid="ship-no-email" checked={shipNoEmail}
                onChange={(e) => setShipNoEmail(e.target.checked)} />
              Ne pas envoyer de courriel au client
            </label>
            <button onClick={confirmShip} disabled={shipBusy} data-testid="ship-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              {shipBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
              {shipNoEmail ? "Marquer « Expédié » SANS courriel" : "Voir l'aperçu du courriel avant d'envoyer"}
            </button>
          </div>
        </div>
      )}

      {/* Popup « Refusé » : motif + note OBLIGATOIRES — courriel poli au client
          (+ copie à commande@trophee.ca) */}
      {refuseFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="refuse-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setRefuseFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60">
            <button onClick={() => setRefuseFor(null)} data-testid="refuse-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Refuser — {refuseFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-6">
              {refuseNoEmail
                ? "Case cochée : AUCUN courriel ne partira — le refus sera seulement tracé ici."
                : `Un courriel poli avec ce motif partira au client (${refuseFor.email}) — copie à commande@trophee.ca.`}
            </p>
            <p className="text-xs uppercase tracking-widest text-ivory/60 mb-2">Motif du refus *</p>
            <div className="space-y-2 mb-4">
              {REFUSAL_MOTIFS.map(([k, lab]) => (
                <label key={k} data-testid={`refuse-motif-${k}`}
                  className={`flex items-center gap-3 rounded-xl border p-3.5 cursor-pointer transition-colors duration-300 ${
                    refuseMotif === k ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}>
                  <input type="radio" name="refuse-motif" className="accent-[#d4af37]"
                    checked={refuseMotif === k} onChange={() => setRefuseMotif(k)} />
                  <span className="text-sm">{lab}</span>
                </label>
              ))}
            </div>
            <label htmlFor="refuse-note" className="block text-xs uppercase tracking-widest text-ivory/60 mb-1.5">
              Petite note (obligatoire) *
            </label>
            <textarea id="refuse-note" rows={3} maxLength={1000} data-testid="refuse-note"
              value={refuseNote} onChange={(e) => setRefuseNote(e.target.value)}
              placeholder="ex. Le budget ne permet pas de…"
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300 mb-3" />
            <label className="flex items-center gap-2 text-xs text-ivory/60 cursor-pointer select-none mb-5 hover:text-gold transition-colors"
              data-testid="refuse-no-email-label">
              <input type="checkbox" data-testid="refuse-no-email" checked={refuseNoEmail}
                onChange={(e) => setRefuseNoEmail(e.target.checked)}
                className="w-3.5 h-3.5 accent-[#d4af37] cursor-pointer" />
              Ne pas envoyer de courriel au client
            </label>
            <button onClick={confirmRefuse} disabled={refuseBusy} data-testid="refuse-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-red-500/90 text-ivory font-bold px-6 py-3.5 rounded-full hover:bg-red-500 transition-colors duration-300 disabled:opacity-60">
              {refuseBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              {refuseNoEmail ? "Marquer « Refusé » SANS courriel" : "Marquer « Refusé » et envoyer le courriel"}
            </button>
          </div>
        </div>
      )}

      {paiementFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="paiement-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setPaiementFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setPaiementFor(null)} data-testid="paiement-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Paiement — {paiementFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-6">
              Balance à encaisser : <strong>{((paiementFor.payment?.balance_due || paiementFor.payment?.total || paiementFor.payment?.amount || paiementFor.payment?.total_due || paiementFor.total || 0)).toFixed(2)} $</strong>
              <br />Choisissez le mode de paiement — {paiementMethod === "stripe"
                ? `le lien Stripe est créé, puis vous voyez le courriel de paiement AVANT l'envoi au client (${paiementFor.email}).`
                : `le courriel de confirmation partira au client (${paiementFor.email}).`}
            </p>
            <div className="space-y-3 mb-6">
              <label data-testid="paiement-interac"
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                  paiementMethod === "interac" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}>
                <input type="radio" name="paiement-method" className="accent-[#d4af37]"
                  checked={paiementMethod === "interac"} onChange={() => { setPaiementMethod("interac"); setPaiementComplet(true); setPaiementMontant(""); }} />
                <div className="flex-1">
                  <span className="text-sm font-semibold">Virement Interac</span>
                  <p className="text-xs text-ivory/55 mt-0.5">Le client paie par virement bancaire Interac</p>
                </div>
              </label>
              <label data-testid="paiement-comptant"
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                  paiementMethod === "comptant" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}>
                <input type="radio" name="paiement-method" className="accent-[#d4af37]"
                  checked={paiementMethod === "comptant"} onChange={() => { setPaiementMethod("comptant"); setPaiementComplet(true); setPaiementMontant(""); }} />
                <div className="flex-1">
                  <span className="text-sm font-semibold">Comptant</span>
                  <p className="text-xs text-ivory/55 mt-0.5">Le client paie en argent comptant</p>
                </div>
              </label>
              <label data-testid="paiement-qr"
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                  paiementMethod === "qr" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}>
                <input type="radio" name="paiement-method" className="accent-[#d4af37]"
                  checked={paiementMethod === "qr"} onChange={() => { setPaiementMethod("qr"); setPaiementComplet(true); setPaiementMontant(""); setPaiementUnite("$"); }} />
                <div className="flex-1">
                  <span className="text-sm font-semibold">Code QR</span>
                  <p className="text-xs text-ivory/55 mt-0.5">Ouvrir le code QR pour le paiement en ligne</p>
                </div>
              </label>
              {/* STRIPE (demande du propriétaire, 2026-10-31) : 4e option du popup
                  « Payé » — l'admin/vendeur choisit un paiement partiel en $ OU EN %
                  AVANT l'envoi du courriel de paiement (aperçu modifiable). */}
              <label data-testid="paiement-stripe"
                className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors duration-300 ${
                  paiementMethod === "stripe" ? "border-gold bg-gold/10" : "border-ivory/15 hover:border-gold/40"}`}>
                <input type="radio" name="paiement-method" className="accent-[#d4af37]"
                  checked={paiementMethod === "stripe"} onChange={() => { setPaiementMethod("stripe"); setPaiementComplet(true); setPaiementMontant(""); setPaiementUnite("$"); }} />
                <div className="flex-1">
                  <span className="text-sm font-semibold">Stripe (lien de paiement)</span>
                  <p className="text-xs text-ivory/55 mt-0.5">Lien en ligne envoyé au client — acompte possible en $ ou en %</p>
                </div>
              </label>
            </div>
            {paiementMethod && (
              <div className="mb-6 rounded-xl border border-ivory/15 p-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none mb-2">
                  <input type="checkbox" data-testid="paiement-complet"
                    checked={paiementComplet} onChange={(e) => { setPaiementComplet(e.target.checked); if (e.target.checked) setPaiementMontant(""); }}
                    className="w-4 h-4 accent-[#d4af37]" />
                  <span>Paiement complet</span>
                </label>
                {!paiementComplet && (
                  <div className="mt-2">
                    {/* Dépôt en $ OU en % pour TOUTES les méthodes (Interac,
                        Comptant, Code QR, Stripe — demande du propriétaire,
                        2026-10-31). L'estimation est calculée sur la balance. */}
                    <div className="flex items-center gap-2">
                      <input type="number" min="0.01" step="0.01" data-testid="paiement-montant"
                        value={paiementMontant} onChange={(e) => setPaiementMontant(e.target.value)}
                        placeholder="Montant ou %"
                        className="w-28 bg-ink border border-ivory/15 rounded-lg px-3 py-1.5 text-sm focus:border-gold focus:outline-none" />
                      <div className="flex rounded-lg border border-ivory/15 overflow-hidden" data-testid="paiement-unite">
                        {["$", "%"].map((u) => (
                          <button key={u} type="button" onClick={() => setPaiementUnite(u)}
                            data-testid={`paiement-unite-${u === "$" ? "cad" : "pct"}`}
                            className={`px-3 py-1.5 text-sm font-bold transition-colors duration-300 ${paiementUnite === u ? "bg-gold text-ink" : "text-ivory/60 hover:text-gold"}`}>
                            {u}
                          </button>
                        ))}
                      </div>
                    </div>
                    {(() => {
                      const bal = Number(paiementFor.payment?.balance_due || paiementFor.payment?.total || paiementFor.payment?.amount || paiementFor.payment?.total_due || paiementFor.total || 0);
                      return (
                        <>
                          <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            {[25, 50, 75].map((p) => (
                              <button key={p} type="button"
                                onClick={() => { setPaiementUnite("%"); setPaiementMontant(String(p)); }}
                                data-testid={`paiement-depot-${p}`}
                                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors duration-300 ${
                                  paiementUnite === "%" && paiementMontant === String(p)
                                    ? "border-gold bg-gold/15 text-gold"
                                    : "border-ivory/15 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
                                Dépôt {p} %
                              </button>
                            ))}
                          </div>
                          {Number(paiementMontant) > 0 && (() => {
                            const v = Number(String(paiementMontant).replace(",", "."));
                            const charge = paiementUnite === "%" ? bal * v / 100 : v;
                            if (charge <= 0) return null;
                            return (
                              <p className="text-xs text-emerald-300/90 mt-2" data-testid="paiement-stripe-estim">
                                Le client paiera <strong>{Math.min(charge, bal).toFixed(2)} $</strong>
                                {" — "}solde après : <strong>{Math.max(0, bal - charge).toFixed(2)} $</strong>
                              </p>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
            <button onClick={confirmerPaiement} disabled={paiementBusy || !paiementMethod || (!paiementComplet && !paiementMontant)} data-testid="paiement-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full hover:bg-gold-bright transition-colors duration-300 disabled:opacity-50">
              {paiementBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : paiementMethod === "qr" ? <QrCode className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
              {paiementMethod === "qr" ? "Ouvrir le code QR"
                : paiementMethod === "stripe" ? "Créer le lien et voir le courriel"
                : "Confirmer et envoyer le courriel"}
            </button>
          </div>
        </div>
      )}

      {/* Popup « Épreuve » (demande du propriétaire, 2026-10-06) : l'étape
          « Montage accepté » ouvre ce popup — fichier choisi OU image COLLÉE
          (Ctrl+V), envoi au client (pilule bleue), puis suivi de sa réponse
          (or = acceptée, rouge = modifications) avec ajustement MANUEL possible
          quand le client répond par courriel. */}
      {proofFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="proof-modal"
          onPaste={onProofPaste}>
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setProofFor(null)} aria-label="Fermer" />
          <div data-lenis-prevent className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setProofFor(null)} data-testid="proof-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Épreuve — {proofFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-5">
              L'épreuve part au client ({proofFor.email}) avec un lien sécurisé : il répond
              « J'accepte » ou « Demander des modifications » — la pilule change de couleur toute seule.
            </p>

            {/* Épreuve actuelle (si déjà envoyée) */}
            {(proofFor.proof?.file || proofFor.proof?.drive_link) && (
              <div className="mb-5 rounded-xl border border-sky-400/30 bg-sky-400/5 p-4" data-testid="proof-current">
                <p className="text-[11px] uppercase tracking-widest text-sky-300 mb-1.5">
                  {PROOF_LABELS[proofFor.proof.status] || "Épreuve envoyée"}
                  {proofFor.proof.sent_at ? ` — le ${fmtDate(proofFor.proof.sent_at)}` : ""}
                </p>
                {proofFor.proof?.file && (
                  <a href={`/api/files/${proofFor.proof.file}`} target="_blank" rel="noreferrer" download
                    data-testid="proof-current-file"
                    className="inline-flex items-center gap-1.5 text-xs text-gold hover:underline break-all">
                    <FileDown className="w-3.5 h-3.5 shrink-0" /> {proofFor.proof.file_name || "épreuve"}
                  </a>
                )}
                {proofFor.proof?.drive_link && (
                  <a href={proofFor.proof.drive_download || proofFor.proof.drive_link} target="_blank" rel="noreferrer"
                    data-testid="proof-current-drive"
                    className="mt-1 inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:underline break-all">
                    <Link2 className="w-3.5 h-3.5 shrink-0" /> Lien Google Drive de l'épreuve
                  </a>
                )}
                {proofFor.proof.status === "modifications" && proofFor.proof.client_comment && (
                  <p className="mt-2 text-xs text-red-300 border-l-2 border-red-400/50 pl-2" data-testid="proof-client-comment">
                    « {proofFor.proof.client_comment} »
                  </p>
                )}
                {/* Réponse reçue par COURRIEL/téléphone : ajustement manuel de la pilule */}
                <p className="text-[10px] text-ivory/45 mt-3 mb-1.5">Le client a répondu par courriel ou téléphone ?</p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => markProof("acceptee")} disabled={proofBusy}
                    data-testid="proof-mark-accepted"
                    className="text-[10px] uppercase tracking-widest border border-gold/50 text-gold rounded-full px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60">
                    Marquer acceptée
                  </button>
                  <button onClick={() => markProof("modifications")} disabled={proofBusy}
                    data-testid="proof-mark-changes"
                    className="text-[10px] uppercase tracking-widest border border-red-400/50 text-red-300 rounded-full px-3 py-1.5 hover:bg-red-500 hover:text-white transition-colors duration-300 disabled:opacity-60">
                    Modifications demandées
                  </button>
                </div>
              </div>
            )}

            {/* Zone de dépôt : fichier choisi OU image collée (Ctrl+V) OU lien Drive */}
            <p className="text-xs uppercase tracking-widest text-ivory/60 mb-2">
              {(proofFor.proof?.file || proofFor.proof?.drive_link) ? "Nouvelle épreuve (remplace l'ancienne)" : "Fichier de l'épreuve *"}
            </p>
            <div className="mb-4 rounded-xl border-2 border-dashed border-gold/30 bg-ink p-4 text-center" data-testid="proof-dropzone">
              {proofFile ? (
                <div className="space-y-2">
                  {proofFile.type?.startsWith("image/") && (
                    <img src={URL.createObjectURL(proofFile)} alt="Aperçu de l'épreuve" data-testid="proof-preview"
                      className="max-h-40 mx-auto rounded-lg border border-ivory/15 object-contain" />
                  )}
                  <p className="text-xs text-gold break-all" data-testid="proof-file-name">{proofFile.name || "image collée"}</p>
                  <button onClick={() => setProofFile(null)} data-testid="proof-file-remove"
                    className="text-[10px] text-ivory/50 hover:text-red-300 underline underline-offset-2 transition-colors">
                    retirer
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-ivory/55 mb-3">Collez une image ici (Ctrl+V) — ou :</p>
                  <label className="inline-flex items-center gap-2 bg-gold/15 border border-gold/40 text-gold text-xs font-semibold rounded-full px-5 py-2.5 cursor-pointer hover:bg-gold hover:text-ink transition-colors duration-300">
                    <Plus className="w-3.5 h-3.5" /> Choisir le fichier
                    <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.pdf" className="hidden"
                      data-testid="proof-file-input"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) setProofFile(f); e.target.value = ""; }} />
                  </label>
                  <p className="text-[10px] text-ivory/40 mt-2">PNG, JPG, WebP, SVG ou PDF — 25 Mo max</p>
                </>
              )}
            </div>

            {/* LIEN GOOGLE DRIVE (demande du propriétaire, 2026-07-02) : pour les
                épreuves lourdes/vidéos — le client reçoit un bouton de
                téléchargement direct dans son courriel et sur sa page, RIEN
                n'est joint au courriel. Peut accompagner ou remplacer le fichier. */}
            <p className="text-xs uppercase tracking-widest text-ivory/60 mb-2">
              {proofFile ? "Et/ou un lien Google Drive" : "Ou un lien Google Drive"}
            </p>
            <div className="mb-4 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4" data-testid="proof-drive-zone">
              <label className="flex items-center gap-2 text-xs text-emerald-300/90 mb-2" htmlFor="proof-drive-link">
                <Link2 className="w-3.5 h-3.5 shrink-0" /> Lien de partage Drive (téléchargeable par le client)
              </label>
              <input id="proof-drive-link" type="url" value={proofDriveLink}
                onChange={(e) => setProofDriveLink(e.target.value)}
                data-testid="proof-drive-link"
                placeholder="https://drive.google.com/file/d/…/view?usp=sharing"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2.5 text-xs focus:border-emerald-400 focus:outline-none transition-colors duration-300 placeholder:text-ivory/30" />
              <p className="text-[10px] text-ivory/45 mt-1.5 leading-relaxed">
                Dans Google Drive : clic droit sur le fichier → <strong>Partager</strong> →
                <strong> « Tous les utilisateurs disposant du lien »</strong> → Copier le lien, puis collez-le ici.
                Le client le téléchargera SANS compte Google.
              </p>
            </div>

            {proofFor.no_email ? (
              <button onClick={sendProof} disabled={proofBusy || (!proofFile && !proofDriveLink.trim())} data-testid="proof-send"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {proofBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Enregistrer l'épreuve SANS courriel
              </button>
            ) : (
              /* RÈGLE PERMANENTE (2026-11-01) : le courriel de l'épreuve se RELIT
                 dans un popup d'aperçu (objet modifiable + note) avant de partir */
              <button onClick={ouvrirApercuEpreuve} disabled={proofApercuBusy || (!proofFile && !proofDriveLink.trim())} data-testid="proof-preview-open"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {proofApercuBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                {(proofFor.proof?.file || proofFor.proof?.drive_link) ? "Voir le courriel avant de renvoyer" : "Voir le courriel avant l'envoi"}
              </button>
            )}
            {proofFor.no_email && (
              <p className="text-[10px] text-emerald-300/80 mt-2 text-center">
                Case « sans courriel » cochée sur ce dossier — rien ne partira au client.
              </p>
            )}
          </div>
        </div>
      )}

      {/* APERÇU du courriel d'épreuve AVANT envoi (règle permanente, 2026-11-01) :
          objet modifiable + note en tête + iframe du HTML exact tel qu'il partira */}
      {proofApercu && proofFor && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center p-4" data-testid="proof-apercu-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setProofApercu(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-2xl p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setProofApercu(null)} data-testid="proof-apercu-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors z-10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Courriel de l'épreuve — aperçu avant envoi</h3>
            <p className="text-xs text-ivory/55 mb-5">
              Sera envoyé à {proofFor.name} ({proofFor.email}) avec l'épreuve en pièce jointe
              {proofDriveLink.trim() ? " (lien Google Drive)" : ""}. Rien n'est envoyé tant que vous ne cliquez pas « Envoyer ».
            </p>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-ivory/50 mb-1.5">Objet du courriel (modifiable)</label>
            <input value={proofApercu.sujet} data-testid="proof-apercu-sujet"
              onChange={(e) => setProofApercu({ ...proofApercu, sujet: e.target.value })}
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none mb-4" />
            <label className="block text-[11px] uppercase tracking-[0.18em] text-ivory/50 mb-1.5">Note ajoutée en tête du courriel (facultatif)</label>
            <textarea rows={2} value={proofApercu.note} data-testid="proof-apercu-note"
              onChange={(e) => changerNoteEpreuve(e.target.value)}
              placeholder="ex. « Voici la version avec le nouveau logo — dites-moi si tout est parfait ! »"
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none mb-4 placeholder:text-ivory/30" />
            <iframe title="Aperçu du courriel d'épreuve" sandbox=""
              srcDoc={proofApercu.html} data-testid="proof-apercu-frame"
              className="w-full h-80 bg-white rounded-lg border border-ivory/10 mb-5" />
            <div className="flex items-center gap-3">
              <button onClick={() => setProofApercu(null)} data-testid="proof-apercu-cancel"
                className="px-5 py-2.5 rounded-full border border-ivory/15 text-sm text-ivory/60 hover:border-gold/50 hover:text-gold transition-colors">
                Retour
              </button>
              <button onClick={sendProof} disabled={proofBusy || proofApercu.sujet.trim().length < 3} data-testid="proof-apercu-send"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                {proofBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Envoyer l'épreuve à {proofFor.email}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Popup « Voir le rappel » : aperçu fidèle du courriel de rappel de paiement
          (iframe du HTML exact) — demande du propriétaire, n'envoie rien */}
      {/* QR code de paiement EN PERSONNE (demande du propriétaire) : le client
          scanne avec son téléphone et paie sur place — dossier lié automatiquement */}
      {/* Invitation au site « mode de fonctionnement » (demande du propriétaire,
          2026-11-01) : une ou plusieurs adresses + objet modifiable + note
          pré-remplie + APERÇU exact avant envoi (règle permanente) */}
      {inviteOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="invite-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setInviteOpen(false)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-2xl p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setInviteOpen(false)} data-testid="invite-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors z-10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Inviter au site — mode de fonctionnement</h3>
            <p className="text-xs text-ivory/55 mb-5">
              Le courriel explique le fonctionnement (demande en ligne → prix → suivi à chaque étape →
              facturation) avec le lien de la soumission rapide et Messenger. Copie à info@trophee.ca.
              Rien n'est envoyé tant que vous ne cliquez pas « Envoyer ».
            </p>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-ivory/50 mb-1.5">
              Adresses courriel — une ou plusieurs (virgule, point-virgule ou retour à la ligne)
            </label>
            <textarea rows={2} value={inviteEmails} data-testid="invite-emails"
              onChange={(e) => setInviteEmails(e.target.value)}
              placeholder="ex. client@exemple.com, equipe@ecole.ca&#10;autre@entreprise.ca"
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none mb-4 placeholder:text-ivory/30" />
            <label className="block text-[11px] uppercase tracking-[0.18em] text-ivory/50 mb-1.5">Objet du courriel (modifiable)</label>
            <input value={inviteSujet} data-testid="invite-sujet"
              onChange={(e) => setInviteSujet(e.target.value)}
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none mb-4" />
            <label className="block text-[11px] uppercase tracking-[0.18em] text-ivory/50 mb-1.5">Message en tête du courriel (modifiable)</label>
            <textarea rows={5} value={inviteNote} data-testid="invite-note"
              onChange={(e) => changerNoteInvitation(e.target.value)}
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none mb-4 placeholder:text-ivory/30" />
            {inviteHtml ? (
              <iframe title="Aperçu du courriel d'invitation" sandbox=""
                srcDoc={inviteHtml} data-testid="invite-frame"
                className="w-full h-80 bg-white rounded-lg border border-ivory/10 mb-5" />
            ) : (
              <div className="w-full h-80 rounded-lg border border-ivory/10 mb-5 flex items-center justify-center text-ivory/40 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement de l'aperçu…
              </div>
            )}
            <button onClick={envoyerInvitation} disabled={inviteBusy || !inviteEmails.trim() || inviteSujet.trim().length < 3} data-testid="invite-send"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              {inviteBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Envoyer l'invitation
            </button>
          </div>
        </div>
      )}

      {/* Dépôt MANUEL — sans courriel ni facture (demande du propriétaire, 2026-10-31) */}
      {depotFor && (() => {
        const bal = Number(depotFor.payment?.balance_due || depotFor.payment?.total || depotFor.payment?.total_due || depotFor.total || 0);
        const v = Number(String(depotMontant).replace(",", ".")) || 0;
        const charge = depotUnite === "%" ? bal * v / 100 : v;
        return (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="depot-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setDepotFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-emerald-400/30 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
            <button onClick={() => setDepotFor(null)} data-testid="depot-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Dépôt manuel — {depotFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-1">
              Total : <strong>{bal.toFixed(2)} $</strong>
              {(depotFor.payment?.paid_total || 0) > 0 && <> · Déjà reçu : <strong>{Number(depotFor.payment.paid_total).toFixed(2)} $</strong></>}
            </p>
            <p className="text-xs text-emerald-300/90 mb-5" data-testid="depot-note">
              Aucun courriel ni facture ne sera envoyé au client — le dépôt est simplement enregistré.
            </p>
            <label className="block text-[11px] uppercase tracking-[0.18em] text-ivory/50 mb-1.5">Montant du dépôt</label>
            <div className="flex items-center gap-2 mb-3">
              <input type="number" min="0.01" step="0.01" data-testid="depot-montant"
                value={depotMontant} onChange={(e) => setDepotMontant(e.target.value)}
                placeholder="Montant ou %"
                className="w-32 bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none" />
              <div className="flex rounded-lg border border-ivory/15 overflow-hidden" data-testid="depot-unite">
                {["$", "%"].map((u) => (
                  <button key={u} type="button" onClick={() => setDepotUnite(u)}
                    data-testid={`depot-unite-${u === "$" ? "cad" : "pct"}`}
                    className={`px-3 py-2 text-sm font-bold transition-colors duration-300 ${depotUnite === u ? "bg-emerald-500 text-ink" : "text-ivory/60 hover:text-emerald-300"}`}>
                    {u}
                  </button>
                ))}
              </div>
            </div>
            {v > 0 && (
              <p className="text-xs text-emerald-300/90 mb-4" data-testid="depot-estim">
                Dépôt : <strong>{Math.min(charge, bal).toFixed(2)} $</strong> — balance après : <strong>{Math.max(0, bal - charge).toFixed(2)} $</strong>
              </p>
            )}
            <label className="block text-[11px] uppercase tracking-[0.18em] text-ivory/50 mb-1.5">Mode de paiement</label>
            <div className="grid grid-cols-3 gap-2 mb-6">
              {[["comptant", "Comptant"], ["interac", "Interac"], ["autre", "Autre"]].map(([val, lbl]) => (
                <button key={val} type="button" onClick={() => setDepotMethode(val)}
                  data-testid={`depot-methode-${val}`}
                  className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors duration-300 ${depotMethode === val ? "border-emerald-400 bg-emerald-500/15 text-emerald-300" : "border-ivory/15 text-ivory/60 hover:border-emerald-400/50"}`}>
                  {lbl}
                </button>
              ))}
            </div>
            <button onClick={enregistrerDepot} disabled={depotBusy || !(v > 0)} data-testid="depot-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 text-ink font-bold px-6 py-3.5 rounded-full hover:bg-emerald-400 transition-colors duration-300 disabled:opacity-50">
              {depotBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />}
              Enregistrer le dépôt — sans courriel
            </button>
          </div>
        </div>
        );
      })()}
      {qrFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="payment-qr-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setQrFor(null)} aria-label="Fermer" />
          <div className="relative bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl shadow-black/60 text-center max-h-[90vh] overflow-y-auto">
            <button onClick={() => setQrFor(null)} data-testid="payment-qr-close"
              className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-800 transition-colors z-10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-xl text-neutral-900 mb-1">Paiement en personne</h3>
            <p className="text-xs text-neutral-500 mb-4">
              {qrFor.name} — <strong>{Number(qrFor.amount || 0).toFixed(2)} $</strong> taxes incluses.
              Le client scanne ce code avec son téléphone et paie par carte, Apple Pay ou Google Pay.
            </p>
            <img src={qrFor.img} alt="QR code de paiement" data-testid="payment-qr-image"
              className="w-64 h-64 mx-auto" />
            <p className="text-sm font-bold text-neutral-900 mt-3" data-testid="payment-qr-amount">
              {Number(qrFor.amount || 0).toFixed(2)} $ taxes incluses
            </p>
            <p className="text-[11px] text-neutral-500 mt-2 leading-snug">
              Dès le paiement, le dossier passe à « Payé » tout seul —<br />
              contrairement au Tap to Pay de l'app Stripe, qui ne se lie à aucun dossier.
            </p>
          </div>
        </div>
      )}

      {reminderPreview && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="reminder-preview-modal">          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setReminderPreview(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-2xl p-6 shadow-2xl shadow-black/60">
            <button onClick={() => setReminderPreview(null)} data-testid="reminder-preview-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors z-10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Rappel de paiement — aperçu</h3>
            <p className="text-xs text-ivory/55 mb-1">
              Courriel que <strong>{reminderPreview.name}</strong> recevra si la soumission reste impayée
              3 jours après son acceptation. Rien n'est envoyé.
            </p>
            <p className="text-xs text-ivory/70 mb-4">
              <span className="text-ivory/45">Objet : </span>
              <strong data-testid="reminder-preview-subject">{reminderPreview.subject}</strong>
            </p>
            <iframe title="Aperçu du courriel de rappel" srcDoc={reminderPreview.html}
              data-testid="reminder-preview-frame"
              className="w-full rounded-lg border border-ivory/15 bg-white" style={{ height: 420 }} />
          </div>
        </div>
      )}

      {/* COURRIEL DE SOLDE après un ajout sur vente payée (demande du propriétaire,
          2026-10-23) : proposé D'EMBLÉE — plein solde ou dépôt convenu (ex. 50 %
          du solde) ; l'aperçu modifiable s'ouvre ensuite, rien n'est envoyé tout seul */}
      {soldeOffre && (
        <div className="fixed inset-0 z-[94] flex items-center justify-center p-4" data-testid="solde-offre-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setSoldeOffre(null)} aria-label="Plus tard" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-lg p-6 shadow-2xl shadow-black/60">
            <button onClick={() => setSoldeOffre(null)} data-testid="solde-offre-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors z-10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Nouveau solde à facturer</h3>
            <p className="text-xs text-ivory/55 mb-4">
              Vente de <strong>{soldeOffre.name}</strong> : un produit a été ajouté après le paiement.
              Proposer le paiement à <strong>{soldeOffre.email}</strong> ?
            </p>
            <label className="flex items-center gap-3 rounded-xl border border-ivory/15 px-4 py-3 mb-2 cursor-pointer hover:border-gold/40 transition-colors"
              data-testid="solde-offre-full-row">
              <input type="radio" name="solde-mode" checked={soldeOffre.mode === "full"}
                onChange={() => setSoldeOffre({ ...soldeOffre, mode: "full" })}
                data-testid="solde-offre-full" className="accent-[#d4af37]" />
              <span className="text-sm text-ivory/85">
                Solde complet — <strong className="text-gold">{soldeOffre.solde.toFixed(2)} $</strong>
              </span>
            </label>
            <label className="flex items-center gap-3 rounded-xl border border-ivory/15 px-4 py-3 mb-4 cursor-pointer hover:border-gold/40 transition-colors"
              data-testid="solde-offre-depot-row">
              <input type="radio" name="solde-mode" checked={soldeOffre.mode === "depot"}
                onChange={() => setSoldeOffre({ ...soldeOffre, mode: "depot" })}
                data-testid="solde-offre-depot" className="accent-[#d4af37]" />
              <span className="text-sm text-ivory/85 flex items-center gap-2 flex-wrap">
                Dépôt convenu —
                <input type="number" min="1" max="99" value={soldeOffre.pct}
                  onChange={(e) => setSoldeOffre({ ...soldeOffre, pct: e.target.value })}
                  onClick={(e) => e.preventDefault()}
                  data-testid="solde-offre-pct"
                  className="bg-ink border border-ivory/15 rounded-lg px-2 py-1 text-xs w-16 focus:border-gold focus:outline-none" />
                % du solde
                <strong className="text-gold">
                  ({(soldeOffre.solde * Math.max(1, Math.min(99, Number(soldeOffre.pct) || 50)) / 100).toFixed(2)} $)
                </strong>
              </span>
            </label>
            <p className="text-[11px] text-ivory/45 mb-4">
              Le lien de paiement du montant choisi sera créé, puis l'aperçu modifiable
              du courriel s'ouvrira — rien n'est envoyé sans « Envoyer ».
            </p>
            <div className="flex gap-3">
              <button onClick={() => setSoldeOffre(null)} data-testid="solde-offre-later"
                className="px-4 py-2 text-sm text-ivory/60 hover:text-ivory transition-colors">Plus tard</button>
              <button onClick={preparerCourrielSolde} disabled={soldeOffre.busy} data-testid="solde-offre-preparer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold rounded-full px-4 py-2.5 text-sm hover:bg-champagne transition-colors disabled:opacity-50">
                {soldeOffre.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Préparer l'aperçu du courriel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aperçu MODIFIABLE de la pilule « Rappel de paiement » (demande du
          propriétaire, 2026-10-22) : objet éditable + note d'accompagnement
          facultative, AVANT l'envoi — le corps est régénéré côté serveur
          (jamais de HTML libre). Même mécanisme que « Envoyer un rappel ». */}
      {reminderDraft && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="rappel-paiement-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setReminderDraft(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-2xl p-6 shadow-2xl shadow-black/60 max-h-[92vh] overflow-y-auto">
            <button onClick={() => setReminderDraft(null)} data-testid="rappel-paiement-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors z-10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">
              {reminderDraft.kind === "terme" ? "Paiement dû / passé dû — aperçu avant envoi" : "Rappel de paiement — aperçu avant envoi"}
            </h3>
            <p className="text-xs text-ivory/55 mb-4">
              Sera envoyé à <strong>{reminderDraft.name}</strong> ({reminderDraft.email})
              {reminderDraft.kind === "terme" ? (
                <> — entente <strong className="text-ivory/80">{reminderDraft.terme}</strong>, échéance
                  le <strong className="text-ivory/80">{reminderDraft.echeance}</strong>
                  {reminderDraft.retard > 0
                    ? <strong className="text-red-300"> — passé dû de {reminderDraft.retard} jour{reminderDraft.retard > 1 ? "s" : ""}</strong>
                    : " — dû"}.</>
              ) : (
                <> — le même courriel gentil que la relance automatique, avec le lien pour payer.</>
              )}{" "}
              Rien n'est envoyé tant que vous ne cliquez pas « Envoyer ».
            </p>
            <label className="block mb-3">
              <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">Objet du courriel (modifiable)</span>
              <input value={reminderDraft.sujet}
                onChange={(e) => setReminderDraft({ ...reminderDraft, sujet: e.target.value })}
                data-testid="rappel-paiement-sujet" maxLength={150}
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors" />
            </label>
            <label className="block mb-4">
              <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">Note ajoutée en tête du courriel (facultatif)</span>
              <textarea value={reminderDraft.note} rows={2} maxLength={800}
                onChange={(e) => changerNoteRappel(e.target.value)}
                data-testid="rappel-paiement-note"
                placeholder="ex. « Il ne reste que le paiement pour lancer la production — on est là si vous avez une question ! »"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors resize-none" />
            </label>
            {renderPiecesJointes(items.find((x) => x.id === reminderDraft.qid), "rappel-paiement")}
            <iframe title="Aperçu du rappel de paiement" srcDoc={reminderDraft.html}
              data-testid="rappel-paiement-frame"
              className="w-full rounded-lg border border-ivory/15 bg-white mb-4" style={{ height: 380 }} />
            <div className="flex justify-end gap-3">
              <button onClick={() => setReminderDraft(null)} data-testid="rappel-paiement-cancel"
                className="px-5 py-2.5 rounded-full text-sm border border-ivory/25 text-ivory/70 hover:border-gold hover:text-gold transition-colors">
                Annuler
              </button>
              <button onClick={envoyerRappelPaiement} disabled={reminderBusy || reminderDraft.sujet.trim().length < 3}
                data-testid="rappel-paiement-send"
                className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-6 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors disabled:opacity-50">
                {reminderBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Envoyer à {reminderDraft.email}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aperçu MODIFIABLE du rappel / pas à pas (demande du propriétaire,
          2026-10-07) : objet éditable + note d'accompagnement facultative,
          le tout AVANT l'envoi. Le corps est régénéré côté serveur. */}
      {courrielEtape && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="courriel-etape-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setCourrielEtape(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-2xl p-6 shadow-2xl shadow-black/60 max-h-[92vh] overflow-y-auto">
            <button onClick={() => setCourrielEtape(null)} data-testid="courriel-etape-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors z-10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">
              {courrielEtape.genre === "pasapas" ? "Étapes pas à pas" : "Rappel client"} — aperçu avant envoi
            </h3>
            <p className="text-xs text-ivory/55 mb-4">
              Sera envoyé à <strong>{courrielEtape.nom}</strong> ({courrielEtape.email}) — contenu selon son étape
              (« {courrielEtape.etape} »). Rien n'est envoyé tant que vous ne cliquez pas « Envoyer ».
            </p>
            <label className="block mb-3">
              <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">Objet du courriel (modifiable)</span>
              <input value={courrielEtape.sujet}
                onChange={(e) => setCourrielEtape({ ...courrielEtape, sujet: e.target.value })}
                data-testid="courriel-etape-sujet" maxLength={150}
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors" />
            </label>
            <label className="block mb-4">
              <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">Note ajoutée en tête du courriel (facultatif)</span>
              <textarea value={courrielEtape.note} rows={2} maxLength={800}
                onChange={(e) => changerNoteCourriel(e.target.value)}
                data-testid="courriel-etape-note"
                placeholder="ex. « Passez nous voir au comptoir si vous préférez, on vous aide avec plaisir ! »"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors resize-none" />
            </label>
            {renderPiecesJointes(items.find((x) => x.id === courrielEtape.qid), "courriel-etape")}
            <iframe title="Aperçu du courriel" srcDoc={courrielEtape.html}
              data-testid="courriel-etape-frame"
              className="w-full rounded-lg border border-ivory/15 bg-white mb-4" style={{ height: 380 }} />
            <div className="flex justify-end gap-3">
              <button onClick={() => setCourrielEtape(null)} data-testid="courriel-etape-cancel"
                className="px-5 py-2.5 rounded-full text-sm border border-ivory/25 text-ivory/70 hover:border-gold hover:text-gold transition-colors">
                Annuler
              </button>
              <button onClick={envoyerCourrielEtape} disabled={courrielBusy || courrielEtape.sujet.trim().length < 3}
                data-testid="courriel-etape-send"
                className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-6 py-2.5 rounded-full text-sm hover:bg-champagne transition-colors disabled:opacity-50">
                {courrielBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Envoyer à {courrielEtape.email}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup « Demander plus d'info » : question libre de l'atelier → courriel au
          client avec le rappel de ses coordonnées (demande du propriétaire) */}
      {askFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="ask-info-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setAskFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-lg p-6 shadow-2xl shadow-black/60">
            <button onClick={() => setAskFor(null)} data-testid="ask-info-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Demander plus d'info</h3>
            <p className="text-xs text-ivory/55 mb-4">
              Courriel à <strong>{askFor.name}</strong> ({askFor.email}) — ses coordonnées
              (prénom, nom, téléphone, courriel) sont rappelées dans le message pour vérification.
            </p>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label htmlFor="ask-info-message" className="block text-xs uppercase tracking-widest text-ivory/60">
                Votre question ou précision demandée *
              </label>
              {/* Rédaction par Claude (demande du propriétaire) : brouillon modifiable */}
              <button type="button" disabled={iaBusy} data-testid="ask-info-ai"
                onClick={async () => {
                  setIaBusy(true);
                  try {
                    const { data } = await api.post("/admin/claude/rediger-courriel", {
                      contexte: "reponse",
                      client: `${askFor.name} — soumission : ${(askFor.items || []).map((i) => i.product_name).join(", ").slice(0, 120) || "articles personnalisés"}`,
                      details: askMsg.trim() || "Question de suivi au sujet de sa soumission",
                      langue: askFor.lang === "en" ? "en" : "fr",
                    });
                    setAskMsg(data.texte);
                    toast.success("Brouillon rédigé par Claude — révisez-le avant l'envoi");
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  setIaBusy(false);
                }}
                className="inline-flex items-center gap-1.5 text-[11px] text-gold hover:text-champagne border border-gold/30 hover:border-gold/60 rounded-full px-3 py-1 transition-colors disabled:opacity-50 shrink-0">
                {iaBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />} Rédiger avec Claude
              </button>
            </div>
            <textarea id="ask-info-message" rows={4} maxLength={2000} data-testid="ask-info-message"
              value={askMsg} onChange={(e) => setAskMsg(e.target.value)}
              placeholder="ex. Pouvez-vous confirmer la date de votre tournoi et le nombre exact de médailles ?"
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300 mb-5" />
            <button onClick={askInfo} disabled={askBusy || !askMsg.trim()} data-testid="ask-info-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              {askBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Voir l'aperçu avant envoi
            </button>
          </div>
        </div>
      )}

      {/* APERÇU MODIFIABLE avant tout courriel client de la fiche (demande du
          propriétaire, 2026-10-22) : le courriel TEL QU'IL PARTIRA — objet
          modifiable, note de tête facultative, question éditable avec aperçu
          rafraîchi. Rien ne part sans « Envoyer » dans CE popup */}
      {courrielApercu && (
        <div className="fixed inset-0 z-[96] flex items-center justify-center p-4" data-testid="courriel-apercu-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" aria-label="Fermer"
            onClick={() => !courrielApercu.busy && setCourrielApercu(null)} />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-2xl p-6 shadow-2xl shadow-black/60 max-h-[92vh] overflow-y-auto">
            <button onClick={() => setCourrielApercu(null)} data-testid="courriel-apercu-close" disabled={courrielApercu.busy}
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors z-10" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">
              {courrielApercu.type === "etape"
                ? `« ${EMAILING_STEPS[courrielApercu.etape]} » — aperçu du courriel avant envoi`
                : courrielApercu.type === "expedie"
                  ? "« Expédié » — aperçu du courriel avant envoi"
                  : COURRIEL_TITRES[courrielApercu.type]}
            </h3>
            <p className="text-xs text-ivory/55 mb-4">
              Sera envoyé à <strong>{courrielApercu.name}</strong> ({courrielApercu.email}) —
              rien n'est envoyé tant que vous ne cliquez pas « Envoyer ».
            </p>
            {(courrielApercu.type === "etape" || courrielApercu.type === "expedie") && (
              <label className="flex items-center gap-2 mb-3 cursor-pointer" data-testid="courriel-apercu-sans-courriel-wrap">
                <input type="checkbox" checked={courrielApercu.sansCourriel}
                  onChange={(e) => setCourrielApercu({ ...courrielApercu, sansCourriel: e.target.checked })}
                  data-testid="courriel-apercu-sans-courriel"
                  className="accent-gold w-4 h-4" />
                <span className="text-xs text-ivory/70">
                  {courrielApercu.type === "expedie"
                    ? "Marquer « Expédiée » SANS envoyer de courriel au client"
                    : "Cocher l'étape SANS envoyer de courriel au client"}
                </span>
              </label>
            )}
            <label className="block mb-3">
              <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">Objet du courriel (modifiable)</span>
              <input value={courrielApercu.sujet} maxLength={150}
                onChange={(e) => setCourrielApercu({ ...courrielApercu, sujet: e.target.value })}
                data-testid="courriel-apercu-sujet"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors" />
            </label>
            {courrielApercu.type === "question" && (
              <label className="block mb-3">
                <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">Votre question (modifiable)</span>
                <textarea value={courrielApercu.message} rows={3} maxLength={2000}
                  onChange={(e) => setCourrielApercu({ ...courrielApercu, message: e.target.value })}
                  data-testid="courriel-apercu-message"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors resize-none" />
              </label>
            )}
            <label className="block mb-2">
              <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">Note ajoutée en tête du courriel (facultatif)</span>
              <textarea value={courrielApercu.note} rows={2} maxLength={800}
                onChange={(e) => setCourrielApercu({ ...courrielApercu, note: e.target.value })}
                data-testid="courriel-apercu-note"
                placeholder="ex. « Livraison prévue la semaine du tournoi — on vous tient au courant ! »"
                className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none transition-colors resize-none" />
            </label>
            {/* P.J. COMMUNES à tous les courriels du popup (demande du
                propriétaire, 2026-10-27 : « P.J. partout ») */}
            {renderPiecesJointes(courrielApercu.fiche, "courriel-apercu")}
            {(courrielApercu.note || courrielApercu.type === "question") && (
              <button type="button" onClick={rafraichirApercuCourriel} data-testid="courriel-apercu-refresh"
                className="inline-flex items-center gap-1.5 text-[11px] text-gold hover:text-champagne border border-gold/30 hover:border-gold/60 rounded-full px-3 py-1 transition-colors mb-3">
                <RefreshCw className="w-3 h-3" /> Mettre à jour l'aperçu avec le texte modifié
              </button>
            )}
            {/* MODÈLES RÉUTILISABLES (demande du propriétaire, 2026-10-22) : garder
                l'objet et la note du popup comme modèle — proposés automatiquement
                aux prochaines fiches pour les courriels d'étapes fréquents */}
            {(courrielApercu.type === "etape" || courrielApercu.type === "expedie") && (
              <div className="flex items-center justify-between gap-2 mb-3 text-[11px]" data-testid="courriel-apercu-modele-row">
                {courrielApercu.modele ? (
                  <span className="text-emerald-300/90" data-testid="courriel-apercu-modele-actif">
                    Modèle enregistré appliqué
                    <button type="button" onClick={retirerModeleCourriel} data-testid="courriel-apercu-modele-retirer"
                      className="ml-2 underline underline-offset-2 text-red-300/90 hover:text-red-200 transition-colors">
                      Retirer le modèle
                    </button>
                  </span>
                ) : <span />}
                <button type="button" onClick={enregistrerModeleCourriel} data-testid="courriel-apercu-modele-save"
                  className="inline-flex items-center gap-1.5 text-gold hover:text-champagne border border-gold/30 hover:border-gold/60 rounded-full px-3 py-1 transition-colors">
                  <Save className="w-3 h-3" /> Enregistrer comme modèle
                </button>
              </div>
            )}
            <iframe title="Aperçu du courriel" srcDoc={courrielApercu.html}
              data-testid="courriel-apercu-frame"
              className="w-full rounded-lg border border-ivory/15 bg-white mb-4" style={{ height: 380 }} />
            <div className="flex gap-3">
              <button onClick={() => setCourrielApercu(null)} disabled={courrielApercu.busy}
                data-testid="courriel-apercu-cancel"
                className="flex-1 py-3 rounded-full border border-ivory/20 text-ivory/70 hover:border-ivory/50 transition-colors text-sm">
                Annuler
              </button>
              <button onClick={envoyerCourrielApercu} disabled={courrielApercu.busy}
                data-testid="courriel-apercu-send"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3 rounded-full hover:bg-champagne transition-colors text-sm disabled:opacity-60">
                {courrielApercu.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {courrielApercu.type === "etape"
                  ? (courrielApercu.sansCourriel
                      ? `Cocher « ${EMAILING_STEPS[courrielApercu.etape]} » SANS courriel`
                      : `Cocher « ${EMAILING_STEPS[courrielApercu.etape]} » et envoyer le courriel`)
                  : courrielApercu.type === "expedie"
                    ? (courrielApercu.sansCourriel
                        ? "Marquer « Expédiée » SANS courriel"
                        : "Marquer « Expédiée » et envoyer le courriel")
                    : COURRIEL_ENVOI[courrielApercu.type]}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Popup de RETRAIT avec raison (ex-« Marquer comme vente perdue », fusionné
          dans la pilule « Retirée par le client » — demande du propriétaire,
          2026-10-22) : raison OBLIGATOIRE — le dossier entre au rapport « Ventes
          perdues » (par thème) et toutes les relances s'arrêtent */}
      {vpFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="lost-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setVpFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-red-400/30 rounded-2xl w-full max-w-md p-6 sm:p-8 shadow-2xl shadow-black/60">
            <h3 className="font-serif text-2xl mb-1">Retirer la soumission — {vpFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-4">
              Le dossier entre dans le rapport « Ventes perdues » (classé par thème) et toutes les
              relances s'arrêtent. Réversible en décochant la pilule « Retirée par le client ».
            </p>
            <label htmlFor="lost-reason" className="block text-xs uppercase tracking-widest text-ivory/60 mb-1.5">
              Raison du retrait *
            </label>
            <textarea id="lost-reason" rows={3} maxLength={1000}
              data-testid="lost-reason" value={vpReason} onChange={(e) => setVpReason(e.target.value)}
              placeholder="ex. : client injoignable, a acheté ailleurs, projet abandonné…"
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-red-300 focus:outline-none transition-colors mb-4" />
            <button onClick={ventePerdue} disabled={vpBusy} data-testid="lost-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-red-500/90 text-ivory font-bold px-6 py-3.5 rounded-full hover:bg-red-500 transition-colors duration-300 disabled:opacity-60">
              {vpBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingDown className="w-4 h-4" />}
              Retirer la soumission
            </button>
          </div>
        </div>
      )}

      {/* Popup « Proposer un rabais » : pourcentage AU CHOIX (défaut 5 %, 1-90 %) —
          une seule contre-offre par soumission, courriel au client avec nouveaux prix */}{discFor && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="discount-modal">
          <button className="absolute inset-0 bg-ink/85 backdrop-blur-sm" onClick={() => setDiscFor(null)} aria-label="Fermer" />
          <div className="relative bg-surface border border-gold/25 rounded-2xl w-full max-w-sm p-6 sm:p-8 shadow-2xl shadow-black/60">
            <button onClick={() => setDiscFor(null)} data-testid="discount-close"
              className="absolute top-4 right-4 text-ivory/50 hover:text-gold transition-colors" aria-label="Fermer">
              <X className="w-5 h-5" />
            </button>
            <h3 className="font-serif text-2xl mb-1">Contre-offre — {discFor.name}</h3>
            <p className="text-xs text-ivory/55 mb-6">
              Rabais sur les produits et la gravure seulement. Le client reçoit un courriel avec
              les nouveaux prix (copie à commande@trophee.ca) — une seule contre-offre par soumission.
            </p>
            <label htmlFor="discount-pct" className="block text-xs uppercase tracking-widest text-ivory/60 mb-1.5">
              Rabais en % *
            </label>
            <input id="discount-pct" type="number" min="1" max="90" step="0.5" inputMode="decimal"
              data-testid="discount-pct" value={discPct} onChange={(e) => setDiscPct(e.target.value)}
              className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300 mb-5" />
            <button onClick={discountOffer} disabled={discBusy === discFor.id} data-testid="discount-confirm"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-6 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
              {discBusy === discFor.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Percent className="w-4 h-4" />}
              Envoyer la contre-offre −{discPct || 5} %
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
